import React, { useState, useEffect, useRef, useMemo, useLayoutEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useAuth } from './AuthContext';
import { clients, files, telecomAuth, healthsherpa, calendarApi, NOT_AVAILABLE_MSG, isNotFound } from './api';
import UploadModal from './UploadModal';
import BackupStatusModal from './BackupStatusModal';
import { authFetch } from './authSession';
import Bonuses, { AddBonusModal } from './Bonuses';
import ClientBonusHistoryModal from './ClientBonusHistoryModal';
import AddAcordModal from './AddAcordModal';
import Renewals, { AddRenewalModal } from './Renewals';
import { subscribe as subscribeInsuredocsChannel, MAIN_WINDOW_NAME } from './insuredocsChannel';   // 5.17.0
import WriterDrawer from './WriterDrawer';
import SignForSignatureModal from './SignForSignatureModal';
import SignedDocumentsPanel from './SignedDocumentsPanel';
import HealthCoveragePanel from './HealthCoveragePanel';   // 5.19.0 HealthSherpa phase 2
import FaxAlertStrip from './FaxAlertStrip';
import { Z_TOPBAR_MENU, Z_TOPBAR_MENU_AWAY } from './layers';   // 5.49.1 top-bar menus above the HealthSherpa lock strip
import HsLockStrip from './HsLockStrip';   // 5.44.0 "HealthSherpa in use" banner / holder dropdown (every page)
import TodayCard from './TodayCard';                   // 5.50.0 the ONE calendar notice (today's items + reminders); replaces the red Today panel + reminder pop-up here
import FollowupDialog from './FollowupDialog';         // 5.38.0 client follow-ups
import ClientFollowups from './ClientFollowups';
import TopBarActions from './TopBarActions';   // 5.23.0 top-bar row 2
import FaqApp from './FaqApp';   // 5.24.0 FAQ tab
import RoleSelect, { RoleBadge } from './RoleSelect';   // 5.27.3 one role list (Staff / Manager / Admin)
import { textMatches } from './searchText';   // 5.27.3 apostrophe-insensitive filters

const PREVIEW_EXTS = ['pdf', 'jpg', 'jpeg', 'png', 'tiff', 'tif', 'gif', 'webp', 'docx'];

// Search-result match highlighting. Phrase mode (any punctuation in the query) ->
// space-insensitive around punctuation so the green lands on a stored "T&T" even
// when the user typed "t & t". Clean mode -> highlight any query word. Mirrors the
// backend _norm_punct regex. Tracks the live searchQuery (matches the DT fork).
const _HL_PUNCT = /[^A-Za-z0-9\s.]/;
const _hlEsc = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
function buildHighlightRegex(query) {
  const raw = (query || '').trim();
  if (!raw) return null;
  if (/[^A-Za-z0-9 ]/.test(raw)) {                       // phrase mode
    const tok = raw.replace(/\s*([^A-Za-z0-9\s.])\s*/g, '$1');
    let pat = '';
    for (const ch of tok) pat += _HL_PUNCT.test(ch) ? `\\s*${_hlEsc(ch)}\\s*` : _hlEsc(ch);
    return pat ? new RegExp(`(${pat})`, 'i') : null;
  }
  const ws = raw.split(/\s+/).filter(Boolean).map(_hlEsc);  // clean mode
  return ws.length ? new RegExp(`(${ws.join('|')})`, 'i') : null;
}
function highlightMatch(value, regex) {
  if (!value || !regex) return value;
  return String(value).split(regex).map((p, i) =>
    i % 2 === 1 ? <strong key={i} style={{ color: '#4ade80' }}>{p}</strong> : p);
}
const EXT_COLORS = {
  pdf: { bg: '#FCEBEB', color: '#A32D2D' },
  eml: { bg: '#E6F1FB', color: '#185FA5' },
  msg: { bg: '#E6F1FB', color: '#185FA5' },
  doc: { bg: '#E6F1FB', color: '#0C447C' },
  docx: { bg: '#E6F1FB', color: '#0C447C' },
  xls: { bg: '#EAF3DE', color: '#3B6D11' },
  xlsx: { bg: '#EAF3DE', color: '#3B6D11' },
};
function extStyle(e) { return EXT_COLORS[e?.toLowerCase()] || { bg: '#F1EFE8', color: '#5F5E5A' }; }
function formatSize(b) {
  if (!b) return '—';
  if (b < 1024) return b + ' B';
  if (b < 1048576) return (b/1024).toFixed(1) + ' KB';
  return (b/1048576).toFixed(1) + ' MB';
}
function formatDate(d) { if (!d) return '—'; return new Date(d).toLocaleDateString(); }

function getFoldersForParent(tree, parentId) {
  // If no parent selected, return top-level folders
  if (parentId === null) {
    return tree;
  }
  // Find the selected folder and return its children
  const findChildren = (folders) => {
    for (const f of folders) {
      if (f.id === parentId) return f.children || [];
      if (f.children && f.children.length > 0) {
        const found = findChildren(f.children);
        if (found !== null) return found;
      }
    }
    return null;
  };
  return findChildren(tree) || [];
}

// ── File-pane multi-select (v4.11.0) ──────────────────────────────────────
const CHK_W = 28;            // checkbox column width (list view)
function flattenFolders(tree, depth = 0) {
  return (tree || []).flatMap(f => [{ ...f, _depth: depth }, ...flattenFolders(f.children || [], depth + 1)]);
}
function findFolderNode(tree, id) {
  for (const n of tree || []) {
    if (n.id === id) return n;
    const hit = findFolderNode(n.children, id);
    if (hit) return hit;
  }
  return null;
}
function collectSubtreeIds(node, acc = new Set()) {
  if (!node) return acc;
  acc.add(node.id);
  (node.children || []).forEach(c => collectSubtreeIds(c, acc));
  return acc;
}
// Presentational checkbox: whole-cell click target lives on the parent; the
// <input> is readOnly + pointerEvents:none so it never fires its own events.
function SelBox({ checked, disabled, onClick }) {
  return (
    <span onClick={onClick}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1 }}>
      <input type="checkbox" readOnly checked={checked} disabled={disabled}
        style={{ pointerEvents: 'none', margin: 0 }} />
    </span>
  );
}

function CenterFolders({ folderTree, selectedFolder, selectFolder, dragOverFolder, setDragOverFolder, draggingFile, moveFile, borderColor, panelBg, textPrimary, darkMode, fileList, selectedClient, view, user, selFolderIds, toggleFolder, selCount, CAP, onDeleteFolder, onMoveFolder, folderDescWidth, startFolderDescResize, folderDateWidth, startFolderDateResize, folderSortCol, setFolderSortCol, folderSortDir, setFolderSortDir, folderResizing, onSaveFolderDesc, bg, onFolderMenu }) {
  const [editingFDesc, setEditingFDesc] = React.useState(null);
  const [editFDescVal, setEditFDescVal] = React.useState('');
  if (!selectedClient || folderTree.length === 0) return null;
  const parentId = selectedFolder?.id || null;
  const centerFolders = [...getFoldersForParent(folderTree, parentId)].sort((a, b) => {
    let av, bv;
    if (folderSortCol === 'date') {
      av = a.created_at ? new Date(a.created_at).getTime() : 0;
      bv = b.created_at ? new Date(b.created_at).getTime() : 0;
    } else if (folderSortCol === 'desc') {
      av = (a.description || '').toLowerCase();
      bv = (b.description || '').toLowerCase();
    } else {
      av = a.name.toLowerCase();
      bv = b.name.toLowerCase();
    }
    if (av < bv) return folderSortDir === 'asc' ? -1 : 1;
    if (av > bv) return folderSortDir === 'asc' ? 1 : -1;
    return 0;
  });
  if (centerFolders.length === 0) return null;
  return (
    <div style={{ marginBottom: 4 }}>
      {view === 'grid' ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '8px 4px' }}>
          {centerFolders.map(f => (
            <div key={f.id}
              onClick={() => selectFolder(f)}
              onContextMenu={e => onFolderMenu && onFolderMenu(e)}
              onDragOver={e => { e.preventDefault(); setDragOverFolder(f.id); }}
              onDragLeave={() => setDragOverFolder(null)}
              onDrop={e => { e.preventDefault(); setDragOverFolder(null); if (draggingFile) moveFile(draggingFile.id, f.id); }}
              style={{
                position: 'relative',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                padding: '10px 14px', borderRadius: 8, cursor: 'pointer', width: 110,
                border: `1px solid ${dragOverFolder === f.id ? '#185FA5' : borderColor}`,
                background: dragOverFolder === f.id ? '#1a2a3a' : panelBg,
                transition: 'all 0.1s',
              }}>
              <span style={{ position: 'absolute', top: 4, left: 4, width: 18, height: 18 }}>
                <SelBox checked={selFolderIds.has(f.id)} disabled={selCount >= CAP && !selFolderIds.has(f.id)} onClick={e => toggleFolder(f, e)} />
              </span>
              <span style={{ fontSize: 36 }}>📁</span>
              <span style={{ fontSize: 11, color: textPrimary, textAlign: 'center', wordBreak: 'break-word', maxWidth: 90 }}>{f.name}</span>
              {(user?.role === 'admin' || user?.role === 'manager') && (
                <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span onClick={e => { e.stopPropagation(); onMoveFolder(f); }}
                    title="Move folder"
                    style={{ fontSize: 16, color: '#185FA5', cursor: 'pointer' }}>⇄</span>
                  <span onClick={e => { e.stopPropagation(); onDeleteFolder(f); }}
                    title="Delete folder"
                    style={{ fontSize: 16, color: '#c0392b', cursor: 'pointer', opacity: 1 }}>🗑</span>
                </span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', padding: '4px 8px', borderBottom: `1px solid ${borderColor}`, background: panelBg, fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', userSelect: 'none' }}>
            <span style={{ width: 22, flexShrink: 0 }}></span>
            <span onClick={() => { if (folderSortCol === 'name') setFolderSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setFolderSortCol('name'); setFolderSortDir('asc'); } }}
              style={{ flex: `0 1 calc(100% - ${folderDescWidth + folderDateWidth + 84}px)`, cursor: 'pointer', color: folderSortCol === 'name' ? '#185FA5' : '#888' }}>
              Name{folderSortCol === 'name' ? (folderSortDir === 'asc' ? ' ↑' : ' ↓') : ''}
            </span>
            <span style={{ width: 6, flexShrink: 0 }}></span>
            <span onClick={() => { if (folderSortCol === 'desc') setFolderSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setFolderSortCol('desc'); setFolderSortDir('asc'); } }}
              style={{ width: folderDescWidth, flexShrink: 0, cursor: 'pointer', color: folderSortCol === 'desc' ? '#185FA5' : '#888' }}>
              Description{folderSortCol === 'desc' ? (folderSortDir === 'asc' ? ' ↑' : ' ↓') : ''}
            </span>
            <span style={{ width: 6, flexShrink: 0 }}></span>
            <span onClick={() => { if (folderSortCol === 'date') setFolderSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setFolderSortCol('date'); setFolderSortDir('desc'); } }}
              style={{ width: folderDateWidth, flexShrink: 0, paddingLeft: 4, cursor: 'pointer', color: folderSortCol === 'date' ? '#185FA5' : '#888' }}>
              Created{folderSortCol === 'date' ? (folderSortDir === 'asc' ? ' ↑' : ' ↓') : ''}
            </span>
          </div>
          {centerFolders.map(f => (
            <div key={f.id}
              onClick={() => { if (!folderResizing.current) selectFolder(f); }}
              onContextMenu={e => onFolderMenu && onFolderMenu(e)}
              onDragOver={e => { e.preventDefault(); setDragOverFolder(f.id); }}
              onDragLeave={() => setDragOverFolder(null)}
              onDrop={e => { e.preventDefault(); setDragOverFolder(null); if (draggingFile) moveFile(draggingFile.id, f.id); }}
              style={{
                display: 'flex', padding: '6px 8px', cursor: 'pointer',
                borderBottom: `1px solid ${borderColor}`,
                background: dragOverFolder === f.id ? '#1a2a3a' : panelBg,
                alignItems: 'center',
                transition: 'background 0.1s',
              }}>
              <span style={{ width: 22, height: 22, flexShrink: 0, display: 'inline-flex' }}>
                <SelBox checked={selFolderIds.has(f.id)} disabled={selCount >= CAP && !selFolderIds.has(f.id)} onClick={e => toggleFolder(f, e)} />
              </span>
              <span style={{ fontSize: 14, marginRight: 8 }}>📁</span>
              <span style={{ color: textPrimary, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: `0 1 calc(100% - ${folderDescWidth + folderDateWidth + 84}px)` }}>{f.name}</span>
              <div onMouseDown={e => { e.stopPropagation(); startFolderDescResize(e); }}
                style={{ width: 6, flexShrink: 0, cursor: 'col-resize', display: 'flex', alignItems: 'center', justifyContent: 'center', alignSelf: 'stretch' }}>
                <div style={{ width: 1, height: '60%', background: borderColor }} />
              </div>
              <span onClick={e => { e.stopPropagation(); setEditingFDesc(f.id); setEditFDescVal(f.description || ''); }}
                title="Click to edit description"
                style={{ width: folderDescWidth, flexShrink: 0, color: '#5BA3D9', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'text' }}>
                {editingFDesc === f.id ? (
                  <input autoFocus value={editFDescVal}
                    onChange={e => setEditFDescVal(e.target.value)}
                    onBlur={() => { onSaveFolderDesc(f.id, editFDescVal); setEditingFDesc(null); }}
                    onKeyDown={e => { if (e.key === 'Enter') { onSaveFolderDesc(f.id, editFDescVal); setEditingFDesc(null); } if (e.key === 'Escape') setEditingFDesc(null); }}
                    onClick={e => e.stopPropagation()}
                    style={{ width: '100%', fontSize: 11, padding: '1px 4px', border: '1px solid #4f8ef7', borderRadius: 3, background: bg, color: textPrimary, boxSizing: 'border-box' }} />
                ) : (f.description || <span style={{ color: '#555', fontStyle: 'italic' }}>+ desc</span>)}
              </span>
              <div onMouseDown={e => { e.stopPropagation(); startFolderDateResize(e); }}
                style={{ width: 6, flexShrink: 0, cursor: 'col-resize', display: 'flex', alignItems: 'center', justifyContent: 'center', alignSelf: 'stretch' }}>
                <div style={{ width: 1, height: '60%', background: borderColor }} />
              </div>
              <span style={{ width: folderDateWidth, flexShrink: 0, fontSize: 11, color: '#888', paddingLeft: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {f.created_at ? new Date(f.created_at).toLocaleDateString() : ''}
              </span>
              {(user?.role === 'admin' || user?.role === 'manager') && (
                <span style={{ display: 'flex', gap: 2, alignItems: 'center', flexShrink: 0 }}>
                  <span onClick={e => { e.stopPropagation(); onMoveFolder(f); }}
                    title="Move folder"
                    style={{ fontSize: 14, color: '#185FA5', cursor: 'pointer', padding: '0 4px' }}>⇄</span>
                  <span onClick={e => { e.stopPropagation(); onDeleteFolder(f); }}
                    title="Delete folder"
                    style={{ fontSize: 14, color: '#c0392b', cursor: 'pointer', opacity: 1, padding: '0 4px' }}>🗑</span>
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      {fileList.length > 0 && <div style={{ height: 1, background: borderColor, margin: '4px 0 8px' }} />}
    </div>
  );
}

// v3.6.8: self-service password change form
function ChangePasswordForm({ onClose, darkMode, panelBg, borderColor, textPrimary, textSecondary }) {
  const [currentPw, setCurrentPw] = React.useState('');
  const [newPw, setNewPw] = React.useState('');
  const [confirmPw, setConfirmPw] = React.useState('');
  const [showPasswords, setShowPasswords] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState(false);

  const submit = async () => {
    setError('');
    if (!currentPw || !newPw || !confirmPw) {
      setError('All fields are required');
      return;
    }
    if (newPw !== confirmPw) {
      setError('New password and confirmation do not match');
      return;
    }
    if (newPw.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }
    if (currentPw === newPw) {
      setError('New password must be different from current');
      return;
    }
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await authFetch('/api/auth/me/password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      setSuccess(true);
      setTimeout(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }, 1800);
    } catch (e) {
      setError(e.message || 'Failed to change password');
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '8px 10px', boxSizing: 'border-box',
    border: `1px solid ${borderColor}`, borderRadius: 6,
    background: darkMode ? '#1a1a1a' : '#fff', color: textPrimary, fontSize: 13,
  };

  if (success) {
    return (
      <div style={{ padding: '12px 0', color: '#27AE60', textAlign: 'center', fontSize: 14, fontWeight: 600 }}>
        Password changed.
        <div style={{ fontSize: 12, fontWeight: 400, marginTop: 6, color: '#666' }}>
          Logging out, please sign in with your new password.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', fontSize: 12, color: textSecondary, marginBottom: 4 }}>Current password</label>
        <input type={showPasswords ? 'text' : 'password'} value={currentPw} onChange={e => setCurrentPw(e.target.value)}
          autoFocus disabled={busy} style={inputStyle} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', fontSize: 12, color: textSecondary, marginBottom: 4 }}>New password</label>
        <input type={showPasswords ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)}
          disabled={busy} style={inputStyle} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', fontSize: 12, color: textSecondary, marginBottom: 4 }}>Confirm new password</label>
        <input type={showPasswords ? 'text' : 'password'} value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
          disabled={busy} onKeyDown={e => { if (e.key === 'Enter') submit(); }} style={inputStyle} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: textSecondary, marginBottom: 14, cursor: 'pointer' }}>
        <input type="checkbox" checked={showPasswords} onChange={e => setShowPasswords(e.target.checked)} disabled={busy} />
        Show passwords
      </label>
      {error && (
        <div style={{ padding: '8px 10px', background: '#fce4e4', color: '#c0392b', borderRadius: 5, fontSize: 12, marginBottom: 12 }}>
          {error}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onClose} disabled={busy}
          style={{ padding: '7px 14px', background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 6, fontSize: 12, cursor: busy ? 'not-allowed' : 'pointer' }}>
          Cancel
        </button>
        <button onClick={submit} disabled={busy}
          style={{ padding: '7px 14px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1 }}>
          {busy ? 'Saving...' : 'Change password'}
        </button>
      </div>
    </div>
  );
}


// --- Telecom auth-codes quick-panel helpers (module scope) ---
async function copyAuthCode(code) {
  if (!code) return;
  try {
    await navigator.clipboard.writeText(code);
  } catch {
    // Fallback for non-secure contexts.
    const ta = document.createElement('textarea');
    ta.value = code;
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); } catch { /* ignore */ }
    document.body.removeChild(ta);
  }
}
function authCodeAge(iso) {
  if (!iso) return '';
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

export default function Main() {
  const { user, logout } = useAuth();

  // Telecom unread badge counts — polls every 20s.
  const [telecomUnread, setTelecomUnread] = React.useState({ sms: 0, fax: 0, total: 0 });
  React.useEffect(() => {
    let cancelled = false;
    const loadUnread = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch('/telecom/api/unread_counts', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) setTelecomUnread(data);
      } catch { /* ignore transient */ }
    };
    loadUnread();
    const id = setInterval(loadUnread, 20000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Telecom nav dropdown + live auth-codes quick panel.
  const [telecomMenuOpen, setTelecomMenuOpen] = React.useState(false);
  const [codesPanelOpen, setCodesPanelOpen] = React.useState(false);
  const [authCodesAllowed, setAuthCodesAllowed] = React.useState(null); // null = not yet checked
  const [authCodes, setAuthCodes] = React.useState([]);
  const [copiedCodeId, setCopiedCodeId] = React.useState(null);
  const [, setCodesTick] = React.useState(0); // re-render so ages stay fresh

  // Resolve /access lazily the first time the dropdown opens — NO call on page
  // load, so plain navigation adds no telecom traffic.
  const ensureAuthAccess = React.useCallback(async () => {
    if (authCodesAllowed !== null) return;
    try {
      const { data } = await telecomAuth.access();
      setAuthCodesAllowed(!!data.allowed);
    } catch {
      setAuthCodesAllowed(false);
    }
  }, [authCodesAllowed]);

  // Poll the codes list ONLY while the panel is open (30s); tick a clock so the
  // "2m ago" labels refresh between polls.
  React.useEffect(() => {
    if (!codesPanelOpen) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await telecomAuth.listCodes({ background: true });   // silent on expiry
        if (!cancelled) setAuthCodes(Array.isArray(data) ? data : []);
      } catch { /* transient; panel just shows last-known/empty */ }
    };
    load();
    const poll = setInterval(load, 30000);
    const clock = setInterval(() => setCodesTick((t) => t + 1), 15000);
    return () => { cancelled = true; clearInterval(poll); clearInterval(clock); };
  }, [codesPanelOpen]);

  const dismissAuthCode = async (id) => {
    setAuthCodes((prev) => prev.filter((c) => c.id !== id)); // optimistic
    try { await telecomAuth.dismissCode(id); }
    catch { /* next poll reconciles */ }
  };
  const copyAuthCodeUI = async (c) => {
    await copyAuthCode(c.code);
    setCopiedCodeId(c.id);
    setTimeout(() => setCopiedCodeId((x) => (x === c.id ? null : x)), 1500);
  };

  const [cabinets, setCabinets] = useState([]);
  // (clientList removed in 4.8.3: the ~830ms mount fetch of all ~12k clients fed
  // only an id-lookup that now reads from `cabinets`, with clients.get fallback.)
  const [expandedCabinets, setExpandedCabinets] = useState({});
  const [selectedClient, setSelectedClient] = useState(null);
  const [folderTree, setFolderTree] = useState([]);
  const [folderTreeCache, setFolderTreeCache] = useState({});
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [view, setView] = useState('list');
  const [treeSearch, setTreeSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState('all');
  const [searchResults, setSearchResults] = useState(null);
  const [searchTruncated, setSearchTruncated] = useState(false);
  const searchHL = useMemo(() => buildHighlightRegex(searchQuery), [searchQuery]);
  const [metadata, setMetadata] = useState({});
  const [folderMeta, setFolderMeta] = useState({});
  const [clientMeta, setClientMeta] = useState({});
  // Manager/admin-only client note log (defensive-only gate; backend is authoritative)
  const isManagerOrAdmin = user?.role === 'admin' || user?.role === 'manager';
  const [managerNotes, setManagerNotes] = useState([]);
  const [managerNoteDraft, setManagerNoteDraft] = useState('');
  const [managerNoteSaving, setManagerNoteSaving] = useState(false);
  const [clientMetaSaving, setClientMetaSaving] = useState(false);
  const [clientMetaSaved, setClientMetaSaved] = useState(false);
  // 5.5.1: the cabinet/search lists carry only a few client fields, so the profile form
  // is filled from the full GET /clients/{id}. clientMeta.loadedFor = the client it was
  // loaded for; Save goes only there and sends only keys that differ from the snapshot.
  const [clientMetaSnapshot, setClientMetaSnapshot] = useState(null);
  const [profileLoadingId, setProfileLoadingId] = useState(null);
  const selectedClientIdRef = useRef(null);
  // 5.5.2: bumped on every client change; an async result is applied only if the epoch
  // (and, for folder/file loads, the requested id) is unchanged when it lands.
  const clientEpochRef = useRef(0);
  const folderReqRef = useRef(null);
  const fileReqRef = useRef(0);
  const [managerNoteDraftFor, setManagerNoteDraftFor] = useState(null);  // client id the draft was typed for
  const [folderMetaSaving, setFolderMetaSaving] = useState(false);
  const [folderMetaSaved, setFolderMetaSaved] = useState(false);
  const [metaSaving, setMetaSaving] = useState(false);
  const [metaSaved, setMetaSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [signFileTarget, setSignFileTarget] = useState(null);   // file being sent for signature
  const [showSignedPanel, setShowSignedPanel] = useState(false);
  // 5.19.0: HealthSherpa policy status for the selected client (shown only when linked + visible to this user)
  const [hsCoverage, setHsCoverage] = useState({ clientId: null, count: 0 });
  const [showHealthCoverage, setShowHealthCoverage] = useState(false);
  const [showBackupModal, setShowBackupModal] = useState(false);
  // Backup status summary for the top-bar button dot (admin only). Worst-of across the
  // backend-computed badges; "stale" is its own state so a silent helper shows distinctly.
  const [backupSummary, setBackupSummary] = useState(null);
  useEffect(() => {
    if (user?.role !== 'admin') return;
    let cancelled = false;
    const fetchSummary = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/files/admin/backup-status', { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok || cancelled) return;
        const d = await res.json();
        let level = 'unknown';
        if (d.overall_state === 'stale' || d.ok === false) {
          level = 'stale';
        } else {
          const badges = (d.targets || []).map(t => t.badge);
          if (d.restore_test) badges.push(d.restore_test.badge);
          if (d.docsvault_guard) badges.push(d.docsvault_guard.badge);
          if (badges.includes('red')) level = 'red';
          else if (badges.includes('amber')) level = 'amber';
          else if (badges.length && badges.every(b => b === 'green')) level = 'green';
        }
        if (!cancelled) setBackupSummary({ level });
      } catch { /* leave summary null -> button shows no dot */ }
    };
    fetchSummary();
    const iv = setInterval(fetchSummary, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user]);
  // 5.24.0 FAQ: overlay + admin-only open-conflict count badge on the FAQ button (badge only, no alerts).
  const [showFaq, setShowFaq] = useState(false);
  const [faqOpenConflicts, setFaqOpenConflicts] = useState(0);
  const [faqCountTick, setFaqCountTick] = useState(0);
  useEffect(() => {
    if (user?.role !== 'admin') return;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/faq/conflicts/count', { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok || cancelled) return;
        const d = await res.json();
        if (!cancelled) setFaqOpenConflicts(d.open || 0);
      } catch { /* keep the last count */ }
    };
    fetchCount();
    const iv = setInterval(fetchCount, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user, faqCountTick]);
  const [showBonuses, setShowBonuses] = useState(false);
  // Remembers where the user was in Bonuses when they followed a client deep-link,
  // so a "← Back to Bonuses" affordance can restore their tab + review filters.
  const [bonusReturnContext, setBonusReturnContext] = useState(null);
  const [showRenewals, setShowRenewals] = useState(false);
  const [addRenewalForClient, setAddRenewalForClient] = useState(null);
  const [addBonusForFolder, setAddBonusForFolder] = useState(null);
  const [historyClient, setHistoryClient] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [addingSubfolderTo, setAddingSubfolderTo] = useState(null);
  const [newSubfolderName, setNewSubfolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesName, setNotesName] = useState('');
  const [notesBody, setNotesBody] = useState('');
  const [darkMode, setDarkMode] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  // Bumped after a detached-editor save so the preview iframe REMOUNTS —
  // browsers cache PDF iframe content even across a new blob URL.
  const [previewReloadKey, setPreviewReloadKey] = useState(0);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [draggingFile, setDraggingFile] = useState(null);
  // ── File-pane multi-select state (v4.11.0) ──────────────────────────────
  const CAP = 10;
  const [selFileIds, setSelFileIds] = useState(() => new Set());
  const [selFolderIds, setSelFolderIds] = useState(() => new Set());
  const [folderFilesMap, setFolderFilesMap] = useState({}); // folderId -> file objs (resolution cache)
  const [selObjCache, setSelObjCache] = useState({});       // id -> file obj (download/move/delete need the object)
  const [selNote, setSelNote] = useState('');               // inline cap/oversize rejection note
  const [menu, setMenu] = useState(null);                   // right-click menu: { x, y, file } | null
  // 5.38.0 client follow-ups: who this login may target (the button shows only when allowed), the open dialog's client,
  // the folder right-click menu {x, y, client}, and a counter that reloads the client's Follow-ups panel
  const [followupTargets, setFollowupTargets] = useState(null);
  const [followupFor, setFollowupFor] = useState(null);
  const [folderMenu, setFolderMenu] = useState(null);
  const [followupReload, setFollowupReload] = useState(0);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [moveTargets, setMoveTargets] = useState([]);       // file objs the move modal will act on
  const [moveFolder, setMoveFolder] = useState(null);       // folder the move modal will act on (admin/manager button)
  const [moveSearch, setMoveSearch] = useState('');
  const [moveBusy, setMoveBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [renamingFile, setRenamingFile] = useState(null);
  const [renameFileValue, setRenameFileValue] = useState('');
  const [renamingFolder, setRenamingFolder] = useState(null);
  const [deletingFolder, setDeletingFolder] = useState(null);
  const [renumberClient, setRenumberClient] = useState(null);
  const [editingDesc, setEditingDesc] = useState(null);
  const [editingFolderDesc, setEditingFolderDesc] = useState(null);
  const [editFolderDescValue, setEditFolderDescValue] = useState('');
  const [editDescValue, setEditDescValue] = useState('');
  const [flagClient, setFlagClient] = useState(null);
  const [showInsureIQ, setShowInsureIQ] = useState(false);
  const [addAcordForClient, setAddAcordForClient] = useState(null);
  const [showUserMgmt, setShowUserMgmt] = useState(false);
  const [userList, setUserList] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  // v3.6.8: self-service password change
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ username: '', full_name: '', password: '', role: 'staff' });
  const [userSaving, setUserSaving] = useState(false);
  const [calAccounts, setCalAccounts] = useState([]);   // 5.32.1 HDCal accounts for the Calendar column dropdown
  const [iqMessages, setIqMessages] = useState([]);
  const [iqInput, setIqInput] = useState('');
  const [iqLoading, setIqLoading] = useState(false);
  const [iqStatus, setIqStatus] = useState(null); // routed-tier loading state ("warming up…" / "deep analysis…")
  const [iqMode, setIqMode] = useState('rag');
  const iqEndRef = React.useRef(null);
  const [iqFileIds, setIqFileIds] = useState(new Set());
  const _isPdf = f => (f?.extension || '').toLowerCase() === 'pdf';
  const toggleIqFile = id => setIqFileIds(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  // Clear selection on client change; auto-seed the open file if it's a PDF.
  useEffect(() => { setIqFileIds(new Set()); }, [selectedClient?.id]);
  // 5.5.2: a client change leaves a divider in the Ask chat (nothing is cleared).
  useEffect(() => {
    const divider = { role: 'divider', content: `— now asking about ${selectedClient ? selectedClient.name : 'no client'} —` };
    setIqMessages(prev => (prev.length === 0 ? prev
      : [...(prev[prev.length - 1].role === 'divider' ? prev.slice(0, -1) : prev), divider]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient?.id]);
  useEffect(() => {
    if (selectedFile && _isPdf(selectedFile)) setIqFileIds(prev => new Set(prev).add(selectedFile.id));
  }, [selectedFile?.id]);
  const [flagValue, setFlagValue] = useState('');
  const [flagNote, setFlagNote] = useState('');
  const [renumberNum, setRenumberNum] = useState('');
  const [renumberSaving, setRenumberSaving] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [trashedFiles, setTrashedFiles] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [renameFolderValue, setRenameFolderValue] = useState('');
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientAddress, setNewClientAddress] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientNotes, setNewClientNotes] = useState('');
  const [newClientSaving, setNewClientSaving] = useState(false);
  const [newClientDupes, setNewClientDupes] = useState(null);   // 5.14.0: possible duplicates (warn only)
  const [nextClientNum, setNextClientNum] = useState(null);
  const [showNewCabinet, setShowNewCabinet] = useState(false);
  const [newCabinetName, setNewCabinetName] = useState('');
  const [newCabinetRestricted, setNewCabinetRestricted] = useState(false);
  const [newCabinetSaving, setNewCabinetSaving] = useState(false);
  const [deleteCabinetTarget, setDeleteCabinetTarget] = useState(null);
  const [deleteCabinetConfirm1, setDeleteCabinetConfirm1] = useState(false);
  const [deleteCabinetTyped, setDeleteCabinetTyped] = useState('');
  const [deleteCabinetSaving, setDeleteCabinetSaving] = useState(false);
  const [deleteClientTarget, setDeleteClientTarget] = useState(null);
  const [deleteClientTyped, setDeleteClientTyped] = useState('');
  const [deleteClientSaving, setDeleteClientSaving] = useState(false);
  const [inlinePreviewUrl, setInlinePreviewUrl] = useState(null);
  const [inlinePreviewFile, setInlinePreviewFile] = useState(null);
  const [inlinePreviewReloadKey, setInlinePreviewReloadKey] = useState(0);
  const [sortCol, setSortCol] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [colWidths, setColWidths] = useState({ name: 500, ext: 65, size: 80, date: 100, by: 100, desc: 200 });
  const [folderDescWidth, setFolderDescWidth] = useState(300);
  const [folderSortCol, setFolderSortCol] = useState('name');
  const [folderSortDir, setFolderSortDir] = useState('asc');
  const [folderDateWidth, setFolderDateWidth] = useState(90);
  const [fontSize, setFontSize] = useState(16);
  const [clock, setClock] = useState(new Date());
  const [leftWidth, setLeftWidth] = useState(480);
  const [rightWidth, setRightWidth] = useState(270);
  const [previewHeight, setPreviewHeight] = useState(400);
  const [mmModalOpen, setMmModalOpen] = useState(false);
  const [mmFile, setMmFile] = useState(null);
  const [mmUsers, setMmUsers] = useState([]);
  const [mmRecipient, setMmRecipient] = useState('');
  const [mmSendType, setMmSendType] = useState('file');
  const [mmMessage, setMmMessage] = useState('');
  const [mmSending, setMmSending] = useState(false);
  const [mmError, setMmError] = useState('');
  const [mmSuccess, setMmSuccess] = useState(false);

  const resizingPanel = useRef(null);

  const startPanelResize = (panel, e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = leftWidth;
    const startRight = rightWidth;
    const startH = previewHeight;
    resizingPanel.current = panel;
    const onMove = (ev) => {
      if (panel === 'left') setLeftWidth(Math.max(150, Math.min(800, startLeft + ev.clientX - startX)));
      else if (panel === 'right') setRightWidth(Math.max(200, Math.min(600, startRight - (ev.clientX - startX))));
      else if (panel === 'preview') setPreviewHeight(Math.max(150, Math.min(800, startH - (ev.clientY - startY))));
    };
    const onUp = () => { resizingPanel.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  const resizing = useRef(null);
  const fileInputRef = useRef();

  const QUICK_FOLDERS = ['Auto', 'Home', 'Life', 'Commercial', 'Health', 'Renters'];
  const COLS = [
    { key: 'name', label: 'Name', field: 'filename' },
    { key: 'desc', label: 'Description', field: 'description' },
    { key: 'date', label: 'Date', field: 'uploaded_at' },
    { key: 'size', label: 'Size', field: 'file_size' },
    { key: 'by', label: 'By', field: 'uploaded_by' },
    { key: 'ext', label: 'Type', field: 'extension' },
  ];

  useEffect(() => {
    clients.byCabinet().then(r => setCabinets(r.data.cabinets)).catch(() => {});
    const timer = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatClock = (d) => d.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });

  // ── 5.5.2: one client-switch path ──────────────────────────────────────────
  // Unsaved work = the profile differs from what was loaded, the folder or file metadata
  // differs from the selected object, a manager-note draft, text in the Notes modal, or an
  // open rename/description edit.
  const FOLDER_META_KEYS = ['document_type', 'phone1', 'phone2', 'phone3', 'phone4', 'fax1', 'fax2', 'email', 'who_pays', 'notes'];
  const FILE_META_KEYS = ['document_type', 'phone1', 'phone2', 'phone3', 'phone4', 'fax1', 'fax2', 'email', 'helped_by', 'who_pays', 'policy_number', 'notes'];
  const differs = (keys, a, b) => keys.some(k => (a?.[k] || '') !== (b?.[k] || ''));
  const hasUnsavedClientWork = () => !!(
    (clientMeta.loadedFor && clientMetaSnapshot && differs(CLIENT_META_KEYS, clientMeta, clientMetaSnapshot)) ||
    (selectedFolder && differs(FOLDER_META_KEYS, folderMeta, selectedFolder)) ||
    (selectedFile && differs(FILE_META_KEYS, metadata, selectedFile)) ||
    (managerNoteDraft.trim() && !managerNoteSaving) || notesName.trim() || notesBody.trim() ||
    renamingFile || renamingFolder || editingDesc || editingFolderDesc
  );
  // true = OK to leave the current client (nothing unsaved, or the user chose Discard).
  const confirmLeaveClient = () => {
    if (!selectedClient || !hasUnsavedClientWork()) return true;
    return window.confirm(`Discard unsaved changes to ${selectedClient.name}?\n\nOK = Discard   ·   Cancel = Stay`);
  };
  // Clears everything that belongs to the client on screen; every switch and deselect calls it.
  const resetClientScoped = (target) => {
    clientEpochRef.current += 1;
    selectedClientIdRef.current = target ? target.id : null;
    folderReqRef.current = null;
    fileReqRef.current += 1;
    setBonusReturnContext(null);
    setSelectedFolder(null); setSelectedFile(null);
    setMetadata({}); setFolderMeta({});
    setClientMeta(target ? { ...clientMetaFrom(target), loadedFor: null } : {}); setClientMetaSnapshot(null);
    setManagerNotes([]); setManagerNoteDraft(''); setManagerNoteDraftFor(null);
    setPreviewUrl(null); setShowPreviewModal(false);
    setInlinePreviewUrl(null); setInlinePreviewFile(null);
    setFileList([]); setFolderTree((target && folderTreeCache[target.id]) || []);
    setExpandedFolders({}); setShowNewFolder(false); setNewFolderName('');
    setAddingSubfolderTo(null); setNewSubfolderName('');
    setRenamingFile(null); setRenamingFolder(null); setEditingDesc(null); setEditingFolderDesc(null);
    setMenu(null); setMoveModalOpen(false); setMoveTargets([]); setMoveFolder(null);
    setShowNotesModal(false); setNotesName(''); setNotesBody('');
  };
  const deselectClient = () => { resetClientScoped(null); setSelectedClient(null); };

  // Returns false if the user chose Stay. Pass { confirmed: true } when the caller already asked.
  const selectClient = async (c, targetFolderId = null, { confirmed = false } = {}) => {
    if (!confirmed && !confirmLeaveClient()) return false;
    const sameClient = selectedClientIdRef.current === c.id;
    // Also drops the Bonuses return-context; the bonus deep-link re-sets it right after.
    resetClientScoped(c);
    const epoch = clientEpochRef.current;
    setSelectedClient(c);
    if (sameClient) loadClientProfile(c.id);   // the id-keyed profile effect won't re-run
    // Manager-note log: load only for admin/manager (staff never call it)
    if (isManagerOrAdmin) {
      clients.managerNotes.list(c.id)
        .then(r => { if (clientEpochRef.current === epoch) setManagerNotes(r.data.notes || []); })
        .catch(() => { if (clientEpochRef.current === epoch) setManagerNotes([]); });
    }
    // Fetch folders and files in parallel
    const SPECIAL = ['5DANS FOLDER',"5Diana Vu's Folder","5Harry Dao's Folder","5Henry Dao's Folder","5Holly Dao's Folder","5Teresa Nguyen's Folder","5Tim Tran's Folder","5Tom Nguyen's Folder","5Vincent's Folder",'6HELP FILES','7PERSONAL FILES','8Quotes','9Contacts','9Harry Dao Inc','9SOLUTIONS INSURANCE AGENCY INC'];
    const isSpecial = SPECIAL.includes(c.name);
    const promises = [clients.getFolders(c.id)];
    if (!isSpecial) promises.push(files.list(c.id));
    let results;
    try {
      results = await Promise.all(promises);
    } catch (e) {
      // 5.21.0: 404 = missing or not available to this user (restricted) — close it, say so once.
      if (clientEpochRef.current !== epoch) return true;
      deselectClient();
      alert(isNotFound(e) ? NOT_AVAILABLE_MSG : `Could not open that client: ${e.message || 'error'}`);
      return false;
    }
    if (clientEpochRef.current !== epoch) return true;   // switched away meanwhile: drop (incl. the folder hop)
    const tree = results[0].data.folders;
    setFolderTree(tree);
    setFolderTreeCache(prev => ({ ...prev, [c.id]: tree }));
    if (!isSpecial && results[1]) setFileList(results[1].data.files);

    // Auto-navigate to target folder if specified
    if (targetFolderId) {
      const findFolder = (nodes) => {
        for (const n of nodes) {
          if (n.id === targetFolderId) return n;
          if (n.children) {
            const found = findFolder(n.children);
            if (found) return found;
          }
        }
        return null;
      };
      const expandParents = (nodes, targetId, path = {}) => {
        for (const n of nodes) {
          if (n.id === targetId) return { ...path, [n.id]: true };
          if (n.children) {
            const result = expandParents(n.children, targetId, { ...path, [n.id]: true });
            if (result) return result;
          }
        }
        return null;
      };
      const target = findFolder(tree);
      if (target) {
        const expanded = expandParents(tree, targetFolderId) || {};
        setExpandedFolders(expanded);
        selectFolder(target);
      }
    }
  };

  const CLIENT_META_KEYS = ['phone1', 'phone2', 'phone3', 'phone4', 'fax1', 'fax2', 'email', 'address_name', 'address_number', 'first_name', 'notes'];
  const clientMetaFrom = (c) => Object.fromEntries(CLIENT_META_KEYS.map(k => [k, c?.[k] || '']));
  const loadClientProfile = async (id) => {
    setProfileLoadingId(id);
    try {
      const r = await clients.get(id);
      if (selectedClientIdRef.current !== id) return;   // switched away meanwhile: drop
      const meta = clientMetaFrom(r.data);
      setClientMetaSnapshot(meta);
      setClientMeta({ ...meta, loadedFor: id });
    } catch (_e) {
      /* loadedFor stays unset: Save refuses and reloads */
    } finally {
      setProfileLoadingId(cur => (cur === id ? null : cur));
    }
  };
  // Every path that changes the client (tree, search, Bonuses, Renewals, folder owner,
  // deselect) goes through this one load.
  useEffect(() => {
    selectedClientIdRef.current = selectedClient?.id ?? null;
    if (!selectedClient) return;
    setClientMeta({ ...clientMetaFrom(selectedClient), loadedFor: null });
    loadClientProfile(selectedClient.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient?.id]);

  // 5.19.0: does this client have linked HealthSherpa applications this user may see? (one small call)
  useEffect(() => {
    const id = selectedClient?.id;
    setShowHealthCoverage(false);
    setHsCoverage({ clientId: id ?? null, count: 0 });
    if (!id) return;
    let alive = true;
    healthsherpa.policy.clientCount(id)
      .then(r => { if (alive) setHsCoverage({ clientId: id, count: r.data.visible ? r.data.count : 0 }); })
      .catch(() => {});
    return () => { alive = false; };
  }, [selectedClient?.id]);

  const addManagerNote = async () => {
    const text = managerNoteDraft.trim();
    if (!text || !selectedClient || managerNoteSaving) return;
    // The draft is stamped with the client it was typed for; it posts only there.
    const target = managerNoteDraftFor;
    if (target !== selectedClient.id) {
      alert('This note was typed for a different client — not added. Please re-type it here.');
      setManagerNoteDraft(''); setManagerNoteDraftFor(null);
      return;
    }
    setManagerNoteSaving(true);
    try {
      const r = await clients.managerNotes.add(target, text);
      if (selectedClientIdRef.current !== target) return;   // switched away meanwhile: don't list it here
      setManagerNotes(prev => [r.data, ...prev]);  // newest-first
      setManagerNoteDraft(''); setManagerNoteDraftFor(null);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not add note');
    } finally {
      setManagerNoteSaving(false);
    }
  };

  const deleteManagerNote = async (noteId) => {
    if (!selectedClient) return;
    if (!window.confirm('Remove this manager note? It will be tombstoned (content hidden, the fact retained). This cannot be undone.')) return;
    try {
      const r = await clients.managerNotes.remove(selectedClient.id, noteId);
      setManagerNotes(prev => prev.map(n => (n.id === noteId ? r.data : n)));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not remove note');
    }
  };

  const selectFolder = async (folder) => {
    // A folder of another client goes through the full switch, then opens there.
    const ownerClientId = folder.client_id || selectedClientIdRef.current;
    if (ownerClientId && ownerClientId !== selectedClientIdRef.current) {
      if (!confirmLeaveClient()) return;
      const epoch = clientEpochRef.current;
      let ownerClient = null;
      for (const cab of cabinets) {
        ownerClient = cab.clients.find(c => c.id === ownerClientId);
        if (ownerClient) break;
      }
      if (!ownerClient) {
        try { ownerClient = (await clients.get(ownerClientId)).data; } catch (e) { if (isNotFound(e)) alert(NOT_AVAILABLE_MSG); return; }   // 5.21.0
        if (clientEpochRef.current !== epoch) return;   // switched away meanwhile: drop
      }
      if (ownerClient) selectClient(ownerClient, folder.id, { confirmed: true });
      return;
    }
    const epoch = clientEpochRef.current;
    folderReqRef.current = folder.id;
    setSelectedFolder(folder);
    setSelectedFile(null);
    setPreviewUrl(null);
    setFolderMeta({
      document_type: folder.document_type || '',
      phone1: folder.phone1 || '', phone2: folder.phone2 || '',
      phone3: folder.phone3 || '', phone4: folder.phone4 || '',
      fax1: folder.fax1 || '', fax2: folder.fax2 || '',
      email: folder.email || '', who_pays: folder.who_pays || '',
      notes: folder.notes || '',
    });
    const clientId = ownerClientId;
    // ── Folder-tree cache contract ──────────────────────────────────────────
    // The folder tree is cached per client in folderTreeCache and reused here
    // instead of being refetched on every folder click (that refetch was a full
    // seq scan of the 48k-row folders table — see idx_folders_client_id).
    // ANY path that MUTATES the tree MUST write through to folderTreeCache[clientId]:
    //   create  → createFolder / createSubfolder
    //   rename  → submitRenameFolder
    //   delete  → deleteFolder
    //   move    → runMoveFolder (re-parents + rewrites folder.path)
    //   renumber→ submitRenumber (rewrites folder.path server-side; keyed on
    //             renumberClient.id, which may differ from the selected client)
    // Paths that deliberately DO NOT touch the tree today, and so must NOT
    // invalidate, are file-level only: upload, trash/restore, moveFile.
    // Client switch (selectClient) always refetches fresh. A future feature that
    // mutates folders must update this cache or this view will go stale.
    const cachedTree = folderTreeCache[clientId];
    let fileRes, folderRes;
    try {
      [fileRes, folderRes] = await Promise.all([
        files.list(clientId, folder.id),
        cachedTree ? Promise.resolve(null) : clients.getFolders(clientId),  // fetch-on-miss only
      ]);
    } catch (e) {
      // 5.21.0: was an unhandled rejection (stale list); now one clear message on 404.
      if (clientEpochRef.current === epoch && folderReqRef.current === folder.id && isNotFound(e)) alert(NOT_AVAILABLE_MSG);
      return;
    }
    if (clientEpochRef.current !== epoch || folderReqRef.current !== folder.id) return;   // moved on: drop
    setFileList(fileRes.data.files);
    if (cachedTree) {
      setFolderTree(cachedTree);
    } else if (folderRes) {
      setFolderTree(folderRes.data.folders);
      setFolderTreeCache(prev => ({ ...prev, [clientId]: folderRes.data.folders }));
    }
  };

  // v5.6.0 (renewals delta 11): the Renewals screens report client ids whose folder
  // tree changed server-side (Renewals/<year> created by an upload or a finish-notes
  // file, a link that wrote queued notes, a re-link that moved files). Per the
  // tree-cache contract above: drop those cache entries; if one is the selected
  // client, refetch its tree (and the open folder's file list) now.
  const onClientTreesChanged = async (ids) => {
    const set = new Set((ids || []).filter(Boolean));
    if (!set.size) return;
    setFolderTreeCache(prev => {
      const next = { ...prev };
      set.forEach(id => { delete next[id]; });
      return next;
    });
    if (!selectedClient || !set.has(selectedClient.id)) return;
    const cid = selectedClient.id, epoch = clientEpochRef.current, fid = selectedFolder?.id;
    try {
      const [treeRes, fileRes] = await Promise.all([
        clients.getFolders(cid),
        fid ? files.list(cid, fid) : Promise.resolve(null),
      ]);
      if (clientEpochRef.current !== epoch) return;          // switched client meanwhile: drop
      setFolderTree(treeRes.data.folders);
      setFolderTreeCache(prev => ({ ...prev, [cid]: treeRes.data.folders }));
      if (fileRes && folderReqRef.current === fid) setFileList(fileRes.data.files);
    } catch (e) {
      console.warn('onClientTreesChanged: refresh failed', e);   // next client switch refetches anyway
    }
  };

  // --- 5.17.0: the HealthSherpa tab and ?client=<id> -----------------------------------------------
  // Name this tab so the HealthSherpa tab's fallback window.open('/?client=…', 'insuredocs-main') reuses it.
  useEffect(() => { try { window.name = MAIN_WINDOW_NAME; } catch (_e) { /* ignore */ } }, []);

  // ?client=<id>: open that client once the cabinet list has loaded. Only a client present in the list is
  // opened — the same rule as the tree (restricted clients aren't listed for non-admins). Then the param
  // is cleaned with replaceState.
  const pendingClientParamRef = useRef((() => {
    try { const v = new URLSearchParams(window.location.search).get('client'); return v && /^\d+$/.test(v) ? Number(v) : null; } catch (_e) { return null; }
  })());
  useEffect(() => {
    const id = pendingClientParamRef.current;
    if (id === null || !cabinets || cabinets.length === 0) return;
    pendingClientParamRef.current = null;
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('client');
      window.history.replaceState(window.history.state, '', u.pathname + (u.search || '') + u.hash);
    } catch (_e) { /* address left as is */ }
    let found = null;
    for (const cab of cabinets) {
      found = (cab.clients || []).find((x) => x.id === id);
      if (found) break;
    }
    if (found) selectClient(found, null, { confirmed: true });
    else alert('That client could not be opened here.');
  }, [cabinets]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Messages from the HealthSherpa tab (ids only). The handler is re-bound every render so it always sees
  // the current state; the subscription itself is made once.
  const channelHandlerRef = useRef(null);
  channelHandlerRef.current = async (msg) => {
    if (msg.type === 'open-client') {
      // exactly like the overlay's old openClientFolder: unsaved work asks first; Stay = nothing changes
      if (!confirmLeaveClient()) return;
      const epoch = clientEpochRef.current;
      let cached = null;
      for (const cab of (cabinets || [])) {
        const found = (cab.clients || []).find((x) => x.id === msg.clientId);
        if (found) { cached = found; break; }
      }
      if (cached) { selectClient(cached, null, { confirmed: true }); return; }
      try {
        const r = await clients.get(msg.clientId);
        if (clientEpochRef.current !== epoch) return;
        if (r.data) selectClient(r.data, null, { confirmed: true });
      } catch (e) {
        console.error('Failed to load client', msg.clientId, e);
        alert(isNotFound(e) ? NOT_AVAILABLE_MSG : 'Could not open that client folder.');   // 5.21.0
      }
    } else if (msg.type === 'clients-changed') {
      try { const r = await clients.byCabinet(); setCabinets(r.data.cabinets); } catch (_e) { /* tree refreshes on next load */ }
      onClientTreesChanged(msg.clientIds);
    }
  };
  useEffect(() => subscribeInsuredocsChannel((msg) => channelHandlerRef.current && channelHandlerRef.current(msg)), []);

  const selectFile = async (f) => {
    // 5.5.2: only the latest click's preview is shown, and never after a client switch.
    const req = ++fileReqRef.current;
    setSelectedFile(f);
    setPreviewUrl(null);
    setInlinePreviewUrl(null);
    setInlinePreviewFile(null);
    setMetadata({
      document_type: f.document_type || '',
      phone1: f.phone1 || '', phone2: f.phone2 || '',
      phone3: f.phone3 || '', phone4: f.phone4 || '',
      fax1: f.fax1 || '', fax2: f.fax2 || '',
      email: f.email || '', helped_by: f.helped_by || '',
      who_pays: f.who_pays || '', policy_number: f.policy_number || '',
      notes: f.notes || '',
    });
    const previewable = ['pdf','jpg','jpeg','png','tiff','tif','gif','webp','txt','csv','eml','msg','html','htm','docx','md'];
    if (previewable.includes(f.extension?.toLowerCase())) {
      const token = localStorage.getItem('token');
      const ext = f.extension?.toLowerCase();
      const endpoint = ext === 'msg' ? `/api/files/preview-msg/${f.id}` : ext === 'eml' ? `/api/files/preview-eml/${f.id}` : ext === 'docx' ? `/api/files/preview-docx/${f.id}` : `/api/files/view/${f.id}`;
      const res = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const rawBlob = await res.blob();
        // 5.21.0: a stored .html/.htm file is shown as its SOURCE TEXT, never rendered — a blob
        // URL runs with this app's origin, so rendering it would let the file's scripts run here.
        const mimeType = ['html','htm'].includes(ext) ? 'text/plain;charset=utf-8' : rawBlob.type;
        const blob = new Blob([rawBlob], { type: mimeType });
        if (fileReqRef.current !== req) return;   // another file or client since: drop
        setInlinePreviewUrl(window.URL.createObjectURL(blob));
        setInlinePreviewFile(f);
      } else if (res.status === 404 && fileReqRef.current === req) {
        alert(NOT_AVAILABLE_MSG);   // 5.21.0: was a silent blank preview
      }
    }
  };

  const saveClientMeta = async () => {
    if (!selectedClient) return;
    const id = clientMeta.loadedFor;
    if (id !== selectedClient.id) {
      alert(id ? 'This profile belongs to a different client — reloaded, please re-check'
               : 'This profile has not loaded yet — reloaded, please re-check');
      loadClientProfile(selectedClient.id);
      return;
    }
    const changed = {};
    for (const k of CLIENT_META_KEYS) {
      if ((clientMeta[k] || '') !== (clientMetaSnapshot?.[k] || '')) changed[k] = clientMeta[k] || '';
    }
    if (Object.keys(changed).length === 0) { alert('No changes to save'); return; }
    setClientMetaSaving(true);
    const token = localStorage.getItem('token');
    const res = await authFetch(`/api/clients/${id}/profile`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(changed)
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      alert('Save failed: ' + (e.detail || res.status));
      setClientMetaSaving(false);
      return;
    }
    if (selectedClientIdRef.current === id) setClientMetaSnapshot(s => ({ ...s, ...changed }));
    setSelectedClient(prev => (prev && prev.id === id ? { ...prev, ...changed } : prev));
    // write-through: clients are re-seeded from cabinet objects on navigate-away/return
    setCabinets(prev => prev.map(cab => ({
      ...cab,
      clients: cab.clients.map(c => c.id === id ? { ...c, ...changed } : c)
    })));
    setClientMetaSaving(false); setClientMetaSaved(true);
    setTimeout(() => setClientMetaSaved(false), 2000);
  };
  // 5.5.2: folder writes go to the folder's own client, and only while that client is on screen.
  const folderOwnerOk = (folder) => {
    if (folder && selectedClient && folder.client_id === selectedClient.id) return true;
    alert('This folder belongs to a different client — nothing was saved. Please re-open the folder.');
    return false;
  };
  const saveFolderMeta = async () => {
    if (!selectedFolder || !selectedClient) return;
    if (!folderOwnerOk(selectedFolder)) return;
    const cid = selectedFolder.client_id, epoch = clientEpochRef.current;
    setFolderMetaSaving(true);
    const token = localStorage.getItem('token');
    const res = await authFetch(`/api/clients/${cid}/folders/${selectedFolder.id}/profile`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(folderMeta)
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      alert('Save failed: ' + (e.detail || res.status));
      setFolderMetaSaving(false);
      return;
    }
    // Update folder in tree
    const updateTree = (nodes) => nodes.map(n => n.id === selectedFolder.id ? { ...n, ...folderMeta } : { ...n, children: n.children ? updateTree(n.children) : [] });
    // write-through (tree-cache contract): folder profile mutates tree nodes
    setFolderTreeCache(prev => prev[cid] ? { ...prev, [cid]: updateTree(prev[cid]) } : prev);
    if (clientEpochRef.current === epoch) {
      setFolderTree(prev => updateTree(prev));
      setSelectedFolder(prev => (prev && prev.id === selectedFolder.id ? { ...prev, ...folderMeta } : prev));
    }
    setFolderMetaSaving(false); setFolderMetaSaved(true);
    setTimeout(() => setFolderMetaSaved(false), 2000);
  };
  const saveMetadata = async () => {
    if (!selectedFile) return;
    setMetaSaving(true);
    await files.updateMetadata(selectedFile.id, metadata);
    // Update file in fileList so re-selecting shows saved values
    setFileList(prev => prev.map(f => f.id === selectedFile.id ? { ...f, ...metadata } : f));
    setSelectedFile(prev => (prev && prev.id === selectedFile.id ? { ...prev, ...metadata } : prev));
    setMetaSaving(false); setMetaSaved(true);
    setTimeout(() => setMetaSaved(false), 2000);
  };

  const handleUpload = async (fileItems) => {
    if (!selectedClient || !selectedFolder) return alert('Select a client folder first');
    if (!folderOwnerOk(selectedFolder)) return;
    const cid = selectedFolder.client_id, fid = selectedFolder.id, epoch = clientEpochRef.current;
    setUploading(true);
    for (const file of fileItems) await files.upload(cid, fid, file);
    const res = await files.list(cid, fid);
    if (clientEpochRef.current === epoch && folderReqRef.current === fid) setFileList(res.data.files);
    setUploading(false);
    // Reset file input so same file can be uploaded again
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e) => { e.preventDefault(); const d = Array.from(e.dataTransfer.files); if (d.length) handleUpload(d); };

  const saveNote = async () => {
    if (!selectedClient || !selectedFolder) return alert('Select a client folder first');
    if (!folderOwnerOk(selectedFolder)) return;
    const cid = selectedFolder.client_id, fid = selectedFolder.id, epoch = clientEpochRef.current;
    const rawName = notesName.trim();
    if (!rawName) return alert('Enter a note name');
    if (!notesBody.trim()) return alert('Enter note text');
    // Sanitize name → safe filename stem (blocks path traversal: / \ .. : control chars)
    const safeStem = rawName.replace(/[\/\\:*?"<>|]/g, '_').replace(/\.\./g, '_').replace(/[\x00-\x1f]/g, '').replace(/\s+/g, ' ').trim().slice(0, 120) || 'note';
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-CA', { timeZone: 'America/Chicago' }); // YYYY-MM-DD, Central
    const filename = `${safeStem}__${dateStr}.md`;
    const content = `# ${rawName} — ${dateStr}\n\n${notesBody}\n`;
    const noteFile = new File([content], filename, { type: 'text/markdown' });
    setUploading(true);
    try {
      await files.upload(cid, fid, noteFile);
      const res = await files.list(cid, fid);
      if (clientEpochRef.current === epoch && folderReqRef.current === fid) setFileList(res.data.files);
      setShowNotesModal(false);
      setNotesName('');
      setNotesBody('');
    } catch (err) {
      alert('Failed to save note: ' + (err?.message || err));
    } finally {
      setUploading(false);
    }
  };

  const createFolder = async (name) => {
    if (!name.trim() || !selectedClient) return;
    if (selectedFolder && !folderOwnerOk(selectedFolder)) return;
    const cid = selectedFolder ? selectedFolder.client_id : selectedClient.id, epoch = clientEpochRef.current;
    await clients.createFolder(cid, { name: name.trim(), parent_id: selectedFolder?.id || null });
    const res = await clients.getFolders(cid);
    setFolderTreeCache(prev => ({ ...prev, [cid]: res.data.folders }));  // write-through (tree-cache contract)
    if (clientEpochRef.current !== epoch) return;   // switched away meanwhile
    setFolderTree(res.data.folders);
    setNewFolderName(''); setShowNewFolder(false);
  };

  const createSubfolder = async (parentFolder) => {
    if (!folderOwnerOk(parentFolder)) return;
    const cid = parentFolder.client_id, epoch = clientEpochRef.current;
    await clients.createFolder(cid, { name: newSubfolderName.trim(), parent_id: parentFolder.id });
    const res = await clients.getFolders(cid);
    setFolderTreeCache(prev => ({ ...prev, [cid]: res.data.folders }));
    if (clientEpochRef.current !== epoch) return;   // switched away meanwhile
    setFolderTree(res.data.folders);
    setAddingSubfolderTo(null);
    setNewSubfolderName('');
    setExpandedFolders(ef => ({ ...ef, [parentFolder.id]: true }));
  };
  const deleteClient = async () => {
    if (deleteClientTyped !== deleteClientTarget.name) return;
    setDeleteClientSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await authFetch(`/api/clients/delete/${deleteClientTarget.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      if (selectedClient?.id === deleteClientTarget.id) deselectClient();   // client is gone: no unsaved-work prompt
      setDeleteClientTarget(null); setDeleteClientTyped('');
      setSearchResults(null);
      const res2 = await clients.byCabinet();
      setCabinets(res2.data.cabinets);
    } catch(e) { alert('Delete failed: ' + e.message); }
    finally { setDeleteClientSaving(false); }
  };
  const runSearch = async () => {
    if (!searchQuery.trim()) { setSearchResults(null); setSearchTruncated(false); return; }
    let res;
    try {
      res = await clients.search(searchQuery, searchMode);
    } catch (e) {
      // 5.21.0: was an unhandled rejection with stale results. Restricted clients are simply
      // left out of results by the server; any failure now shows an empty list + a message.
      setSearchResults([]); setSearchTruncated(false);
      alert(isNotFound(e) ? NOT_AVAILABLE_MSG : `Search failed: ${e.message || 'error'}`);
      return;
    }
    setSearchResults(res.data.results);
    setSearchTruncated(!!res.data.truncated);
  };

  const handleSort = (key) => {
    if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(key); setSortDir('asc'); }
  };

  const sortArrow = (key) => sortCol === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  // Memoized sort — was recomputed on every render (4k+ rows). Keyed on the
  // inputs only; COLS is module-static so it's intentionally not a dep.
  const sorted = useMemo(() => {
    const col = COLS.find(c => c.key === sortCol) || COLS[0];
    const field = col?.field || 'uploaded_at';
    return [...fileList].sort((a, b) => {
      let av = a[field] || '', bv = b[field] || '';
      if (field === 'file_size') { av = Number(av); bv = Number(bv); }
      else if (field === 'uploaded_at' || field === 'modified_at') { av = new Date(av || 0); bv = new Date(bv || 0); }
      else { av = String(av).toLowerCase(); bv = String(bv).toLowerCase(); }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileList, sortCol, sortDir]);

  // ---- File list/grid virtualization (client-wide lists can be thousands) ----
  // CenterFolders renders ABOVE the files inside one shared scroll container, so
  // each virtualizer offsets by its wrapper's offsetTop (scrollMargin). Row/grid
  // wrappers are measured below; gridCols mirrors the CSS auto-fill math so the
  // virtualized grid matches the non-virtualized layout.
  const FILE_ROW_H = 30, FILE_ACTIONS_W = 124;  // 124: ✎ 💬 ✍️ ⇄ 🗑 (v4.22.0 move)
  const GRID_CARD_W = 110, GRID_GAP = 8, GRID_PAD_X = 16;
  const filesScrollRef = useRef(null);
  const listInnerRef = useRef(null);
  const gridInnerRef = useRef(null);
  const [listScrollMargin, setListScrollMargin] = useState(0);
  const [gridScrollMargin, setGridScrollMargin] = useState(0);
  const [gridCols, setGridCols] = useState(1);
  const rowTotalW = COLS.reduce((s, c) => s + (colWidths[c.key] || 0), 0) + FILE_ACTIONS_W + CHK_W;

  useLayoutEffect(() => {
    const sc = filesScrollRef.current;
    if (!sc) return;
    const measure = () => {
      if (listInnerRef.current) setListScrollMargin(listInnerRef.current.offsetTop);
      if (gridInnerRef.current) setGridScrollMargin(gridInnerRef.current.offsetTop);
      const w = sc.clientWidth - GRID_PAD_X;
      setGridCols(Math.max(1, Math.floor((w + GRID_GAP) / (GRID_CARD_W + GRID_GAP))));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(sc);
    return () => ro.disconnect();
  }, [view, folderTree, fileList.length]);

  const listVirtualizer = useVirtualizer({
    count: view === 'grid' ? 0 : sorted.length,
    getScrollElement: () => filesScrollRef.current,
    estimateSize: () => FILE_ROW_H,
    overscan: 12,
    scrollMargin: listScrollMargin,
  });

  const gridRowCount = Math.max(1, Math.ceil(sorted.length / gridCols));
  const gridVirtualizer = useVirtualizer({
    count: view === 'grid' ? gridRowCount : 0,
    getScrollElement: () => filesScrollRef.current,
    estimateSize: () => 130,
    overscan: 6,
    scrollMargin: gridScrollMargin,
    measureElement: (el) => (el ? el.getBoundingClientRect().height : 130),
  });

  const folderResizing = React.useRef(false);
  const startFolderDescResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    folderResizing.current = true;
    const startX = e.clientX, startW = folderDescWidth;
    const onMove = ev => { ev.preventDefault(); setFolderDescWidth(Math.max(100, startW - (ev.clientX - startX))); };
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); setTimeout(() => { folderResizing.current = false; }, 50); };
    document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
  };
  const startFolderDateResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    folderResizing.current = true;
    const startX = e.clientX, startW = folderDateWidth;
    const onMove = ev => { ev.preventDefault(); setFolderDateWidth(Math.max(50, startW - (ev.clientX - startX))); };
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); setTimeout(() => { folderResizing.current = false; }, 50); };
    document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
  };
  const startResize = (col, e) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX, startW = colWidths[col];
    resizing.current = { col, startX, startW };
    const onMove = (ev) => {
      if (!resizing.current) return;
      const newW = Math.max(40, resizing.current.startW + ev.clientX - resizing.current.startX);
      setColWidths(w => ({ ...w, [resizing.current.col]: newW }));
    };
    const onUp = () => { resizing.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const downloadFile = async (fileObj) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`/api/files/download/${fileObj.id}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { alert(res.status === 404 ? NOT_AVAILABLE_MSG : `Download failed (HTTP ${res.status})`); return; }   // 5.21.0
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = fileObj.filename; a.click();
    window.URL.revokeObjectURL(url);
  };

  // ── File-pane multi-select: derived state, effects, handlers (v4.11.0) ────
  const resolvedSelected = useMemo(() => {            // deduped union: checked files + folder-resolved files
    const m = new Map();
    selFileIds.forEach(id => { const o = selObjCache[id]; if (o) m.set(id, o); });
    selFolderIds.forEach(fid => (folderFilesMap[fid] || []).forEach(o => m.set(o.id, o)));
    return [...m.values()];
  }, [selFileIds, selFolderIds, folderFilesMap, selObjCache]);
  const selCount = resolvedSelected.length;
  const checkedFileObjs = useMemo(                     // Option B: directly-checked FILES only (move scope)
    () => [...selFileIds].map(id => selObjCache[id]).filter(Boolean), [selFileIds, selObjCache]);

  const clearSelection = () => { setSelFileIds(new Set()); setSelFolderIds(new Set()); setSelNote(''); };
  // Clear selection on client switch (move is same-client; avoids stale cross-client picks).
  useEffect(() => { setSelFileIds(new Set()); setSelFolderIds(new Set()); setSelNote(''); setFolderFilesMap({}); setSelObjCache({}); }, [selectedClient?.id]);
  // 5.38.0 follow-ups: whom this login may target (once per page)
  useEffect(() => {
    calendarApi.followupTargets().then((r) => setFollowupTargets(r.data)).catch(() => setFollowupTargets({ allowed: false }));
  }, []);
  const canFollowup = !!followupTargets?.allowed;
  // a folder (tree, center panel) or the client row: right-click -> "📅 Follow-up" for THAT client (never the subfolder)
  const openFolderMenu = (e, client) => {
    if (!canFollowup || !client) return;           // nothing to offer: keep the browser's own menu
    e.preventDefault(); e.stopPropagation();
    setFolderMenu({ x: e.clientX, y: e.clientY, client });
  };
  useEffect(() => {
    if (!folderMenu) return;
    const close = () => setFolderMenu(null);
    const onKey = e => { if (e.key === 'Escape') setFolderMenu(null); };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('click', close); window.removeEventListener('scroll', close, true); window.removeEventListener('keydown', onKey); };
  }, [folderMenu]);

  // Dismiss right-click menu on outside-click / Escape / capture-phase scroll.
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    const onKey = e => { if (e.key === 'Escape') setMenu(null); };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('click', close); window.removeEventListener('scroll', close, true); window.removeEventListener('keydown', onKey); };
  }, [menu]);

  const toggleFile = (f, e) => {
    e.stopPropagation();
    setSelFileIds(prev => {
      const next = new Set(prev);
      if (next.has(f.id)) { next.delete(f.id); setSelNote(''); }
      else if (selCount >= CAP) { setSelNote(`Limit of ${CAP} files reached`); return prev; }
      else { next.add(f.id); setSelObjCache(c => ({ ...c, [f.id]: f })); setSelNote(''); }
      return next;
    });
  };
  const toggleFolder = async (folder, e) => {
    e.stopPropagation();
    if (selFolderIds.has(folder.id)) {
      setSelFolderIds(prev => { const n = new Set(prev); n.delete(folder.id); return n; });
      setSelNote(''); return;
    }
    let objs = folderFilesMap[folder.id];
    if (!objs) {
      try { objs = (await files.list(folder.client_id || selectedClient.id, folder.id)).data.files;
            setFolderFilesMap(m => ({ ...m, [folder.id]: objs })); }
      catch { setSelNote('Could not load folder contents'); return; }
    }
    const existing = new Set(resolvedSelected.map(o => o.id));
    const added = objs.filter(o => !existing.has(o.id)).length;
    if (objs.length > CAP || existing.size + added > CAP) {
      setSelNote(`"${folder.name}" has ${objs.length} files — exceeds the ${CAP}-file limit`); return;
    }
    setSelObjCache(c => { const n = { ...c }; objs.forEach(o => { n[o.id] = o; }); return n; });
    setSelFolderIds(prev => new Set(prev).add(folder.id)); setSelNote('');
  };

  const bulkDownload = async () => {                   // sequential, 300ms-paced, reuse downloadFile
    setMenu(null); setBulkBusy(true);
    for (const f of resolvedSelected) { await downloadFile(f); await new Promise(r => setTimeout(r, 300)); }
    setBulkBusy(false);
  };
  const bulkDelete = async () => {                     // admin-only; one soft-delete call per file
    setMenu(null);
    if (!window.confirm(`Delete these ${resolvedSelected.length} files?\n\n${resolvedSelected.map(f => f.filename).join('\n')}\n\nFiles will be recoverable for 60 days.`)) return;
    setBulkBusy(true);
    const token = localStorage.getItem('token'); let failed = 0;
    for (const f of resolvedSelected) {
      try { const res = await authFetch(`/api/files/soft-delete/${f.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }); if (!res.ok) failed++; }
      catch { failed++; }
    }
    const fileRes = await files.list(selectedClient.id, selectedFolder?.id);
    setFileList(fileRes.data.files);
    if (selectedFile && resolvedSelected.some(f => f.id === selectedFile.id)) { setSelectedFile(null); setMetadata({}); setInlinePreviewUrl(null); }
    setFolderFilesMap({}); clearSelection(); setBulkBusy(false);
    if (failed) alert(`${failed} file(s) could not be deleted.`);
  };
  const runMove = async (targetFolderId) => {          // files only; reuse POST /move; refetch fileList ONLY
    setMenu(null); setMoveBusy(true);
    const token = localStorage.getItem('token'); let failed = 0;
    for (const f of moveTargets) {
      try { const res = await authFetch(`/api/files/move/${f.id}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ target_folder_id: targetFolderId }) }); if (!res.ok) failed++; }
      catch { failed++; }
    }
    // File-level move: per the folder-tree cache contract, folderTreeCache is deliberately NOT invalidated.
    const fileRes = await files.list(selectedClient.id, selectedFolder?.id);
    setFileList(fileRes.data.files);
    setMoveBusy(false); setMoveModalOpen(false); setMoveFolder(null); setMoveSearch(''); clearSelection();
    if (failed) alert(`${failed} file(s) could not be moved.`);
  };
  const openMenu = (e, f) => { e.preventDefault(); e.stopPropagation(); setMenu({ x: e.clientX, y: e.clientY, file: f }); };
  // 5.13.0: start a signature request in ACORD's Send for Signature screen (new tab, like
  // the other ACORD links). ACORD fetches the file server-side with the user's own login;
  // the link carries ids only. PDFs only.
  const isPdfFile = (f) => (f?.extension || '').toLowerCase() === 'pdf' || (f?.filename || '').toLowerCase().endsWith('.pdf');
  const openSignFlow = (f, clientId) => {
    const q = new URLSearchParams({ sign: 'upload' });
    const cid = f?.client_id ?? clientId ?? selectedClient?.id;
    if (cid != null) q.set('sign_client', String(cid));
    if (f) q.set('sign_file', String(f.id));
    window.open(`/acord/?${q.toString()}`, '_blank');
  };

  const openMmModal = async (file) => {
    const token = localStorage.getItem('token');
    setMmFile(file); setMmRecipient(''); setMmSendType('file');
    setMmMessage(''); setMmError(''); setMmSuccess(false); setMmModalOpen(true);
    if (mmUsers.length === 0) {
      try {
        const res = await fetch('/api/mattermost/users', { headers: { Authorization: `Bearer ${token}` } });
        setMmUsers(await res.json());
      } catch { setMmError('Could not load users.'); }
    }
  };
  const handleMmSend = async () => {
    const token = localStorage.getItem('token');
    setMmSending(true); setMmError('');
    try {
      const res = await authFetch('/api/mattermost/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ file_id: mmFile.id, recipient_mm_id: mmRecipient, send_type: mmSendType, message: mmMessage })
      });
      setMmSuccess(true); setTimeout(() => setMmModalOpen(false), 1500);
    } catch(e) { setMmError(e.message || 'Failed to send.'); }
    finally { setMmSending(false); }
  };
  const previewFile = async (fileObj) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`/api/files/view/${fileObj.id}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { alert(res.status === 404 ? NOT_AVAILABLE_MSG : `Preview failed (HTTP ${res.status})`); return; }   // 5.21.0
    const blob = await res.blob();
    setPreviewUrl(window.URL.createObjectURL(blob));
    setShowPreviewModal(true);
  };

  // Unified PDF editor — opens for ANY PDF (form fields if present + markup) in
  // a detached window (?view=editor mounts only EditorWindow, see index.js).
  // Pass the specific file so row/inline buttons act on THEIR file, not selectedFile.
  const openEditor = (fileObj) => {
    if (!fileObj || (fileObj.extension || '').toLowerCase() !== 'pdf') return;
    const qs = new URLSearchParams({
      view: 'editor',
      fileId: String(fileObj.id),
      filename: fileObj.filename || '',
      dark: darkMode ? '1' : '0',
    });
    const w = window.open('/?' + qs.toString(), 'idedit_' + fileObj.id,
      'width=1200,height=950,menubar=no,toolbar=no,location=no,status=no');
    if (!w) { alert('Pop-up blocked — allow pop-ups for this site to open the editor.'); return; }
    try { w.focus(); } catch (_e) { /* focus may be refused; harmless */ }
  };

  // Detached editor windows broadcast a save; refresh the folder list and
  // re-point any on-screen preview of the edited file (no-store refetch + new
  // blob URL + iframe key remount — all three are needed, browsers cache PDF
  // iframes hard). No alert() in the opener — the save notice belongs to the
  // editor window.
  //
  // The channel intentionally re-subscribes whenever these deps change, so the
  // handler always closes over the current selection/preview state. The
  // sub-millisecond gap during teardown+resubscribe is an accepted trade-off —
  // do NOT "fix" this into a refs-based single-subscribe.
  useEffect(() => {
    let ch;
    try { ch = new BroadcastChannel('insuredocs-edits'); } catch (_e) { return; }
    ch.onmessage = async (ev) => {
      const editedId = ev?.data?.fileId;
      if (!editedId) return;
      try {
        if (selectedClient) {
          const epoch = clientEpochRef.current;
          const fr = await files.list(selectedClient.id, selectedFolder?.id);
          if (clientEpochRef.current !== epoch) return;   // 5.5.2: switched meanwhile: drop
          setFileList(fr.data.files);
          if (selectedFile?.id === editedId) {
            const updated = fr.data.files.find(x => x.id === editedId);
            if (updated) setSelectedFile(updated);
          }
        }
      } catch (_e) { /* non-fatal */ }
      const token = localStorage.getItem('token');
      const refetch = async () => {
        const r = await fetch(`/api/files/view/${editedId}?t=${Date.now()}`,
          { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' });
        return r.ok ? await r.blob() : null;
      };
      if (showPreviewModal && selectedFile?.id === editedId) {
        try { const b = await refetch(); if (b) { setPreviewUrl(prev => { if (prev) window.URL.revokeObjectURL(prev); return window.URL.createObjectURL(b); }); setPreviewReloadKey(k => k + 1); } } catch (_e) {}
      }
      if (inlinePreviewFile?.id === editedId) {
        try { const b2 = await refetch(); if (b2) { setInlinePreviewUrl(prev => { if (prev) window.URL.revokeObjectURL(prev); return window.URL.createObjectURL(b2); }); setInlinePreviewReloadKey(k => k + 1); } } catch (_e) {}
      }
    };
    return () => { try { ch.close(); } catch (_e) {} };
  }, [selectedClient, selectedFolder, selectedFile, showPreviewModal, inlinePreviewFile]);

  const bg = darkMode ? '#1a1a1a' : '#f5f5f5';
  const panelBg = darkMode ? '#242424' : '#fff';
  const inputBg = darkMode ? '#1a1a1a' : '#ffffff';
  const borderColor = darkMode ? '#333' : '#e5e5e5';
  const textPrimary = darkMode ? '#f0f0f0' : '#1a1a1a';
  const textSecondary = darkMode ? '#aaa' : '#666';
  const cabBg = darkMode ? '#1a1a2e' : '#E6F1FB';
  // Shared action-bar/menu button style (depends on the theme vars above).
  const ACTION_BTN = { padding: '4px 10px', fontSize: 11, border: `1px solid ${borderColor}`, borderRadius: 5, cursor: 'pointer', background: panelBg, color: textPrimary };

  function renderFolderTree(folders, depth = 0) {
    folders = [...folders].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }));
    folders = [...folders].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }));
    return folders.map(f => {
      const hasChildren = f.children?.length > 0;
      const isExp = expandedFolders[f.id];
      return (
        <div key={f.id}>
          <div onContextMenu={e => openFolderMenu(e, selectedClient)} style={{
            padding: `5px 10px 5px ${12 + depth * 14}px`, cursor: 'pointer',
            color: selectedFolder?.id === f.id ? '#185FA5' : textPrimary,
            background: selectedFolder?.id === f.id ? (darkMode ? '#0d1f2d' : '#E6F1FB') : 'transparent',
            borderLeft: selectedFolder?.id === f.id ? '2px solid #185FA5' : '2px solid transparent',
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <span onClick={(e) => { e.stopPropagation(); if (hasChildren) setExpandedFolders(ef => ({...ef, [f.id]: !ef[f.id]})); }}
              style={{ fontSize: 9, width: 10, color: textSecondary, cursor: hasChildren ? 'pointer' : 'default', flexShrink: 0 }}>
              {hasChildren ? (isExp ? '▼' : '▶') : ''}
            </span>
            <span onClick={(e) => { e.stopPropagation(); selectFolder(f); }}
              onDoubleClick={e => startRenameFolder(f, e)}
              style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1 }}>
              <span style={{ fontSize: 11 }}>📁</span>
              {renamingFolder === f.id ? (
                <input value={renameFolderValue} onChange={e => setRenameFolderValue(e.target.value)}
                  onBlur={() => submitRenameFolder(f)}
                  onKeyDown={e => { if (e.key === 'Enter') submitRenameFolder(f); if (e.key === 'Escape') setRenamingFolder(null); }}
                  autoFocus onClick={e => e.stopPropagation()}
                  style={{ width: 120, padding: '1px 4px', fontSize: 11, border: '1px solid #185FA5', borderRadius: 3, background: bg, color: textPrimary }} />
              ) : (
                <span style={{ color: textPrimary, flex: 1 }}>{f.name}</span>
              )}
            </span>
            {selectedFolder?.id === f.id && (
              <span onDoubleClick={e => { e.stopPropagation(); setEditingFolderDesc(f.id); setEditFolderDescValue(f.description || ''); }}
                title={f.description ? "Double-click to edit description" : "Double-click to add description"}
                style={{ fontSize: 10, color: '#4f8ef7', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120, cursor: 'text', flexShrink: 0 }}>
                {editingFolderDesc === f.id ? (
                  <input autoFocus value={editFolderDescValue}
                    onChange={e => setEditFolderDescValue(e.target.value)}
                    onBlur={() => saveFolderDescription(f.id, editFolderDescValue)}
                    onKeyDown={e => { if (e.key === 'Enter') saveFolderDescription(f.id, editFolderDescValue); if (e.key === 'Escape') setEditingFolderDesc(null); }}
                    onClick={e => e.stopPropagation()}
                    style={{ width: 120, fontSize: 10, padding: '1px 4px', border: '1px solid #4f8ef7', borderRadius: 3, background: bg, color: textPrimary }} />
                ) : (f.description || <span style={{ color: '#555', fontStyle: 'italic' }}>+ desc</span>)}
              </span>
            )}
            <span onClick={e => { e.stopPropagation(); setAddingSubfolderTo(f.id); setNewSubfolderName(''); setExpandedFolders(ef => ({...ef, [f.id]: true})); }}
              title="New subfolder"
              style={{ fontSize: 10, color: '#4a9eda', cursor: 'pointer', padding: '0 4px', opacity: 0.7, flexShrink: 0 }}>+</span>
            {(user?.role === 'admin' || user?.role === 'manager') && (
            <>
            <span onClick={e => { e.stopPropagation(); setMoveFolder(f); setMoveTargets([]); setMoveSearch(''); setMoveModalOpen(true); }}
              title="Move folder"
              style={{ fontSize: 12, color: '#185FA5', cursor: 'pointer', padding: '0 4px', flexShrink: 0 }}>⇄</span>
            <span onClick={e => { e.stopPropagation(); setDeletingFolder(f); }}
              title="Delete folder"
              style={{ fontSize: 12, color: '#c0392b', cursor: 'pointer', padding: '0 4px', opacity: 1, flexShrink: 0 }}>🗑</span>
            </>
          )}
          </div>
          {addingSubfolderTo === f.id && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: `4px 10px 4px ${12 + (depth + 1) * 14}px` }}>
              <span style={{ fontSize: 11 }}>📁</span>
              <input
                autoFocus
                value={newSubfolderName}
                onChange={e => setNewSubfolderName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') createSubfolder(f); if (e.key === 'Escape') setAddingSubfolderTo(null); }}
                placeholder="Subfolder name..."
                style={{ flex: 1, padding: '2px 6px', fontSize: 11, border: '1px solid #185FA5', borderRadius: 3, background: bg, color: textPrimary }}
              />
              <span onClick={() => createSubfolder(f)} style={{ fontSize: 11, color: '#4a9eda', cursor: 'pointer' }}>✓</span>
              <span onClick={() => setAddingSubfolderTo(null)} style={{ fontSize: 11, color: '#888', cursor: 'pointer' }}>✕</span>
            </div>
          )}
          {hasChildren && isExp && renderFolderTree(f.children, depth + 1)}
        </div>
      );
    });
  }

  const deleteCabinet = async () => {
    if (!deleteCabinetTarget) return;
    if (deleteCabinetTyped !== deleteCabinetTarget.cabinet) return;
    setDeleteCabinetSaving(true);
    try {
      await clients.deleteCabinet(deleteCabinetTarget.id);
      const cabRes = await clients.byCabinet();
      setCabinets(cabRes.data.cabinets);
      if (selectedClient?.id === deleteCabinetTarget.id) deselectClient();   // client is gone: no unsaved-work prompt
      setDeleteCabinetTarget(null);
      setDeleteCabinetConfirm1(false);
      setDeleteCabinetTyped('');
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    }
    setDeleteCabinetSaving(false);
  };

  const loadTrash = async () => {
    setTrashLoading(true);
    const token = localStorage.getItem('token');
    const res = await fetch('/api/files/trash', { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json();
    setTrashedFiles(data.files || []);
    setTrashLoading(false);
  };

  const restoreFile = async (f) => {
    const token = localStorage.getItem('token');
    const res = await authFetch(`/api/files/restore/${f.id}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (res.ok) {
      await loadTrash();
    } else {
      const e = await res.json();
      alert('Restore failed: ' + e.detail);
    }
  };

  const FLAG_COLORS = {
    red:    { bg: '#e74c3c', label: '🚨 Alert',    desc: 'Urgent attention needed' },
    orange: { bg: '#e67e22', label: '⚠️ Warning',  desc: 'Needs review' },
    yellow: { bg: '#f1c40f', label: '⭐ Note',     desc: 'General note' },
    purple: { bg: '#8e44ad', label: '🔒 Hold',     desc: 'Do not modify' },
    blue:   { bg: '#2980b9', label: '📋 Info',     desc: 'Informational flag' },
  };

  const getFlagIcon = (c) => {
    if (c.flag) return FLAG_COLORS[c.flag]?.label.split(' ')[0] || '🚩';
    if (/\d+x\s/i.test(c.name) || /^0\d+x/i.test(c.name)) return '⚠️';
    return null;
  };

  const loadUsers = async () => {
    const token = localStorage.getItem('token');
    const res = await fetch('/api/auth/users', { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json();
    setUserList(data.users || []);
    // 5.32.1: Davis (HDCal) accounts + who uses each — for the Calendar column dropdown. Optional: ignore failures.
    try {
      const r2 = await fetch('/api/calendar/accounts', { headers: { Authorization: `Bearer ${token}` } });
      if (r2.ok) { const d2 = await r2.json(); setCalAccounts(d2.accounts || []); }
    } catch (_e) { /* the column still works without the list */ }
  };

  const saveUser = async (userId, updates) => {
    setUserSaving(true);
    const token = localStorage.getItem('token');
    const res = await authFetch(`/api/auth/users/${userId}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); alert('Save failed: ' + (e.detail || res.status)); setUserSaving(false); return; }
    await loadUsers();
    setEditingUser(null);
    setUserSaving(false);
  };

  const deleteUser = async (userId, username) => {
    if (!window.confirm(`Delete user "${username}"? This cannot be undone.`)) return;
    const token = localStorage.getItem('token');
    const res = await authFetch(`/api/auth/users/${userId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); alert('Delete failed: ' + (e.detail || res.status)); return; }
    await loadUsers();
  };

  const createUser = async () => {
    if (!newUserForm.username || !newUserForm.password || !newUserForm.full_name) {
      alert('Username, full name and password are required');
      return;
    }
    setUserSaving(true);
    const token = localStorage.getItem('token');
    const res = await authFetch('/api/auth/create-user', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(newUserForm)
    });
    if (res.ok) {
      const made = await res.json().catch(() => ({}));
      if (made.note) alert(made.note);   // 5.32.1: e.g. personal calendar left off (account already used)
      setNewUserForm({ username: '', full_name: '', password: '', role: 'staff' });
      await loadUsers();
    } else {
      const e = await res.json();
      alert('Error: ' + e.detail);
    }
    setUserSaving(false);
  };

  const sendToInsureIQ = async (question, opts = {}) => {
    const q = question || iqInput.trim();
    if (!q || iqLoading) return;
    if (!opts.noEcho) {
      setIqInput('');
      setIqMessages(prev => [...prev, { role: 'user', content: q }]);
    }
    setIqLoading(true);
    setIqStatus(null);
    try {
      const body = {
        question: q,
        mode: iqMode,
        top_k: 8,
        tier: opts.tier || 'auto',
        selected_file_ids: iqMode === 'general' ? [] : Array.from(iqFileIds),
      };
      if (opts.escalateOf) body.escalate_of = opts.escalateOf;
      // Answers are stamped with the client they were asked about; one that lands after a
      // switch is shown with that client's name.
      const askedFor = selectedClient ? { id: selectedClient.id, name: selectedClient.name } : null;
      const label = () => ((askedFor?.id ?? null) !== selectedClientIdRef.current
        ? { askedAbout: askedFor ? askedFor.name : 'no client' } : {});
      if (selectedClient) {
        body.client_id = selectedClient.id;          // internal id — mdstore key + file scoping
        body.client_name = selectedClient.name;
      }
      if (selectedFolder) {
        body.folder_context = selectedFolder.name;
      }
      const token = localStorage.getItem('token');
      const res = await authFetch('/api/ask', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const pushAnswer = (data) => setIqMessages(prev => [...prev, {
        role: 'ai',
        content: data.answer || 'No response.',
        mode: data.mode,
        elapsed: data.elapsed,
        sources: data.sources || [],
        tier: data.tier,               // undefined when routing is off — UI degrades to today's look
        confidence: data.confidence,
        ask_id: data.ask_id,
        question: q,                   // kept for anchoring-free escalation (re-send question, never the answer)
        clientId: askedFor?.id ?? null,
        ...label(),
      }]);
      const ctype = res.headers.get('content-type') || '';
      if (ctype.includes('ndjson') && res.body) {
        // Routed shape: NDJSON stream — a route event (drives the honest loading
        // state, incl. fast-tier cold start), then the answer.
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = '';
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          let nl;
          while ((nl = buf.indexOf('\n')) >= 0) {
            const line = buf.slice(0, nl).trim();
            buf = buf.slice(nl + 1);
            if (!line) continue;
            const ev = JSON.parse(line);
            if (ev.event === 'route') {
              setIqStatus(ev.tier === 'fast'
                ? (ev.cold_start ? '⚡ Warming up the fast model (~10s)…' : '⚡ Fast answer…')
                : '🧠 Deep analysis — this can take up to a minute…');
            } else if (ev.event === 'answer') {
              pushAnswer(ev);
            } else if (ev.event === 'error') {
              setIqMessages(prev => [...prev, { role: 'ai', content: 'Error: ' + (ev.detail || 'request failed'), ...label() }]);
            }
          }
        }
      } else {
        const data = await res.json();
        pushAnswer(data);
      }
    } catch (e) {
      setIqMessages(prev => [...prev, { role: 'ai', content: 'Connection error — InsureIQ may be busy.' }]);
    }
    setIqLoading(false);
    setIqStatus(null);
    setTimeout(() => iqEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const escalateIq = (idx) => {
    const msg = iqMessages[idx];
    if (!msg || msg.escalated || iqLoading) return;
    if ((msg.clientId ?? null) !== selectedClientIdRef.current) {   // 5.5.2: never re-ask under another client
      alert('This answer was about a different client — ask again here instead.');
      return;
    }
    setIqMessages(prev => prev.map((m, j) => j === idx ? { ...m, escalated: true } : m));
    // Original question + full context to SMART; the fast answer is never sent (no anchoring).
    sendToInsureIQ(msg.question, { tier: 'smart', escalateOf: msg.ask_id, noEcho: true });
  };

  const submitFlag = async () => {
    if (!flagClient) return;
    try {
      const token = localStorage.getItem('token');
      const res = await authFetch(`/api/clients/${flagClient.id}/flag`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ flag: flagValue || null, flag_note: flagNote })
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); alert('Failed: ' + (e.detail || res.status)); return; }
      // Update in cabinets state
      setCabinets(prev => prev.map(cab => ({
        ...cab,
        clients: cab.clients.map(c => c.id === flagClient.id ? { ...c, flag: flagValue || null, flag_note: flagNote } : c)
      })));
      // Update selectedClient if it's the one being flagged (so profile pane refreshes)
      setSelectedClient(prev => prev && prev.id === flagClient.id ? { ...prev, flag: flagValue || null, flag_note: flagNote } : prev);
      // Update searchResults if present (so search view refreshes)
      setSearchResults(prev => {
        if (!prev || !Array.isArray(prev)) return prev;
        return prev.map(c => c.id === flagClient.id ? { ...c, flag: flagValue || null, flag_note: flagNote } : c);
      });
      setFlagClient(null);
    } catch (e) { alert('Failed: ' + e.message); }
  };

  const saveFolderDescription = async (folderId, desc) => {
    setEditingFolderDesc(null);
    const token = localStorage.getItem('token');
    const res = await authFetch(`/api/clients/folder/${folderId}/description`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ description: desc })
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      alert('Save failed: ' + (e.detail || res.status));
      return;
    }
    // Update folder in tree
    const updateTree = (folders) => folders.map(f => {
      if (f.id === folderId) return { ...f, description: desc };
      if (f.children) return { ...f, children: updateTree(f.children) };
      return f;
    });
    setFolderTree(prev => updateTree(prev));
    // write-through (tree-cache contract): description mutates a tree node
    if (selectedClient) setFolderTreeCache(prev => prev[selectedClient.id] ? { ...prev, [selectedClient.id]: updateTree(prev[selectedClient.id]) } : prev);
    if (selectedFolder?.id === folderId) setSelectedFolder(prev => ({ ...prev, description: desc }));
  };

  const saveDescription = async (fileId, desc) => {
    setEditingDesc(null);
    const token = localStorage.getItem('token');
    const res = await authFetch(`/api/files/description/${fileId}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ description: desc })
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      alert('Save failed: ' + (e.detail || res.status));
      return;
    }
    setFileList(prev => prev.map(f => f.id === fileId ? { ...f, description: desc } : f));
  };

  const submitRenumber = async () => {
    if (!renumberNum.trim() || !renumberClient) return;
    setRenumberSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await authFetch(`/api/clients/renumber/${renumberClient.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_number: renumberNum.trim() })
      });
      if (!res.ok) { const e = await res.json(); alert('Failed: ' + e.detail); setRenumberSaving(false); return; }
      const data = await res.json();
      // Refresh cabinets
      const cabRes = await clients.byCabinet();
      setCabinets(cabRes.data.cabinets);
      // Update selected client if it was renumbered
      if (selectedClient?.id === renumberClient.id) {
        setSelectedClient(prev => (prev && prev.id === renumberClient.id ? { ...prev, name: data.new_name } : prev));
      }
      // Tree-cache contract: renumber rewrote folder.path for every folder of
      // this client server-side, so its cached tree is stale. Refetch + write
      // through, keyed on renumberClient.id (NOT selectedClient — you can
      // renumber a client that isn't the one open). Refresh the visible tree
      // too if it happens to be the selected client.
      try {
        const treeRes = await clients.getFolders(renumberClient.id);
        setFolderTreeCache(prev => ({ ...prev, [renumberClient.id]: treeRes.data.folders }));
        if (selectedClientIdRef.current === renumberClient.id) setFolderTree(treeRes.data.folders);
      } catch (_) { /* non-fatal: next client switch will refetch */ }
      setRenumberClient(null);
      setRenumberNum('');
      alert(`Renamed to: ${data.new_name}`);
    } catch (e) { alert('Failed: ' + e.message); }
    setRenumberSaving(false);
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`Delete "${f.filename}"?\n\nFile will be recoverable for 60 days.`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await authFetch(`/api/files/soft-delete/${f.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) { const e = await res.json(); alert('Delete failed: ' + e.detail); return; }
      if (selectedFile?.id === f.id) { setSelectedFile(null); setMetadata({}); setInlinePreviewUrl(null); }
      const fileRes = await files.list(selectedClient.id, selectedFolder?.id);
      setFileList(fileRes.data.files);
    } catch (e) { alert('Delete failed: ' + e.message); }
  };

  const startRenameFile = (f, e) => {
    e.stopPropagation();
    setRenamingFile(f.id);
    setRenameFileValue(f.filename);
  };

  const submitRenameFile = async (f) => {
    if (!renameFileValue.trim() || renameFileValue === f.filename) {
      setRenamingFile(null); return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await authFetch(`/api/files/rename/${f.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_name: renameFileValue.trim() })
      });
      if (!res.ok) { const e = await res.json(); alert('Rename failed: ' + e.detail); return; }
      const fileRes = await files.list(selectedClient.id, selectedFolder?.id);
      setFileList(fileRes.data.files);
      if (selectedFile?.id === f.id) setSelectedFile(prev => ({ ...prev, filename: renameFileValue.trim() }));
    } catch (e) { alert('Rename failed: ' + e.message); }
    setRenamingFile(null);
  };

  const deleteFolder = async (folder) => {
    try {
      const token = localStorage.getItem('token');
      const res = await authFetch(`/api/files/delete-folder/${folder.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        // Defensive per §13: the body may not be JSON (a bare "Internal Server
        // Error" 500 was the original bug). Never let res.json()'s parse throw
        // become the user-facing message — fall back to the status line.
        let msg;
        try { const e = await res.json(); msg = e.detail || `${res.status} ${res.statusText}`; }
        catch (_) { msg = `${res.status} ${res.statusText}`; }
        alert('Delete failed: ' + msg);
        return;
      }
      // Read the success payload defensively: if files_soft_deleted is missing
      // for any reason, fall back to a countless confirmation (never "undefined").
      const data = await res.json().catch(() => ({}));
      setDeletingFolder(null);
      if (selectedFolder?.id === folder.id) {
        setSelectedFolder(null);
        setFileList([]);
      }
      const fRes = await clients.getFolders(selectedClient.id);
      setFolderTree(fRes.data.folders);
      setFolderTreeCache(prev => ({ ...prev, [selectedClient.id]: fRes.data.folders }));
      // Soft-delete now, not a DB purge — tell the admin the files are recoverable.
      const n = data.files_soft_deleted;
      alert(Number.isInteger(n)
        ? `Folder deleted — ${n} file(s) moved to Trash (restorable).`
        : 'Folder deleted.');
    } catch (e) { alert('Delete failed: ' + e.message); }
  };

  const startRenameFolder = (f, e) => {
    e.stopPropagation();
    setRenamingFolder(f.id);
    setRenameFolderValue(f.name);
  };

  const submitRenameFolder = async (f) => {
    if (!renameFolderValue.trim() || renameFolderValue === f.name) {
      setRenamingFolder(null); return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await authFetch(`/api/files/rename-folder/${f.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_name: renameFolderValue.trim() })
      });
      if (!res.ok) { const e = await res.json(); alert('Rename failed: ' + e.detail); return; }
      const fRes = await clients.getFolders(selectedClient.id);
      setFolderTree(fRes.data.folders);
      setFolderTreeCache(prev => ({ ...prev, [selectedClient.id]: fRes.data.folders }));
    } catch (e) { alert('Rename failed: ' + e.message); }
    setRenamingFolder(null);
  };

  const moveFile = async (fileId, targetFolderId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await authFetch(`/api/files/move/${fileId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_folder_id: targetFolderId })
      });
      if (!res.ok) { const e = await res.json(); alert('Move failed: ' + e.detail); return; }
      const fileRes = await files.list(selectedClient.id, selectedFolder?.id);
      setFileList(fileRes.data.files);
    } catch (e) {
      alert('Move failed: ' + e.message);
    }
  };

  const openMoveFolder = (f) => {
    setMoveFolder(f);
    setMoveTargets([]);
    setMoveSearch('');
    setMoveModalOpen(true);
  };
  const openMoveFile = (f) => {
    setMoveFolder(null);
    setMoveTargets([f]);
    setMoveSearch('');
    setMoveModalOpen(true);
  };
  const runMoveFolder = async (targetFolderId) => {
    if (!moveFolder) return;
    const dest = flattenFolders(folderTree).find(x => x.id === targetFolderId);
    if (!window.confirm(`Move “${moveFolder.name}” into “${dest?.name || targetFolderId}”?`)) return;
    setMoveBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await authFetch(`/api/files/move-folder/${moveFolder.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ target_folder_id: targetFolderId })
      });
      if (!res.ok) {
        let detail = res.status;
        try { const e = await res.json(); detail = e.detail || detail; } catch (_err) {}
        alert('Move failed: ' + detail);
        setMoveBusy(false);
        return;
      }
      const fRes = await clients.getFolders(selectedClient.id);
      setFolderTree(fRes.data.folders);
      setFolderTreeCache(prev => ({ ...prev, [selectedClient.id]: fRes.data.folders }));
      setMoveBusy(false);
      setMoveModalOpen(false);
      setMoveFolder(null);
      setMoveSearch('');
    } catch (e) {
      alert('Move failed: ' + e.message);
      setMoveBusy(false);
    }
  };

  const createNewCabinet = async () => {
    if (!newCabinetName.trim()) return;
    setNewCabinetSaving(true);
    try {
      await clients.createCabinet({ name: newCabinetName.trim(), restricted: newCabinetRestricted });
      const cabRes = await clients.byCabinet();
      setCabinets(cabRes.data.cabinets);
      setShowNewCabinet(false);
      setNewCabinetName('');
      setNewCabinetRestricted(false);
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    }
    setNewCabinetSaving(false);
  };

  const openNewClientModal = async () => {
    setShowNewClient(true);
    setNewClientName('');
    setNewClientAddress('');
    setNewClientPhone('');
    setNewClientEmail('');
    setNewClientNotes('');
    setNewClientDupes(null);
    const res = await clients.nextNumber();
    setNextClientNum(res.data.next_number);
  };

  // 5.14.0: check for possible duplicates first (warn, never block). Matches are shown in
  // the modal with "Open this client" / "Create anyway"; a failed check never stops a create.
  const createNewClient = async ({ skipDuplicateCheck = false } = {}) => {
    if (!newClientName.trim() || !newClientAddress.trim()) return;
    setNewClientSaving(true);
    if (!skipDuplicateCheck) {
      try {
        const d = await clients.possibleDuplicates({
          name: newClientName.trim(), phone: newClientPhone.trim(), email: newClientEmail.trim(),
        });
        const found = d.data?.results || [];
        if (found.length) {
          setNewClientDupes(found);
          setNewClientSaving(false);
          return;
        }
      } catch (_e) { /* check unavailable: create as before */ }
    }
    try {
      const res = await clients.createNew({
        name: newClientName.trim(),
        address_name: newClientAddress.trim(),
        phone1: newClientPhone.trim(),
        email: newClientEmail.trim(),
        notes: newClientNotes.trim(),
      });
      const newClient = res.data.client;
      // Refresh cabinet list
      const cabRes = await clients.byCabinet();
      setCabinets(cabRes.data.cabinets);
      // Select the new client
      await selectClient(newClient);
      setShowNewClient(false);
      setNewClientName('');
      setNewClientAddress('');
      setNewClientPhone('');
      setNewClientEmail('');
      setNewClientNotes('');
      setNewClientDupes(null);
      // Expand MAIN cabinet
      setExpandedCabinets(e => ({ ...e, '1MAIN FILE CABINET': true }));
    } catch (e) {
      alert('Error creating client: ' + (e.response?.data?.detail || e.message));
    }
    setNewClientSaving(false);
  };

  const toggleCabinet = async (name, cab) => {
    const nowExpanding = !expandedCabinets[name];
    setExpandedCabinets(e => ({ ...e, [name]: nowExpanding }));
    const SPECIAL_CABINETS = ['1MAIN FILE CABINET','2OLD Regular File','3OLDCommercial File','4 NEW VUL'];
    if (nowExpanding && cab && cab.clients && cab.clients.length === 1 && !SPECIAL_CABINETS.includes(cab.cabinet)) {
      await selectClient(cab.clients[0]);
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateRows: 'minmax(54px, auto) 1fr 28px', gridTemplateColumns: `${leftWidth}px 6px 1fr 6px ${rightWidth}px`, height: '100vh', background: bg, color: textPrimary }}>

      {/* TOPBAR (5.34.0: data-today-anchor = the Today panel sits just below it, fax strip included) */}
      <div data-today-anchor="" style={{ gridColumn: '1 / 6', background: panelBg, borderBottom: `1px solid ${borderColor}`, display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 12, rowGap: 10, padding: '6px 16px', minHeight: 54, boxSizing: 'border-box' }}>
        <div onClick={() => { if (!confirmLeaveClient()) return; deselectClient(); setSearchQuery(""); setSearchResults(null); }} style={{ fontSize: 20, fontWeight: 700, color: "#185FA5", paddingRight: 14, borderRight: `1px solid ${borderColor}`, whiteSpace: "nowrap", cursor: "pointer" }}>InsureDocs <span style={{ fontSize: 11, fontWeight: 400, color: '#888' }}>v{process.env.REACT_APP_VERSION || 'UNSET'}</span></div>

        {/* Telecom nav dropdown (Dash / SMS / Fax / Authentication) + quick-codes panel */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 6 }}>
          <button onClick={() => setTelecomMenuOpen((o) => { const n = !o; if (n) ensureAuthAccess(); return n; })}
            title="Telecom — Dash, SMS, Fax, Authentication"
            style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#0d9488', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            <span>💬 Telecom ▾</span>
            {telecomUnread.fax > 0 && (
              <span style={{ background: '#dc2626', color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 10, padding: '1px 7px', minWidth: 18, textAlign: 'center', display: 'inline-flex', alignItems: 'center', gap: 3 }} title={`${telecomUnread.fax} unread fax${telecomUnread.fax === 1 ? '' : 'es'}`}>
                📠 {telecomUnread.fax}
              </span>
            )}
            {telecomUnread.sms > 0 && (
              <span style={{ background: '#f59e0b', color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 10, padding: '1px 7px', minWidth: 18, textAlign: 'center', display: 'inline-flex', alignItems: 'center', gap: 3 }} title={`${telecomUnread.sms} unread SMS`}>
                💬 {telecomUnread.sms}
              </span>
            )}
          </button>

          {/* Standalone quick-codes icon (only once access is confirmed) */}
          {authCodesAllowed && (
            <button onClick={() => setCodesPanelOpen(true)}
              title="Quick codes — live authentication codes"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 6, fontSize: 15, cursor: 'pointer' }}>
              🔑
            </button>
          )}

          {telecomMenuOpen && (
            <>
              {/* click-away catcher */}
              <div onClick={() => setTelecomMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: Z_TOPBAR_MENU_AWAY }} />
              <div data-testid="telecom-menu" style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: Z_TOPBAR_MENU, background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', minWidth: 188, padding: 4 }}>
                {[
                  { label: '📊  Dash', url: '/telecom/' },
                  { label: '💬  SMS', url: '/telecom/sms' },
                  { label: '📠  Fax', url: '/telecom/faxes' },
                  { label: '🔑  Authentication', url: '/telecom/auth' },
                ].map((item) => (
                  <button key={item.url}
                    onClick={() => { window.open(item.url, '_blank'); setTelecomMenuOpen(false); }}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', borderRadius: 6, fontSize: 13, color: textPrimary, cursor: 'pointer' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(125,125,125,0.15)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}>
                    {item.label}
                  </button>
                ))}
                {authCodesAllowed && (
                  <>
                    <div style={{ height: 1, background: borderColor, margin: '4px 6px' }} />
                    <button onClick={() => { setCodesPanelOpen(true); setTelecomMenuOpen(false); }}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', borderRadius: 6, fontSize: 13, color: textPrimary, cursor: 'pointer' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(125,125,125,0.15)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}>
                      ⚡  Quick codes
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* 5.32.0: Calendar + Tasks — opens its own tab (CalendarApp, /?calendar=1). Same shape as Telecom; violet. */}
        <button onClick={() => window.open('/?calendar=1', 'insuredocs-calendar')} data-testid="top-calendar"
          title="Calendar — your calendar, Office and Tasks"
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <span>📅 Calendar</span>
        </button>

        {/* Slide-in quick-codes panel (polls only while open) */}
        {codesPanelOpen && (
          <>
            <div onClick={() => setCodesPanelOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: Z_TOPBAR_MENU_AWAY }} />
            <div style={{ position: 'fixed', top: 0, right: 0, height: '100vh', width: 360, maxWidth: '90vw', background: panelBg, borderLeft: `1px solid ${borderColor}`, boxShadow: '-8px 0 24px rgba(0,0,0,0.18)', zIndex: Z_TOPBAR_MENU, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: `1px solid ${borderColor}` }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: textPrimary }}>🔑 Live authentication codes</div>
                <button onClick={() => setCodesPanelOpen(false)} title="Close"
                  style={{ background: 'none', border: 'none', fontSize: 18, lineHeight: 1, cursor: 'pointer', color: textPrimary }}>✕</button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {authCodes.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#888', fontSize: 13, padding: '32px 8px' }}>
                    No active codes. New codes appear within a minute of arriving.
                  </div>
                ) : authCodes.map((c) => (
                  <div key={c.id} style={{ border: `1px solid ${borderColor}`, borderRadius: 8, padding: 12, background: bg }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        {c.company && (
                          <div style={{ fontSize: 13, fontWeight: 600, color: textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={c.company}>{c.company}</div>
                        )}
                        <div style={{ fontSize: 12, fontFamily: 'monospace', color: '#888' }}>{c.sender}</div>
                      </div>
                      <button onClick={() => dismissAuthCode(c.id)} title="Dismiss — clear this code"
                        style={{ flexShrink: 0, fontSize: 12, color: '#888', background: 'none', border: 'none', cursor: 'pointer' }}>✓ Done</button>
                    </div>
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                      {c.code ? (
                        <>
                          <span style={{ fontSize: 26, fontWeight: 700, fontFamily: 'monospace', letterSpacing: 2, color: textPrimary, userSelect: 'all' }}>{c.code}</span>
                          <button onClick={() => copyAuthCodeUI(c)} title="Copy code"
                            style={{ marginLeft: 'auto', fontSize: 12, padding: '3px 9px', borderRadius: 6, border: `1px solid ${borderColor}`, background: panelBg, color: textPrimary, cursor: 'pointer' }}>
                            {copiedCodeId === c.id ? 'Copied' : 'Copy'}
                          </button>
                        </>
                      ) : (
                        <span style={{ fontSize: 13, fontStyle: 'italic', color: '#999' }}>(no code extracted)</span>
                      )}
                    </div>
                    <div style={{ marginTop: 8, fontSize: 11, color: '#999' }} title={c.received_at}>{authCodeAge(c.received_at)}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <div style={{ flex: '1 1 360px', display: 'flex', gap: 6, maxWidth: 520, minWidth: 360 }}>
          <select value={searchMode} onChange={e => setSearchMode(e.target.value)}
            style={{ padding: '6px 10px', fontSize: 13, border: `1px solid ${borderColor}`, borderRadius: 6, background: panelBg, color: textPrimary }}>
            <option value="all">All words</option>
            <option value="any">Any words</option>
            <option value="addr">Address</option>
            <option value="folder">Folders</option>
          </select>
          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && runSearch()}
            placeholder="Search clients…"
            style={{ flex: 1, minWidth: 0, padding: '6px 12px', fontSize: 14, border: `1px solid ${borderColor}`, borderRadius: 6, background: panelBg, color: textPrimary }} />
          <button onClick={runSearch} style={{ padding: '6px 16px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Search</button>
          {searchResults && <button onClick={() => setSearchResults(null)} style={{ padding: '6px 12px', border: `1px solid ${borderColor}`, borderRadius: 6, fontSize: 13, background: panelBg, color: textSecondary, cursor: 'pointer' }}>Clear</button>}
        </div>
        <div style={{ flex: '1 1 0' }} />
        {/* ROW 1 right: clock · text size · Light/Dark · user menu (5.23.0: the app buttons moved to row 2) */}
        <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', columnGap: 12 }}>
          <div style={{ textAlign: 'right', minWidth: 230, whiteSpace: 'nowrap' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: textPrimary, fontVariantNumeric: 'tabular-nums' }}>{formatClock(clock)}</div>
          </div>
          <div style={{ width: 1, height: 30, background: borderColor }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: textSecondary }}>A</span>
            <input type="range" min="12" max="32" step="2" value={fontSize} onChange={e => setFontSize(Number(e.target.value))} style={{ width: 60, cursor: 'pointer' }} />
            <span style={{ fontSize: 15, color: textSecondary }}>A</span>
          </div>
          <button onClick={() => setDarkMode(!darkMode)} title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'} style={{ padding: '6px 12px', border: `1px solid ${borderColor}`, borderRadius: 6, fontSize: 13, background: panelBg, color: textSecondary, cursor: 'pointer' }}>
            {darkMode ? '☀️ Light' : '🌙 Dark'}
          </button>
          <div style={{ width: 1, height: 30, background: borderColor }} />
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowUserMenu(v => !v)}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', background: 'transparent', border: `1px solid ${showUserMenu ? '#185FA5' : 'transparent'}`, borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}
              title="Account options">
              <span style={{ fontSize: 15, fontWeight: 600, color: textPrimary }}>{user?.full_name}</span>
              <span style={{ fontSize: 11, color: textSecondary, textTransform: 'capitalize' }}>{user?.role}</span>
            </button>
            {showUserMenu && (
              <>
                <div onClick={() => setShowUserMenu(false)}
                  style={{ position: 'fixed', inset: 0, zIndex: Z_TOPBAR_MENU_AWAY }} />
                <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, minWidth: 200, background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: Z_TOPBAR_MENU, padding: 6 }}>
                  <button onClick={() => { setShowUserMenu(false); setShowChangePassword(true); }}
                    style={{ width: '100%', textAlign: 'left', padding: '8px 12px', background: 'transparent', border: 'none', borderRadius: 5, fontSize: 13, color: textPrimary, cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = darkMode ? '#333' : '#f0f0f0'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    Change password
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* 5.23.0: ROW 2 — [+ New ▾] Renewals · Bonus · ACORD · Books · HealthSherpa … [Admin ▾] InsureIQ · Logout,
            all one width (TopBarActions.jsx). Menus: TopMenu.jsx. Visibility rules unchanged. */}
        <TopBarActions
          user={user} darkMode={darkMode} theme={{ panelBg, borderColor, textPrimary }}
          backupSummary={backupSummary} showInsureIQ={showInsureIQ}
          showFaq={showFaq} faqOpenConflicts={faqOpenConflicts}
          onNewClient={openNewClientModal}
          onNewCabinet={() => setShowNewCabinet(true)}
          onNewSignature={() => openSignFlow(null, selectedClient?.id)}
          onSignatures={() => setShowSignedPanel(true)}
          onUsers={() => { setShowUserMgmt(true); loadUsers(); }}
          onBackups={() => setShowBackupModal(true)}
          onTrash={() => { setShowTrash(true); loadTrash(); }}
          onRenewals={() => setShowRenewals(true)}
          onBonus={() => setShowBonuses(true)}
          onAcord={() => window.open('/acord/', '_blank')}
          onBooks={() => window.open('/books/', '_blank')}
          onHealthSherpa={() => window.open('/?healthsherpa=1', 'insuredocs-healthsherpa')}
          onFaq={() => setShowFaq(true)}
          onInsureIQ={() => setShowInsureIQ(prev => !prev)}
          onLogout={logout}
        />

        {/* Fax failure warning (5.12.0) — full-width strip at the bottom of the TOPBAR; renders nothing with 0 alerts */}
        <FaxAlertStrip />
        {/* 5.44.0: "HealthSherpa in use" — banner for others, countdown dropdown for the holder; nothing while free */}
        <div style={{ flex: '0 0 calc(100% + 32px)', margin: '0 -16px' }} data-testid="hslock-slot"><HsLockStrip /></div>
        {/* 5.50.0: the Today card — own Personal events today + own tasks due today + reminders as they come due, draggable,
            bottom-right by default (replaces the 5.34.0 red Today panel and the 5.32.0 reminder pop-ups on this page) */}
        <TodayCard onOpenCalendar={() => window.open('/?calendar=1', 'insuredocs-calendar')}
          onOpenClient={(id) => channelHandlerRef.current && channelHandlerRef.current({ type: 'open-client', clientId: id })} />
      </div>

      {/* LEFT PANEL */}
      <div style={{ background: panelBg, borderRight: `1px solid ${borderColor}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', fontSize: fontSize }}>
        <div style={{ padding: '8px 10px', borderBottom: `1px solid ${borderColor}` }}>
          <input value={treeSearch} onChange={e => setTreeSearch(e.target.value)}
            placeholder="Filter clients..."
            style={{ width: '100%', padding: '5px 8px', border: `1px solid ${borderColor}`, borderRadius: 6, background: bg, color: textPrimary, boxSizing: 'border-box', fontSize: 'inherit' }} />
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {searchResults ? (
            <div>
              <div style={{ padding: '6px 10px', fontSize: '0.75em', color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${borderColor}` }}>
                {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
              </div>
              {searchTruncated && (
                <div style={{ padding: '4px 10px', fontSize: '0.7em', color: '#e67e22' }}>
                  Showing first 200 — refine your search
                </div>
              )}
              {searchResults.map(c => (
                <div key={c.id} onClick={() => selectClient(c)}
                  style={{ padding: '7px 10px', cursor: 'pointer', borderBottom: `1px solid ${borderColor}`,
                    background: selectedClient?.id === c.id ? (darkMode ? '#0d1f2d' : '#E6F1FB') : 'transparent',
                    borderLeft: selectedClient?.id === c.id ? '3px solid #185FA5' : '3px solid transparent' }}>
                  <div style={{ fontWeight: 500, color: textPrimary, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {getFlagIcon(c) && (
                      <span title={c.flag_note || (c.flag ? FLAG_COLORS[c.flag]?.desc : 'See Harry — X marked file')}
                        style={{ flexShrink: 0 }}>{getFlagIcon(c)}</span>
                    )}
                    <span>{c.name}</span>
                    {user?.role === 'admin' && !/^\d{4,5} /.test(c.name) && (
                      <span onClick={async e => { e.stopPropagation(); setRenumberClient(c); const r = await clients.nextNumber(); setRenumberNum(String(r.data.next_number)); }}
                        title="Assign conforming file number"
                        style={{ fontSize: 11, background: '#e67e22', color: '#fff', padding: '2px 6px', borderRadius: 4, cursor: 'pointer', fontWeight: 700, flexShrink: 0 }}>#</span>
                    )}
                    {user?.role === 'admin' && (
                      <span onClick={e => { e.stopPropagation(); setFlagClient(c); setFlagValue(c.flag || ''); setFlagNote(c.flag_note || ''); }}
                        title="Set flag"
                        style={{ fontSize: 11, background: c.flag ? FLAG_COLORS[c.flag]?.bg : '#555', color: '#fff', padding: '2px 6px', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}>
                        🚩
                      </span>
                    )}
                    {user?.role === 'admin' && (
                      <span onClick={e => { e.stopPropagation(); setDeleteClientTarget(c); setDeleteClientTyped(''); }}
                        title="Delete client"
                        style={{ fontSize: 11, background: '#c0392b', color: '#fff', padding: '2px 6px', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}>
                        🗑
                      </span>
                    )}
                  </div>
                  <div style={{ color: textSecondary, fontSize: '0.8em' }}>{c.phone1 || ''}</div>
                  {c.matched_on && (
                    <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>
                      ↳ matched in {c.matched_on.field}: {highlightMatch(c.matched_on.value, searchHL)}
                    </div>
                  )}
                  {c.matched_folders && c.matched_folders.length > 0 && (
                    <div style={{ marginTop: 3 }}>
                      {c.matched_folders.map(mf => (
                        <div key={mf.id} onClick={e => { e.stopPropagation(); selectClient(c, mf.id); }}
                          style={{ fontSize: 11, color: '#5BA3D9', cursor: 'pointer', padding: '2px 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ opacity: 0.6 }}>📁</span> {mf.name}
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedClient?.id === c.id && (
                    <div>
                      {renderFolderTree(folderTree)}
                      <div style={{ padding: '4px 0 4px 8px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, padding: '4px 0' }}>
                          {QUICK_FOLDERS.map(qf => (
                            <button key={qf} onClick={e => { e.stopPropagation(); createFolder(qf); }}
                              style={{ padding: '2px 7px', fontSize: '0.7em', border: `1px solid ${borderColor}`, borderRadius: 10, background: panelBg, color: textSecondary, cursor: 'pointer' }}>+{qf}</button>
                          ))}
                          <button onClick={e => { e.stopPropagation(); setShowNewFolder(true); }}
                            style={{ padding: '2px 7px', fontSize: '0.7em', border: '1px solid #185FA5', borderRadius: 10, background: panelBg, color: '#185FA5', cursor: 'pointer' }}>+ Custom</button>
                        </div>
                        {showNewFolder && (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <input value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && createFolder(newFolderName)}
                              placeholder="Folder name" autoFocus
                              style={{ flex: 1, padding: '3px 6px', fontSize: '0.75em', border: `1px solid ${borderColor}`, borderRadius: 4, background: bg, color: textPrimary }} />
                            <button onClick={() => createFolder(newFolderName)}
                              style={{ padding: '3px 6px', fontSize: '0.75em', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>+</button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            cabinets.map(cab => {
              const isExp = expandedCabinets[cab.cabinet];
              const filtered = treeSearch
                ? cab.clients.filter(c => textMatches(c.name, treeSearch))   // 5.27.3 apostrophes ignored
                : cab.clients;
              if (treeSearch && filtered.length === 0) return null;
              return (
                <div key={cab.cabinet}>
                  <div onClick={() => toggleCabinet(cab.cabinet, cab)}
                    style={{ padding: '8px 10px', cursor: 'pointer', background: cabBg, borderBottom: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#185FA5', position: 'sticky', top: 0, zIndex: 1 }}>
                    <span>{isExp ? '▼' : '▶'}</span>
                    <span style={{ flex: 1 }}>{cab.cabinet}</span>
                    {cab.restricted && <span style={{ fontSize: '0.7em', background: '#854F0B', color: '#FAC775', padding: '1px 6px', borderRadius: 4, marginRight: 4 }}>Admin</span>}
                    <span style={{ fontSize: '0.75em', color: textSecondary, fontWeight: 400 }}>{filtered.length}</span>
                    {user?.role === 'admin' && cab.clients.length === 1 && 
                     !['1MAIN FILE CABINET','2OLD Regular File','3OLDCommercial File','4 NEW VUL',
                       "5Harry Dao's Folder","5Henry Dao's Folder",'9Harry Dao Inc','9SOLUTIONS INSURANCE AGENCY INC'].includes(cab.cabinet) && (
                      <span onClick={e => { e.stopPropagation(); setDeleteCabinetTarget({ id: cab.clients[0].id, cabinet: cab.cabinet }); setDeleteCabinetConfirm1(false); setDeleteCabinetTyped(''); }}
                        style={{ fontSize: '0.7em', background: '#c0392b', color: '#fff', padding: '1px 6px', borderRadius: 4, marginLeft: 4, cursor: 'pointer' }}>🗑</span>
                    )}
                  </div>
                  {isExp && filtered.map(c => (
                    <div key={c.id}>
                      <div onContextMenu={e => openFolderMenu(e, c)} onClick={() => {
                        if (selectedClient?.id === c.id) {
                          if (confirmLeaveClient()) deselectClient();
                        } else {
                          selectClient(c);
                        }
                      }}
                        style={{
                          padding: '5px 10px 5px 20px', cursor: 'pointer',
                          background: selectedClient?.id === c.id ? (darkMode ? '#0d1f2d' : '#E6F1FB') : 'transparent',
                          borderLeft: selectedClient?.id === c.id ? '3px solid #185FA5' : '3px solid transparent',
                          color: selectedClient?.id === c.id ? '#0C447C' : textPrimary,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                      {['1MAIN FILE CABINET','2OLD Regular File','3OLDCommercial File','4 NEW VUL'].includes(cab.cabinet) && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, overflow: 'hidden' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, overflow: 'hidden' }}>
                            {getFlagIcon(c) && <span style={{ flexShrink: 0 }} title={c.flag_note || ''}>{getFlagIcon(c)}</span>}
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                          </span>
                          {user?.role === 'admin' && !/^\d{4,5} /.test(c.name) && (
                            <span onClick={async e => { e.stopPropagation(); setRenumberClient(c); const r = await clients.nextNumber(); setRenumberNum(String(r.data.next_number)); }}
                              title="Assign conforming file number"
                              style={{ fontSize: 11, background: '#e67e22', color: '#fff', padding: '2px 6px', borderRadius: 4, cursor: 'pointer', flexShrink: 0, fontWeight: 700 }}>
                              #
                            </span>
                          )}
                          {user?.role === 'admin' && (
                            <span onClick={e => { e.stopPropagation(); setFlagClient(c); setFlagValue(c.flag || ''); setFlagNote(c.flag_note || ''); }}
                              title="Set flag"
                              style={{ fontSize: 11, background: c.flag ? FLAG_COLORS[c.flag]?.bg : 'transparent', color: c.flag ? '#fff' : '#666', padding: '2px 4px', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}>
                              🚩
                            </span>
                          )}
                        </span>
                      )}
                      </div>
                      {selectedClient?.id === c.id && (
                        <div style={{ paddingLeft: 8, background: panelBg, color: textPrimary }}>
                          {renderFolderTree(folderTreeCache[c.id] || folderTree)}
                          <div style={{ padding: '4px 10px 8px 16px' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                              {QUICK_FOLDERS.map(qf => (
                                <button key={qf} onClick={() => createFolder(qf)}
                                  style={{ padding: '2px 7px', fontSize: '0.7em', border: `1px solid ${borderColor}`, borderRadius: 10, background: panelBg, color: textSecondary, cursor: 'pointer' }}>+{qf}</button>
                              ))}
                              <button onClick={() => setShowNewFolder(true)}
                                style={{ padding: '2px 7px', fontSize: '0.7em', border: '1px solid #185FA5', borderRadius: 10, background: panelBg, color: '#185FA5', cursor: 'pointer' }}>+ Custom</button>
                            </div>
                            {showNewFolder && (
                              <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                                <input value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                                  onKeyDown={e => e.key === 'Enter' && createFolder(newFolderName)}
                                  placeholder="Folder name" autoFocus
                                  style={{ flex: 1, padding: '3px 6px', fontSize: '0.75em', border: `1px solid ${borderColor}`, borderRadius: 4, background: bg, color: textPrimary }} />
                                <button onClick={() => createFolder(newFolderName)}
                                  style={{ padding: '3px 6px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>+</button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* LEFT-CENTER DIVIDER */}
      <div onMouseDown={e => startPanelResize('left', e)}
        style={{ background: borderColor, cursor: 'col-resize', gridRow: '2', gridColumn: '2', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s' }}
        onMouseEnter={e => e.currentTarget.style.background = '#185FA5'}
        onMouseLeave={e => e.currentTarget.style.background = borderColor}>
        <div style={{ width: 2, height: 30, background: '#555', borderRadius: 1 }} />
      </div>

      {/* CENTER PANEL */}
      <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', background: bg, minHeight: 0 }}>
        <div style={{ background: panelBg, borderBottom: `1px solid ${borderColor}`, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
          {selectedClient && bonusReturnContext && (
            <button onClick={() => setShowBonuses(true)}
              title="Return to where you were in Bonuses"
              style={{ marginRight: 6, padding: '5px 10px', fontSize: 12, border: '1px solid #185FA5', borderRadius: 6, cursor: 'pointer', background: panelBg, color: '#185FA5', fontWeight: 600, flexShrink: 0 }}>
              ← Back to Bonuses
            </button>
          )}
          <div style={{ flex: 1, fontSize: 13, color: textPrimary, display: 'flex', alignItems: 'center', gap: 6 }}>
            {selectedFolder && (
              <span onClick={() => {
                if (!selectedFolder.parent_id) {
                  setSelectedFolder(null);
                  setFileList([]);
                } else {
                  // Find parent folder anywhere in tree
                  const findInTree = (folders) => {
                    for (const f of folders) {
                      if (f.id === selectedFolder.parent_id) return f;
                      if (f.children?.length) { const r = findInTree(f.children); if (r) return r; }
                    }
                    return null;
                  };
                  const parent = findInTree(folderTree);
                  if (parent) selectFolder(parent);
                  else setSelectedFolder(null);
                }
              }}
              title="Go up one folder"
              style={{ cursor: 'pointer', color: '#185FA5', fontSize: 16, fontWeight: 700, padding: '2px 6px', borderRadius: 4, border: `1px solid ${borderColor}`, lineHeight: 1.2, flexShrink: 0 }}>
                ↑
              </span>
            )}
            {selectedClient ? (() => {
              // Build full breadcrumb chain
              const crumbs = [];
              if (selectedFolder) {
                let current = selectedFolder;
                while (current) {
                  crumbs.unshift(current);
                  if (current.parent_id) {
                    const findInTree = (folders) => {
                      for (const f of folders) {
                        if (f.id === current.parent_id) return f;
                        if (f.children?.length) { const r = findInTree(f.children); if (r) return r; }
                      }
                      return null;
                    };
                    current = findInTree(folderTree);
                  } else {
                    current = null;
                  }
                }
              }
              return <>
                <span onClick={() => { setSelectedFolder(null); setFileList([]); files.list(selectedClient.id).then(r => setFileList(r.data.files)); }}
                  style={{ cursor: 'pointer', fontWeight: 700 }} title="Go to client root">
                  {selectedClient.name}
                </span>
                {crumbs.map((c, i) => (
                  <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <span style={{ margin: '0 4px', color: textSecondary, fontSize: 11 }}>/</span>
                    <span onClick={() => { if (c.id !== selectedFolder?.id) selectFolder(c); }}
                      style={{ cursor: c.id !== selectedFolder?.id ? 'pointer' : 'default', color: c.id === selectedFolder?.id ? textPrimary : '#5BA3D9', fontWeight: c.id === selectedFolder?.id ? 600 : 400, fontSize: 12 }}>
                      {c.name}
                    </span>
                  </span>
                ))}
                {!showNewFolder && (
                  <span onClick={() => { setShowNewFolder(true); setNewFolderName(''); }}
                    style={{ marginLeft: 8, padding: '2px 8px', fontSize: 11, border: `1px solid ${borderColor}`, borderRadius: 5, cursor: 'pointer', background: panelBg, color: '#5BA3D9', fontWeight: 600 }}>
                    + Folder
                  </span>
                )}
                {selectedFolder && (
                  <span onClick={() => { setShowNotesModal(true); setNotesName(''); setNotesBody(''); }}
                    style={{ marginLeft: 8, padding: '2px 8px', fontSize: 11, border: `1px solid ${borderColor}`, borderRadius: 5, cursor: 'pointer', background: panelBg, color: '#27AE60', fontWeight: 600 }}>
                    + Notes
                  </span>
                )}
{showNewFolder && (
                  <span style={{ marginLeft: 8, display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                    <input value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') createFolder(newFolderName); if (e.key === 'Escape') setShowNewFolder(false); }}
                      autoFocus placeholder="New folder name..."
                      style={{ padding: '3px 6px', fontSize: 11, border: `1px solid ${borderColor}`, borderRadius: 4, background: panelBg, color: textPrimary, width: 150 }} />
                    <button onClick={() => createFolder(newFolderName)}
                      style={{ padding: '3px 6px', fontSize: 10, background: '#185FA5', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Create</button>
                    <button onClick={() => setShowNewFolder(false)}
                      style={{ padding: '3px 6px', fontSize: 10, background: panelBg, color: textSecondary, border: `1px solid ${borderColor}`, borderRadius: 4, cursor: 'pointer' }}>✕</button>
                  </span>
                )}
              </>;
            })() : 'Select a client'}
          </div>
          {selectedClient && !selectedFolder && (
            <button onClick={() => {
              // v3.3: bonus is anchored to the client folder itself, no subfolder context
              setAddBonusForFolder({ client: selectedClient, sourceFolderPath: selectedClient.name });
            }}
              style={{ marginRight: 8, padding: '6px 12px', fontSize: 12, border: '1px solid #27AE60', borderRadius: 6, cursor: 'pointer', background: panelBg, color: '#27AE60', fontWeight: 600 }}>
              + Bonus
            </button>
          )}
          {selectedClient && !selectedFolder && (
            <button onClick={() => setAddRenewalForClient(selectedClient)}
              style={{ marginRight: 8, padding: '6px 12px', fontSize: 12, border: '1px solid #185FA5', borderRadius: 6, cursor: 'pointer', background: panelBg, color: '#185FA5', fontWeight: 600 }}>
              + Renewal
            </button>
          )}
          {selectedClient && !selectedFolder && (
            <button onClick={() => setAddAcordForClient(selectedClient)}
              title="Add/link this client to ACORD"
              style={{ marginRight: 8, padding: '6px 12px', fontSize: 12, border: '1px solid #0f766e', borderRadius: 6, cursor: 'pointer', background: panelBg, color: '#0f766e', fontWeight: 600 }}>
              + ACORD
            </button>
          )}
          {selectedClient && user?.role === 'admin' && (
            <button onClick={() => setHistoryClient(selectedClient)}
              title="View this client's bonus history (admin only)"
              style={{ marginRight: 8, padding: '6px 10px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 6, cursor: 'pointer', background: panelBg, color: textPrimary, fontWeight: 600 }}>
              📜 Bonus History
            </button>
          )}
          {selectedClient && canFollowup && (
            <button onClick={() => setFollowupFor(selectedClient)} data-testid="client-followup-btn"
              title="Add a follow-up appointment or task for this client (always the client, even from a subfolder)"
              style={{ marginRight: 8, padding: '6px 10px', fontSize: 12, border: '1px solid #7c3aed', borderRadius: 6, cursor: 'pointer', background: panelBg, color: '#7c3aed', fontWeight: 600 }}>
              📅 Follow-up
            </button>
          )}
          <div style={{ display: 'flex', border: `1px solid ${borderColor}`, borderRadius: 6, overflow: 'hidden' }}>
            {['list', 'grid'].map(v => (
              <button key={v} onClick={() => setView(v)}
                style={{ padding: '4px 10px', fontSize: 11, border: 'none', cursor: 'pointer', background: view === v ? '#185FA5' : panelBg, color: view === v ? '#fff' : textSecondary }}>
                {v === 'list' ? '☰ List' : '⊞ Grid'}
              </button>
            ))}
          </div>

          {selCount > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 10, flexWrap: 'wrap' }}>
              <button onClick={bulkDownload} disabled={bulkBusy} style={ACTION_BTN}>↓ Download ({selCount})</button>
              <button onClick={() => { setMoveTargets(checkedFileObjs); setMoveModalOpen(true); }}
                disabled={checkedFileObjs.length === 0}
                title={checkedFileObjs.length === 0 ? 'Check files (folders excluded) to move' : ''}
                style={{ ...ACTION_BTN, opacity: checkedFileObjs.length === 0 ? 0.4 : 1 }}>⇄ Move ({checkedFileObjs.length})</button>
              {user?.role === 'admin' && (
                <button onClick={bulkDelete} disabled={bulkBusy} style={{ ...ACTION_BTN, color: '#ff4757', borderColor: '#ff4757' }}>🗑 Delete ({selCount})</button>
              )}
              <button onClick={clearSelection} style={ACTION_BTN}>Clear</button>
              {selNote && <span style={{ fontSize: 11, color: '#c0392b' }}>{selNote}</span>}
            </div>
          )}

        </div>

        {selectedClient && (
          <div onDragOver={e => e.preventDefault()} onDrop={handleDrop}
            onClick={() => selectedFolder && setShowUploadModal(true)}
            style={{ margin: 8, padding: '8px 14px', border: `1.5px dashed ${borderColor}`, borderRadius: 8, textAlign: 'center', fontSize: 12, color: textSecondary, background: panelBg, cursor: selectedFolder ? 'pointer' : 'default' }}>
            {uploading ? 'Uploading...' : selectedFolder ? '↓ Drop files here or click to upload' : 'Select a folder first, then upload'}
          </div>
        )}
        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={e => handleUpload(Array.from(e.target.files))} />

        <div ref={filesScrollRef} style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', padding: '0 8px 8px' }}>
          {/* 5.38.0 the client's Follow-ups panel + log (main folder only) */}
          {selectedClient && !selectedFolder && (
            <ClientFollowups key={selectedClient.id} client={selectedClient} me={user?.username} reloadKey={followupReload}
              theme={{ panelBg, borderColor, textPrimary, textSecondary }} />
          )}
          {/* FOLDERS IN CENTER PANEL */}
          <CenterFolders
            folderTree={folderTree} folderDescWidth={folderDescWidth} startFolderDescResize={startFolderDescResize} folderDateWidth={folderDateWidth} startFolderDateResize={startFolderDateResize} folderSortCol={folderSortCol} setFolderSortCol={setFolderSortCol} folderSortDir={folderSortDir} setFolderSortDir={setFolderSortDir} folderResizing={folderResizing}
            onSaveFolderDesc={saveFolderDescription}
            bg={bg}
            selectedFolder={selectedFolder}
            selectFolder={selectFolder}
            dragOverFolder={dragOverFolder}
            setDragOverFolder={setDragOverFolder}
            draggingFile={draggingFile}
            moveFile={moveFile}
            borderColor={borderColor}
            panelBg={panelBg}
            textPrimary={textPrimary}
            darkMode={darkMode}
            fileList={fileList}
            selectedClient={selectedClient}
            view={view}
            user={user}
            selFolderIds={selFolderIds}
            toggleFolder={toggleFolder}
            selCount={selCount}
            CAP={CAP}
            onDeleteFolder={f => setDeletingFolder(f)}
            onMoveFolder={openMoveFolder}
            onFolderMenu={e => openFolderMenu(e, selectedClient)}
          />
          {view === 'grid' ? (
            <div ref={gridInnerRef} style={{ position: 'relative', width: '100%', height: gridVirtualizer.getTotalSize() }}>
              {gridVirtualizer.getVirtualItems().map(vRow => {
                const start = vRow.index * gridCols;
                const rowItems = sorted.slice(start, start + gridCols);
                return (
                  <div key={vRow.key} data-index={vRow.index} ref={gridVirtualizer.measureElement}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', transform: `translateY(${vRow.start - gridVirtualizer.options.scrollMargin}px)`, display: 'grid', gridTemplateColumns: `repeat(${gridCols}, minmax(110px, 1fr))`, gap: 8, paddingBottom: 8 }}>
                    {rowItems.map(f => {
                      const es = extStyle(f.extension);
                      return (
                        <div key={f.id} onClick={() => selectFile(f)}
                          onContextMenu={e => openMenu(e, f)}
                          style={{ position: 'relative', background: panelBg, border: `1px solid ${selectedFile?.id === f.id ? '#185FA5' : borderColor}`, borderRadius: 8, padding: 8, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                          <span style={{ position: 'absolute', top: 4, left: 4, width: 18, height: 18 }}>
                            <SelBox checked={selFileIds.has(f.id)} disabled={selCount >= CAP && !selFileIds.has(f.id)} onClick={e => toggleFile(f, e)} />
                          </span>
                          <div style={{ width: 70, height: 80, background: bg, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ padding: '2px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: es.bg, color: es.color }}>{(f.extension||'?').toUpperCase()}</span>
                          </div>
                          <div style={{ fontSize: 10, textAlign: 'center', wordBreak: 'break-all', lineHeight: 1.3, color: textPrimary }}>{f.filename}</div>
                          <div style={{ fontSize: 9, color: textSecondary }}>{formatDate(f.uploaded_at)}</div>
                          {(user?.role === 'admin' || user?.role === 'manager') && (
                            <span onClick={e => { e.stopPropagation(); openMoveFile(f); }}
                              title="Move file"
                              style={{ fontSize: 14, color: '#185FA5', cursor: 'pointer' }}>⇄</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ minWidth: 'max-content' }}>
              {fileList.length > 0 && (
                <div style={{ display: 'flex', padding: '4px 8px', fontSize: 11, color: textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `1px solid ${borderColor}`, background: panelBg, position: 'sticky', top: 0, zIndex: 2 }}>
                  <div style={{ width: CHK_W, flexShrink: 0 }} />
                  {COLS.map(({ key, label }) => (
                    <div key={key} style={{ width: colWidths[key], flexShrink: 0, display: 'flex', alignItems: 'center', position: 'relative', paddingRight: 4 }}>
                      <span onClick={() => handleSort(key)} style={{ cursor: 'pointer', color: sortCol === key ? '#185FA5' : textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', userSelect: 'none' }}>
                        {label}{sortArrow(key)}
                      </span>
                      <div onMouseDown={e => startResize(key, e)}
                        style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 8, cursor: 'col-resize', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ width: 2, height: 14, background: borderColor, borderRadius: 1 }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div ref={listInnerRef} style={{ position: 'relative', width: '100%', minWidth: rowTotalW, height: listVirtualizer.getTotalSize() }}>
              {listVirtualizer.getVirtualItems().map(vi => {
                const f = sorted[vi.index];
                const es = extStyle(f.extension);
                return (
                  <div key={f.id} onClick={() => selectFile(f)}
                    draggable
                    onDragStart={() => setDraggingFile(f)}
                    onDragEnd={() => setDraggingFile(null)}
                    onContextMenu={e => openMenu(e, f)}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', minWidth: rowTotalW, height: FILE_ROW_H, boxSizing: 'border-box', transform: `translateY(${vi.start - listVirtualizer.options.scrollMargin}px)`, display: 'flex', padding: '6px 8px', background: selectedFile?.id === f.id ? (darkMode ? '#0d1f2d' : '#E6F1FB') : panelBg, borderRadius: 4, cursor: 'grab', alignItems: 'center', borderBottom: `1px solid ${borderColor}`, opacity: draggingFile?.id === f.id ? 0.5 : 1 }}>
                    <div style={{ width: CHK_W, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                      <SelBox checked={selFileIds.has(f.id)} disabled={selCount >= CAP && !selFileIds.has(f.id)} onClick={e => toggleFile(f, e)} />
                    </div>
                    <div style={{ width: colWidths.name, flexShrink: 0, fontSize: 12, paddingRight: 8 }}>
                      {renamingFile === f.id ? (
                        <input value={renameFileValue} onChange={e => setRenameFileValue(e.target.value)}
                          onBlur={() => submitRenameFile(f)}
                          onKeyDown={e => { if (e.key === 'Enter') submitRenameFile(f); if (e.key === 'Escape') setRenamingFile(null); }}
                          autoFocus onClick={e => e.stopPropagation()}
                          style={{ width: '100%', padding: '1px 4px', fontSize: 12, border: '1px solid #185FA5', borderRadius: 3, background: bg, color: textPrimary }} />
                      ) : (
                        <span onDoubleClick={e => startRenameFile(f, e)} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', color: textPrimary }} title="Double-click to rename">{f.filename}</span>
                      )}
                    </div>
                    <div style={{ width: colWidths.desc, flexShrink: 0, fontSize: 11, color: '#4f8ef7', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8, cursor: 'text', minHeight: 18 }}
                      title={f.description || 'Click to add description'}
                      onClick={e => { if (editingDesc !== f.id) { e.stopPropagation(); setEditingDesc(f.id); setEditDescValue(f.description || ''); } }}>
                      {editingDesc === f.id ? (
                        <input autoFocus value={editDescValue}
                          onChange={e => setEditDescValue(e.target.value)}
                          onBlur={() => saveDescription(f.id, editDescValue)}
                          onKeyDown={e => { if (e.key === 'Enter') saveDescription(f.id, editDescValue); if (e.key === 'Escape') setEditingDesc(null); }}
                          onClick={e => e.stopPropagation()}
                          style={{ width: '100%', fontSize: 11, padding: '1px 4px', border: `1px solid #4f8ef7`, borderRadius: 3, background: bg, color: textPrimary }} />
                      ) : (f.description || <span style={{ color: textSecondary, opacity: 0.3 }}>+ desc</span>)}
                    </div>
                    <div style={{ width: colWidths.date, flexShrink: 0, fontSize: 11, color: textSecondary, paddingRight: 8 }}>{formatDate(f.uploaded_at)}</div>
                    <div style={{ width: colWidths.size, flexShrink: 0, fontSize: 11, color: textSecondary, paddingRight: 8 }}>{formatSize(f.file_size)}</div>
                    <div style={{ width: colWidths.by, flexShrink: 0, fontSize: 11, color: textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>{f.uploaded_by || '—'}</div>
                    <div style={{ width: colWidths.ext, flexShrink: 0, paddingRight: 8 }}>
                      <span style={{ padding: '1px 5px', borderRadius: 3, fontSize: 9, fontWeight: 600, background: es.bg, color: es.color }}>{(f.extension||'?').toUpperCase()}</span>
                    </div>
                    <div style={{ flexShrink: 0, width: FILE_ACTIONS_W, display: 'flex', gap: 8, alignItems: 'center' }}>
                      {/* Edit (PDF only) — first action, full-weight green pencil so it
                          reads as a real button next to the emoji siblings. */}
                      {(f.extension || '').toLowerCase() === 'pdf' && (
                      <span onClick={e => { e.stopPropagation(); openEditor(f); }}
                        title="Edit PDF — fill fields / mark up"
                        style={{ fontSize: 16, fontWeight: 700, lineHeight: 1, color: '#2ecc71', cursor: 'pointer', opacity: 1, padding: '0 2px' }}>✎</span>
                      )}
                      <span onClick={e => { e.stopPropagation(); openMmModal(f); }}
                        title="Send via Mattermost"
                        style={{ fontSize: 14, color: '#4a9eda', cursor: 'pointer', opacity: 1, padding: '0 2px' }}>💬</span>
                      {(f.extension || '').toLowerCase() === 'pdf' && (
                      <span onClick={e => { e.stopPropagation(); openSignFlow(f); }}
                        title="Send for signature (opens ACORD)"
                        style={{ fontSize: 14, cursor: 'pointer', opacity: 1, padding: '0 2px' }}>✍️</span>
                      )}
                      {(user?.role === 'admin' || user?.role === 'manager') && (
                      <span onClick={e => { e.stopPropagation(); openMoveFile(f); }}
                        title="Move file"
                        style={{ fontSize: 14, color: '#185FA5', cursor: 'pointer', padding: '0 2px' }}>⇄</span>
                      )}
                      {(user?.role === 'admin' || user?.role === 'manager' || f.uploaded_by_id === user?.id) && (
                      <span onClick={e => { e.stopPropagation(); deleteFile(f); }}
                        title="Delete file"
                        style={{ fontSize: 14, color: '#ff4757', cursor: 'pointer', padding: '0 2px' }}>🗑</span>
                      )}
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          )}
        </div>

        {/* INLINE PREVIEW */}
        {inlinePreviewUrl && inlinePreviewFile && (
          <div style={{ height: previewHeight, borderTop: `2px solid #185FA5`, display: 'flex', flexDirection: 'column', background: '#111' }}>
            <div onMouseDown={e => startPanelResize('preview', e)}
              style={{ height: 6, cursor: 'ns-resize', background: '#185FA5', opacity: 0.6 }} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 12px', background: '#1a1a2e' }}>
              <span style={{ fontSize: 12, color: '#aaa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inlinePreviewFile.filename}</span>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {(inlinePreviewFile.extension || '').toLowerCase() === 'pdf' && (
                  <button onClick={() => openEditor(inlinePreviewFile)}
                    style={{ padding: '3px 10px', background: '#2ecc71', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 11 }}>
                    ✎ Edit
                  </button>
                )}
                <button onClick={() => setShowPreviewModal(true) || setPreviewUrl(inlinePreviewUrl)}
                  style={{ padding: '3px 10px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 11 }}>
                  ⛶ Full screen
                </button>
                <button onClick={() => { setInlinePreviewUrl(null); setInlinePreviewFile(null); }}
                  style={{ padding: '3px 10px', background: '#333', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 11 }}>
                  ✕ Close
                </button>
              </div>
            </div>
            <iframe key={inlinePreviewReloadKey} src={inlinePreviewUrl} style={{ flex: 1, border: 'none', width: '100%' }} title="inline-preview" />
          </div>
        )}
      </div>

      {/* CENTER-RIGHT DIVIDER */}
      <div onMouseDown={e => startPanelResize('right', e)}
        style={{ background: borderColor, cursor: 'col-resize', gridRow: '2', gridColumn: '4', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s' }}
        onMouseEnter={e => e.currentTarget.style.background = '#185FA5'}
        onMouseLeave={e => e.currentTarget.style.background = borderColor}>
        <div style={{ width: 2, height: 30, background: '#555', borderRadius: 1 }} />
      </div>

      {/* RIGHT PANEL */}
      <div style={{ background: panelBg, borderLeft: `1px solid ${borderColor}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>

        {selectedFile && (
          <div style={{ padding: '6px 10px', borderBottom: `1px solid ${borderColor}`, display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            <button onClick={() => downloadFile(selectedFile)}
              style={{ padding: '3px 8px', fontSize: 11, border: `1px solid ${borderColor}`, borderRadius: 5, cursor: 'pointer', background: panelBg, color: textPrimary }}>
              ↓ Download
            </button>
            {PREVIEW_EXTS.includes(selectedFile.extension?.toLowerCase()) && (
              <button onClick={() => previewFile(selectedFile)}
                style={{ padding: '3px 8px', fontSize: 11, border: `1px solid ${borderColor}`, borderRadius: 5, cursor: 'pointer', background: panelBg, color: textPrimary }}>
                👁 Preview
              </button>
            )}
            <button onClick={() => openMmModal(selectedFile)}
              style={{ padding: '3px 8px', fontSize: 11, border: '1px solid #1d628a', borderRadius: 5, cursor: 'pointer', background: panelBg, color: '#4a9eda' }}>
              💬 Send
            </button>
          </div>
        )}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px' }}>
          {selectedFile ? (
            <>
              <div style={{ fontSize: 10, fontWeight: 600, color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Profile · File</div>
              {[
                { label: 'Document type', key: 'document_type', type: 'select', options: ['', 'Auto', 'Commercial', 'Health', 'Home', 'Investments', 'Life', 'Other', 'Renters'] },
                { label: 'Helped by', key: 'helped_by' },
                { label: 'Who pays', key: 'who_pays' },
                { label: 'Phone 1', key: 'phone1' },
                { label: 'Phone 2', key: 'phone2' },
                { label: 'Phone 3', key: 'phone3' },
                { label: 'Phone 4', key: 'phone4' },
                { label: 'Fax 1', key: 'fax1' },
                { label: 'Fax 2', key: 'fax2' },
                { label: 'Email', key: 'email' },
                { label: 'Policy number', key: 'policy_number' },
              ].map(field => (
                <div key={field.key} style={{ marginBottom: 7 }}>
                  <div style={{ fontSize: 10, color: textSecondary, marginBottom: 2 }}>{field.label}</div>
                  {field.type === 'select' ? (
                    <select value={metadata[field.key] || ''} onChange={e => setMetadata(m => ({ ...m, [field.key]: e.target.value }))}
                      style={{ width: '100%', padding: '4px 6px', fontSize: 11, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary }}>
                      {field.options.map(o => <option key={o} value={o}>{o || '— select —'}</option>)}
                    </select>
                  ) : (
                    <input value={metadata[field.key] || ''} onChange={e => setMetadata(m => ({ ...m, [field.key]: e.target.value }))}
                      style={{ width: '100%', padding: '4px 6px', fontSize: 11, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary, boxSizing: 'border-box' }} />
                  )}
                </div>
              ))}
              <div style={{ marginBottom: 7 }}>
                <div style={{ fontSize: 10, color: textSecondary, marginBottom: 2 }}>Document notes</div>
                <textarea value={metadata.notes || ''} onChange={e => setMetadata(m => ({ ...m, notes: e.target.value }))}
                  rows={5}
                  style={{ width: '100%', padding: '5px 6px', fontSize: 11, border: `1px solid ${borderColor}`, borderRadius: 5, background: darkMode ? '#1a2a1a' : '#FAFAE8', color: darkMode ? '#e0e0c0' : '#333', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5 }} />
              </div>
            </>
          ) : selectedFolder ? (
            <>
              <div style={{ fontSize: 10, fontWeight: 600, color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Profile · Folder</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#5BA3D9', marginBottom: 10, wordBreak: 'break-word' }}>{selectedFolder.name}</div>
              {[
                { label: 'Document type', key: 'document_type', type: 'select', options: ['', 'Auto', 'Commercial', 'Health', 'Home', 'Investments', 'Life', 'Other', 'Renters'] },
                { label: 'Who pays', key: 'who_pays' },
                { label: 'Phone 1', key: 'phone1' },
                { label: 'Phone 2', key: 'phone2' },
                { label: 'Phone 3', key: 'phone3' },
                { label: 'Phone 4', key: 'phone4' },
                { label: 'Fax 1', key: 'fax1' },
                { label: 'Fax 2', key: 'fax2' },
                { label: 'Email', key: 'email' },
              ].map(field => (
                <div key={field.key} style={{ marginBottom: 7 }}>
                  <div style={{ fontSize: 10, color: textSecondary, marginBottom: 2 }}>{field.label}</div>
                  {field.type === 'select' ? (
                    <select value={folderMeta[field.key] || ''} onChange={e => setFolderMeta(m => ({ ...m, [field.key]: e.target.value }))}
                      style={{ width: '100%', padding: '4px 6px', fontSize: 11, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary }}>
                      {field.options.map(o => <option key={o} value={o}>{o || '— select —'}</option>)}
                    </select>
                  ) : (
                    <input value={folderMeta[field.key] || ''} onChange={e => setFolderMeta(m => ({ ...m, [field.key]: e.target.value }))}
                      style={{ width: '100%', padding: '4px 6px', fontSize: 11, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary, boxSizing: 'border-box' }} />
                  )}
                </div>
              ))}
              <div style={{ marginBottom: 7 }}>
                <div style={{ fontSize: 10, color: textSecondary, marginBottom: 2 }}>Folder notes</div>
                <textarea value={folderMeta.notes || ''} onChange={e => setFolderMeta(m => ({ ...m, notes: e.target.value }))}
                  rows={5}
                  style={{ width: '100%', padding: '5px 6px', fontSize: 11, border: `1px solid ${borderColor}`, borderRadius: 5, background: darkMode ? '#1a2a1a' : '#FAFAE8', color: darkMode ? '#e0e0c0' : '#333', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5 }} />
              </div>
            </>
          ) : selectedClient ? (
            <>
              <div style={{ fontSize: 10, fontWeight: 600, color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Profile · Client</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#5BA3D9', marginBottom: 10, wordBreak: 'break-word' }}>{selectedClient.name}</div>
              {hsCoverage.clientId === selectedClient.id && hsCoverage.count > 0 && (
                <button onClick={() => setShowHealthCoverage(true)} title="HealthSherpa policy status (API + exports)"
                  style={{ width: '100%', marginBottom: 10, padding: '6px 8px', fontSize: 12, fontWeight: 600, textAlign: 'left', border: `1px solid ${borderColor}`, borderRadius: 6, background: bg, color: textPrimary, cursor: 'pointer' }}>
                  🩺 Health coverage <span style={{ fontWeight: 400, color: textSecondary }}>({hsCoverage.count} application{hsCoverage.count === 1 ? '' : 's'})</span>
                </button>
              )}
              {[
                { label: 'First Name', key: 'first_name' },
                { label: 'Address', key: 'address_name' },
                { label: 'Address #', key: 'address_number' },
                { label: 'Phone 1', key: 'phone1' },
                { label: 'Phone 2', key: 'phone2' },
                { label: 'Phone 3', key: 'phone3' },
                { label: 'Phone 4', key: 'phone4' },
                { label: 'Fax 1', key: 'fax1' },
                { label: 'Fax 2', key: 'fax2' },
                { label: 'Email', key: 'email' },
              ].map(field => (
                <div key={field.key} style={{ marginBottom: 7 }}>
                  <div style={{ fontSize: 10, color: textSecondary, marginBottom: 2 }}>{field.label}</div>
                  <input value={clientMeta[field.key] || ''} readOnly={profileLoadingId === selectedClient.id} onChange={e => setClientMeta(m => ({ ...m, [field.key]: e.target.value }))}
                    style={{ width: '100%', padding: '4px 6px', fontSize: 11, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary, boxSizing: 'border-box' }} />
                </div>
              ))}
              <div style={{ marginBottom: 7 }}>
                <div style={{ fontSize: 10, color: textSecondary, marginBottom: 2 }}>Client notes</div>
                <textarea value={clientMeta.notes || ''} readOnly={profileLoadingId === selectedClient.id} onChange={e => setClientMeta(m => ({ ...m, notes: e.target.value }))}
                  rows={5}
                  style={{ width: '100%', padding: '5px 6px', fontSize: 11, border: `1px solid ${borderColor}`, borderRadius: 5, background: darkMode ? '#1a2a1a' : '#FAFAE8', color: darkMode ? '#e0e0c0' : '#333', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5 }} />
              </div>

              {/* 🔒 Manager Notes — manager/admin-only, append-only, separate from the
                  all-visible Client notes above. Defensive gate only; backend require_role
                  is authoritative (staff never receive note content over the wire). */}
              {isManagerOrAdmin && (
                <div style={{ marginTop: 14, paddingTop: 12, borderTop: `2px solid ${borderColor}` }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#9B5BD9', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                    🔒 Manager Notes <span style={{ fontWeight: 400, textTransform: 'none', color: textSecondary }}>— managers &amp; admins only</span>
                  </div>
                  <div style={{ background: darkMode ? '#1d1726' : '#F6F1FB', border: `1px solid ${darkMode ? '#3a2d4d' : '#E3D6F2'}`, borderRadius: 6, padding: 8 }}>
                    {managerNotes.length === 0 ? (
                      <div style={{ fontSize: 11, color: textSecondary, fontStyle: 'italic', padding: '2px 0' }}>No manager notes yet.</div>
                    ) : (
                      managerNotes.map(n => {
                        const when = n.is_deleted ? n.deleted_at : n.created_at;
                        const stamp = when ? new Date(when).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }) : '';
                        if (n.is_deleted) {
                          return (
                            <div key={n.id} style={{ fontSize: 11, color: textSecondary, fontStyle: 'italic', padding: '5px 0', borderBottom: `1px solid ${darkMode ? '#2a2235' : '#EADFF5'}` }}>
                              🔒 note removed by {n.deleted_by_name || 'admin'} · {stamp}
                            </div>
                          );
                        }
                        return (
                          <div key={n.id} style={{ padding: '6px 0', borderBottom: `1px solid ${darkMode ? '#2a2235' : '#EADFF5'}` }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6 }}>
                              <div style={{ fontSize: 10, color: textSecondary }}>{n.author_name} · {stamp}</div>
                              {user?.role === 'admin' && (
                                <button onClick={() => deleteManagerNote(n.id)} title="Remove (tombstone)"
                                  style={{ fontSize: 10, color: '#C0392B', background: 'none', border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0 }}>Delete</button>
                              )}
                            </div>
                            <div style={{ fontSize: 12, color: textPrimary, whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 2, lineHeight: 1.45 }}>{n.note}</div>
                          </div>
                        );
                      })
                    )}
                    <div style={{ marginTop: 8 }}>
                      <textarea value={managerNoteDraft} onChange={e => { setManagerNoteDraft(e.target.value); setManagerNoteDraftFor(selectedClient.id); }} rows={3}
                        placeholder="Add a private manager note…"
                        style={{ width: '100%', padding: '5px 6px', fontSize: 11, border: `1px solid ${darkMode ? '#3a2d4d' : '#D4C2E8'}`, borderRadius: 5, background: bg, color: textPrimary, resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5 }} />
                      <button onClick={addManagerNote} disabled={managerNoteSaving || !managerNoteDraft.trim()}
                        style={{ marginTop: 4, padding: '5px 12px', background: (managerNoteSaving || !managerNoteDraft.trim()) ? '#9B8FB0' : '#7B4BB0', color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: (managerNoteSaving || !managerNoteDraft.trim()) ? 'default' : 'pointer' }}>
                        {managerNoteSaving ? 'Adding…' : 'Add note'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: 11, color: textSecondary, textAlign: 'center', marginTop: 20 }}>Select a client to view profile</div>
          )}
        </div>
        <div style={{ padding: '8px 10px', borderTop: `1px solid ${borderColor}`, display: 'flex', gap: 6 }}>
          {selectedFile ? (
            <>
              <button onClick={saveMetadata} disabled={metaSaving}
                style={{ flex: 1, padding: '7px', background: metaSaved ? '#3B6D11' : '#185FA5', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {metaSaved ? 'Saved!' : metaSaving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={() => { setSelectedFile(null); setMetadata({}); setPreviewUrl(null); }}
                style={{ padding: '7px 12px', border: `1px solid ${borderColor}`, borderRadius: 6, fontSize: 13, background: panelBg, color: textSecondary, cursor: 'pointer' }}>
                Clear
              </button>
            </>
          ) : selectedFolder ? (
            <button onClick={saveFolderMeta} disabled={folderMetaSaving}
              style={{ flex: 1, padding: '7px', background: folderMetaSaved ? '#3B6D11' : '#185FA5', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {folderMetaSaved ? 'Saved!' : folderMetaSaving ? 'Saving...' : 'Save Folder Profile'}
            </button>
          ) : selectedClient ? (
            <button onClick={saveClientMeta} disabled={clientMetaSaving || profileLoadingId === selectedClient.id}
              style={{ flex: 1, padding: '7px', background: clientMetaSaved ? '#3B6D11' : '#185FA5', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {profileLoadingId === selectedClient.id ? 'Loading…' : clientMetaSaved ? 'Saved!' : clientMetaSaving ? 'Saving...' : 'Save Client Profile'}
            </button>
          ) : null}
        </div>
      </div>

      {/* TRASH MODAL */}
      {showTrash && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: panelBg, borderRadius: 10, padding: 28, width: 700, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: textPrimary }}>🗑 Trash — Deleted Files (60-day recovery)</div>
              <button onClick={() => setShowTrash(false)}
                style={{ padding: '5px 12px', border: `1px solid ${borderColor}`, borderRadius: 6, background: panelBg, color: textSecondary, cursor: 'pointer' }}>Close</button>
            </div>
            {trashLoading ? (
              <div style={{ textAlign: 'center', padding: 40, color: textSecondary }}>Loading...</div>
            ) : trashedFiles.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: textSecondary }}>No deleted files found</div>
            ) : (
              <div style={{ overflowY: 'auto', flex: 1 }}>
                <div style={{ display: 'flex', padding: '6px 8px', fontSize: 11, color: textSecondary, fontWeight: 600, borderBottom: `1px solid ${borderColor}`, textTransform: 'uppercase' }}>
                  <div style={{ flex: 2 }}>Filename</div>
                  <div style={{ flex: 1 }}>Client</div>
                  <div style={{ flex: 1 }}>Deleted</div>
                  <div style={{ flex: 1 }}>Expires</div>
                  <div style={{ width: 80 }}>Action</div>
                </div>
                {trashedFiles.map(f => {
                  const deletedDate = new Date(f.deleted_at);
                  const expiresDate = new Date(deletedDate.getTime() + 60 * 24 * 60 * 60 * 1000);
                  const daysLeft = Math.ceil((expiresDate - new Date()) / (24 * 60 * 60 * 1000));
                  return (
                    <div key={f.id} style={{ display: 'flex', padding: '8px', borderBottom: `1px solid ${borderColor}`, alignItems: 'center' }}>
                      <div style={{ flex: 2, fontSize: 12, color: textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>{f.filename}</div>
                      <div style={{ flex: 1, fontSize: 11, color: textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.client_name}</div>
                      <div style={{ flex: 1, fontSize: 11, color: textSecondary }}>{deletedDate.toLocaleDateString()}</div>
                      <div style={{ flex: 1, fontSize: 11, color: daysLeft <= 7 ? '#e74c3c' : textSecondary }}>{daysLeft}d left</div>
                      <div style={{ width: 80 }}>
                        <button onClick={() => restoreFile(f)}
                          style={{ padding: '3px 8px', background: '#2ecc71', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>
                          Restore
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* FLAG CLIENT MODAL */}
      {flagClient && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: panelBg, borderRadius: 10, padding: 28, width: 480, boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: textPrimary, marginBottom: 6 }}>Set Client Flag</div>
            <div style={{ fontSize: 12, color: textSecondary, marginBottom: 16 }}>{flagClient.name}</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <button onClick={() => setFlagValue('')}
                style={{ padding: '8px 14px', border: `2px solid ${flagValue === '' ? '#aaa' : borderColor}`, borderRadius: 6, background: flagValue === '' ? '#555' : panelBg, color: '#fff', cursor: 'pointer', fontSize: 12 }}>
                None
              </button>
              {Object.entries(FLAG_COLORS).map(([key, val]) => (
                <button key={key} onClick={() => setFlagValue(key)}
                  style={{ padding: '8px 14px', border: `2px solid ${flagValue === key ? val.bg : borderColor}`, borderRadius: 6, background: flagValue === key ? val.bg : panelBg, color: flagValue === key ? '#fff' : textPrimary, cursor: 'pointer', fontSize: 12 }}>
                  {val.label}
                </button>
              ))}
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: textSecondary, marginBottom: 4 }}>Note (optional — shown on hover)</div>
              <input value={flagNote} onChange={e => setFlagNote(e.target.value)}
                placeholder="e.g. Pending renewal, Under review..."
                style={{ width: '100%', padding: '8px 12px', fontSize: 13, border: `1px solid ${borderColor}`, borderRadius: 6, background: bg, color: textPrimary, boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setFlagClient(null)}
                style={{ padding: '8px 20px', border: `1px solid ${borderColor}`, borderRadius: 6, background: panelBg, color: textSecondary, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={submitFlag}
                style={{ padding: '8px 20px', background: flagValue ? FLAG_COLORS[flagValue]?.bg : '#555', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                {flagValue ? `Set ${FLAG_COLORS[flagValue]?.label}` : 'Clear Flag'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RENUMBER CLIENT MODAL */}
      {renumberClient && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: panelBg, borderRadius: 10, padding: 28, width: 500, boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: textPrimary, marginBottom: 6 }}>Assign Conforming File Number</div>
            <div style={{ fontSize: 12, color: textSecondary, marginBottom: 16 }}>Current name: <strong style={{ color: '#e67e22' }}>{renumberClient.name}</strong></div>
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: textSecondary, marginBottom: 4 }}>New file number — pre-filled with next available number (you can change it)</div>
              <input value={renumberNum} onChange={e => setRenumberNum(e.target.value.replace(/\D/g, ''))}
                onKeyDown={e => e.key === 'Enter' && submitRenumber()}
                placeholder="e.g. 19958" autoFocus maxLength={5}
                style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: `1px solid ${borderColor}`, borderRadius: 6, background: bg, color: textPrimary, boxSizing: 'border-box' }} />
            </div>
            {renumberNum && (
              <div style={{ fontSize: 12, color: '#2ecc71', marginBottom: 16 }}>
                New name will be: <strong>{renumberNum} {renumberClient.name.replace(/^\S+\s*/, '')}</strong>
              </div>
            )}
            <div style={{ fontSize: 11, color: textSecondary, marginBottom: 20 }}>
              This will rename the folder on disk and update all file paths in the database.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setRenumberClient(null); setRenumberNum(''); }}
                style={{ padding: '8px 20px', border: `1px solid ${borderColor}`, borderRadius: 6, background: panelBg, color: textSecondary, cursor: 'pointer', fontSize: 13 }}>
                Cancel
              </button>
              <button onClick={submitRenumber} disabled={!renumberNum.trim() || renumberSaving}
                style={{ padding: '8px 20px', background: '#e67e22', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: renumberNum ? 1 : 0.5 }}>
                {renumberSaving ? 'Renaming...' : 'Assign Number'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE FOLDER MODAL */}
      {deletingFolder && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: panelBg, borderRadius: 10, padding: 28, width: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.5)', border: '1px solid #c0392b' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#c0392b', marginBottom: 10 }}>⚠️ Delete Folder</div>
            <div style={{ fontSize: 13, color: textPrimary, marginBottom: 8 }}>
              Delete folder <strong>"{deletingFolder.name}"</strong>?
            </div>
            <div style={{ fontSize: 12, color: textSecondary, marginBottom: 20 }}>
              All files inside will be moved to Trash (restorable). Files on disk will NOT be deleted.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setDeletingFolder(null)}
                style={{ padding: '7px 18px', border: `1px solid ${borderColor}`, borderRadius: 6, background: panelBg, color: textSecondary, cursor: 'pointer', fontSize: 13 }}>
                Cancel
              </button>
              <button onClick={() => deleteFolder(deletingFolder)}
                style={{ padding: '7px 18px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Delete Folder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CABINET MODAL */}
      {deleteCabinetTarget && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: panelBg, borderRadius: 10, padding: 32, width: 500, boxShadow: '0 8px 32px rgba(0,0,0,0.6)', border: '2px solid #c0392b' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#c0392b', marginBottom: 8 }}>⚠️ Delete Cabinet</div>
            {!deleteCabinetConfirm1 ? (
              <>
                <div style={{ fontSize: 14, color: textPrimary, marginBottom: 8 }}>
                  You are about to permanently delete:
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#c0392b', padding: '10px 14px', background: darkMode ? '#2a0a0a' : '#fff0f0', borderRadius: 6, marginBottom: 16 }}>
                  {deleteCabinetTarget.cabinet}
                </div>
                <div style={{ fontSize: 13, color: textSecondary, marginBottom: 20 }}>
                  This will move all files inside this cabinet to Trash (restorable) and remove its folders. Files on disk will NOT be deleted.
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => setDeleteCabinetTarget(null)}
                    style={{ padding: '8px 20px', border: `1px solid ${borderColor}`, borderRadius: 6, background: panelBg, color: textSecondary, cursor: 'pointer', fontSize: 13 }}>
                    Cancel
                  </button>
                  <button onClick={() => setDeleteCabinetConfirm1(true)}
                    style={{ padding: '8px 20px', background: '#e67e22', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    Yes, I want to delete this
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 14, color: textPrimary, marginBottom: 16 }}>
                  To confirm, type the cabinet name exactly:
                </div>
                <div style={{ fontSize: 12, color: textSecondary, fontFamily: 'monospace', padding: '6px 10px', background: darkMode ? '#1a1a1a' : '#f5f5f5', borderRadius: 4, marginBottom: 12 }}>
                  {deleteCabinetTarget.cabinet}
                </div>
                <input value={deleteCabinetTyped} onChange={e => setDeleteCabinetTyped(e.target.value)}
                  placeholder="Type cabinet name to confirm..."
                  style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: `2px solid ${deleteCabinetTyped === deleteCabinetTarget.cabinet ? '#2ecc71' : '#c0392b'}`, borderRadius: 6, background: bg, color: textPrimary, boxSizing: 'border-box', marginBottom: 16 }} />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => { setDeleteCabinetTarget(null); setDeleteCabinetConfirm1(false); setDeleteCabinetTyped(''); }}
                    style={{ padding: '8px 20px', border: `1px solid ${borderColor}`, borderRadius: 6, background: panelBg, color: textSecondary, cursor: 'pointer', fontSize: 13 }}>
                    Cancel
                  </button>
                  <button onClick={deleteCabinet}
                    disabled={deleteCabinetTyped !== deleteCabinetTarget.cabinet || deleteCabinetSaving}
                    style={{ padding: '8px 20px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: deleteCabinetTyped === deleteCabinetTarget.cabinet ? 1 : 0.4 }}>
                    {deleteCabinetSaving ? 'Deleting...' : '🗑 Permanently Delete'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {deleteClientTarget && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: panelBg, borderRadius: 10, padding: 32, width: 480, boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#c0392b', marginBottom: 8 }}>Delete Client</div>
            <div style={{ fontSize: 13, color: textSecondary, marginBottom: 20 }}>
              This will <strong style={{ color: textPrimary }}>permanently delete</strong> the client, all folders, and all files. This cannot be undone.
            </div>
            <div style={{ background: darkMode ? '#1a1a1a' : '#f5f5f5', borderRadius: 6, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: textPrimary }}>
              {deleteClientTarget.name}
            </div>
            <div style={{ fontSize: 12, color: textSecondary, marginBottom: 8 }}>Type the full client name to confirm:</div>
            <input value={deleteClientTyped} onChange={e => setDeleteClientTyped(e.target.value)}
              placeholder={deleteClientTarget.name}
              style={{ width: '100%', padding: '10px 12px', fontSize: 13, border: `2px solid ${deleteClientTyped === deleteClientTarget.name ? '#2ecc71' : '#c0392b'}`, borderRadius: 6, background: bg, color: textPrimary, boxSizing: 'border-box', marginBottom: 20 }} />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setDeleteClientTarget(null); setDeleteClientTyped(''); }}
                style={{ padding: '8px 20px', background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                Cancel
              </button>
              <button onClick={deleteClient}
                disabled={deleteClientTyped !== deleteClientTarget.name || deleteClientSaving}
                style={{ padding: '8px 20px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: deleteClientTyped === deleteClientTarget.name ? 1 : 0.4 }}>
                {deleteClientSaving ? 'Deleting...' : 'Permanently Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* NEW CABINET MODAL */}
      {showNewCabinet && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: panelBg, borderRadius: 10, padding: 32, width: 480, boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: textPrimary, marginBottom: 6 }}>New Cabinet</div>
            <div style={{ fontSize: 12, color: textSecondary, marginBottom: 20 }}>Creates a new top-level folder visible to all staff (or admin-only if restricted)</div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: textSecondary, marginBottom: 4 }}>Cabinet Name</div>
              <input value={newCabinetName} onChange={e => setNewCabinetName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && createNewCabinet()}
                placeholder="e.g. 5Teresa Mejia's Folder"
                autoFocus
                style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: `1px solid ${borderColor}`, borderRadius: 6, background: bg, color: textPrimary, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" id="restricted" checked={newCabinetRestricted} onChange={e => setNewCabinetRestricted(e.target.checked)} />
              <label htmlFor="restricted" style={{ fontSize: 13, color: textPrimary, cursor: 'pointer' }}>
                Admin only (restricted) — hide from staff users
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowNewCabinet(false); setNewCabinetName(''); setNewCabinetRestricted(false); }}
                style={{ padding: '8px 20px', border: `1px solid ${borderColor}`, borderRadius: 6, background: panelBg, color: textSecondary, cursor: 'pointer', fontSize: 13 }}>
                Cancel
              </button>
              <button onClick={createNewCabinet} disabled={!newCabinetName.trim() || newCabinetSaving}
                style={{ padding: '8px 20px', background: '#8e44ad', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: newCabinetName.trim() ? 1 : 0.5 }}>
                {newCabinetSaving ? 'Creating...' : 'Create Cabinet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NEW CLIENT MODAL */}
      {showNewClient && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: panelBg, borderRadius: 10, padding: 32, width: 480, boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: textPrimary, marginBottom: 8 }}>New Client</div>
            <div style={{ fontSize: 12, color: textSecondary, marginBottom: 20 }}>
              Will be created as client #{nextClientNum || '...'} in 1MAIN FILE CABINET
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: textSecondary, marginBottom: 4 }}>
                Client Name <span style={{ color: '#e74c3c' }}>*</span>
              </div>
              <input value={newClientName} onChange={e => { setNewClientName(e.target.value.toUpperCase()); setNewClientDupes(null); }}
                placeholder="e.g. JOHN SMITH JANE SMITH"
                autoFocus
                style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: `1px solid ${borderColor}`, borderRadius: 6, background: bg, color: textPrimary, boxSizing: 'border-box' }} />
              <div style={{ fontSize: 11, color: textSecondary, marginTop: 4 }}>
                Folder: <strong style={{ color: '#2ecc71' }}>{nextClientNum} {newClientName}</strong>
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: textSecondary, marginBottom: 4 }}>
                Address <span style={{ color: '#e74c3c' }}>*</span>
              </div>
              <input value={newClientAddress} onChange={e => setNewClientAddress(e.target.value)}
                placeholder="e.g. 16726 MISSIONARY RIDGE LN HOUSTON TX 77083"
                style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: `1px solid ${borderColor}`, borderRadius: 6, background: bg, color: textPrimary, boxSizing: 'border-box' }} />
              <div style={{ fontSize: 11, color: textSecondary, marginTop: 4 }}>
                Required for address search to find this client.
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 11, color: textSecondary, marginBottom: 4 }}>Phone</div>
                <input value={newClientPhone} onChange={e => { setNewClientPhone(e.target.value); setNewClientDupes(null); }}
                  placeholder="(713) 555-0100"
                  style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: `1px solid ${borderColor}`, borderRadius: 6, background: bg, color: textPrimary, boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: textSecondary, marginBottom: 4 }}>Email</div>
                <input value={newClientEmail} onChange={e => { setNewClientEmail(e.target.value); setNewClientDupes(null); }}
                  placeholder="client@example.com"
                  style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: `1px solid ${borderColor}`, borderRadius: 6, background: bg, color: textPrimary, boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: textSecondary, marginBottom: 4 }}>Notes</div>
              <textarea value={newClientNotes} onChange={e => setNewClientNotes(e.target.value)}
                rows={2}
                placeholder="Optional notes about this client"
                style={{ width: '100%', padding: '8px 12px', fontSize: 13, border: `1px solid ${borderColor}`, borderRadius: 6, background: bg, color: textPrimary, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
            <div style={{ fontSize: 11, color: textSecondary, marginBottom: 14 }}>
              Additional fields (phone 2-4, fax, first name) can be added later in the client profile.
            </div>
            {newClientDupes && newClientDupes.length > 0 && (
              <div data-testid="new-client-dupes" style={{ marginBottom: 14, padding: '10px 12px', border: '1px solid #e67e22', borderRadius: 6, background: darkMode ? '#2a2117' : '#fff7ed' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#e67e22', marginBottom: 6 }}>
                  ⚠ {newClientDupes.length === 1 ? 'A client' : `${newClientDupes.length} clients`} may already exist — check before creating:
                </div>
                <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                  {newClientDupes.map(d => (
                    <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderTop: `1px solid ${borderColor}` }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: textPrimary }}>{d.name}</div>
                        <div style={{ fontSize: 11, color: textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {[d.address_name, d.phone1, d.email].filter(Boolean).join(' · ') || '—'}
                        </div>
                        <div style={{ fontSize: 10, color: '#e67e22' }}>same {(d.matched_on || []).join(' + ')}</div>
                      </div>
                      <button onClick={async () => {
                          const ok = await selectClient(d);
                          if (ok === false) return;
                          setShowNewClient(false); setNewClientName(''); setNewClientAddress(''); setNewClientPhone(''); setNewClientEmail(''); setNewClientNotes(''); setNewClientDupes(null);
                        }}
                        style={{ padding: '5px 10px', border: `1px solid ${borderColor}`, borderRadius: 5, background: panelBg, color: textPrimary, cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap' }}>
                        Open this client
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowNewClient(false); setNewClientName(''); setNewClientAddress(''); setNewClientPhone(''); setNewClientEmail(''); setNewClientNotes(''); setNewClientDupes(null); }}
                style={{ padding: '8px 20px', border: `1px solid ${borderColor}`, borderRadius: 6, background: panelBg, color: textSecondary, cursor: 'pointer', fontSize: 13 }}>
                Cancel
              </button>
              <button onClick={() => createNewClient({ skipDuplicateCheck: !!(newClientDupes && newClientDupes.length) })} disabled={!newClientName.trim() || !newClientAddress.trim() || newClientSaving}
                style={{ padding: '8px 20px', background: (newClientDupes && newClientDupes.length) ? '#e67e22' : '#2ecc71', color: '#fff', border: 'none', borderRadius: 6, cursor: (newClientName.trim() && newClientAddress.trim()) ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600, opacity: (newClientName.trim() && newClientAddress.trim()) ? 1 : 0.5 }}>
                {newClientSaving ? 'Creating...' : (newClientDupes && newClientDupes.length) ? 'Create anyway' : 'Create Client'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* INSUREIQ PANEL */}
      {showInsureIQ && (
        <div style={{ background: '#0d0f14', borderLeft: '2px solid #4f8ef7', display: 'flex', flexDirection: 'column', overflow: 'hidden', gridRow: '2', gridColumn: showInsureIQ ? '-2 / -1' : 'unset' }}>
          {/* IQ Header */}
          <div style={{ padding: '10px 14px', borderBottom: '1px solid #2a2f3d', display: 'flex', alignItems: 'center', gap: 8, background: '#161920' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#e8eaf0', flex: 1 }}>⚡ InsureIQ</span>
            {selectedClient && <span style={{ fontSize: 10, color: '#4f8ef7', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150 }}>{selectedClient.name}</span>}
            {/* Mode selector */}
            <div style={{ display: 'flex', background: '#1e222d', borderRadius: 6, padding: 2, gap: 1 }}>
              {['rag','hybrid','general'].map(m => (
                <button key={m} onClick={() => setIqMode(m)}
                  style={{ padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, border: 'none', cursor: 'pointer', textTransform: 'uppercase',
                    background: iqMode === m ? '#4f8ef7' : 'transparent', color: iqMode === m ? '#fff' : '#8891a8' }}>
                  {m}
                </button>
              ))}
            </div>
            <button onClick={() => setIqMessages([])} title="Clear chat"
              style={{ background: 'none', border: '1px solid #2a2f3d', color: '#8891a8', borderRadius: 5, padding: '2px 7px', fontSize: 11, cursor: 'pointer' }}>Clear</button>
            <button onClick={() => setShowInsureIQ(false)}
              style={{ background: 'none', border: 'none', color: '#8891a8', cursor: 'pointer', fontSize: 16 }}>✕</button>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {iqMessages.length === 0 && (
              <div style={{ textAlign: 'center', padding: '30px 10px', color: '#8891a8' }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>⚡</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#e8eaf0', marginBottom: 6 }}>InsureIQ Ready</div>
                <div style={{ fontSize: 11, marginBottom: 16 }}>{selectedClient ? `Ask anything about ${selectedClient.name}` : 'Select a client or ask a general question'}</div>
                {['What policies does this client have?', 'Show all policy numbers', 'Any pending renewals?', 'What coverage does this policy include?'].map(tip => (
                  <div key={tip} onClick={() => sendToInsureIQ(tip)}
                    style={{ background: '#1e222d', border: '1px solid #2a2f3d', borderRadius: 16, padding: '5px 12px', fontSize: 11, color: '#8891a8', cursor: 'pointer', marginBottom: 5, transition: 'all 0.15s' }}
                    onMouseEnter={e => e.target.style.borderColor = '#4f8ef7'}
                    onMouseLeave={e => e.target.style.borderColor = '#2a2f3d'}>
                    {tip}
                  </div>
                ))}
              </div>
            )}
            {iqMessages.map((msg, i) => msg.role === 'divider' ? (
              <div key={i} style={{ textAlign: 'center', fontSize: 10, color: '#8891a8', padding: '2px 0' }}>{msg.content}</div>
            ) : (
              <div key={i} style={{ display: 'flex', gap: 8, flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: msg.role === 'user' ? '#2a2f3d' : 'linear-gradient(135deg,#4f8ef7,#7c5cbf)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                  {msg.role === 'user' ? '👤' : 'AI'}
                </div>
                <div style={{ maxWidth: '85%' }}>
                  {msg.askedAbout && (
                    <div style={{ fontSize: 10, color: '#e0a458', marginBottom: 2 }}>↩ Answer about {msg.askedAbout} (asked before you switched)</div>
                  )}
                  <div style={{ padding: '8px 12px', borderRadius: 10, fontSize: 12, lineHeight: 1.6,
                    background: msg.role === 'user' ? '#4f8ef7' : '#161920',
                    border: msg.role === 'user' ? 'none' : '1px solid #2a2f3d',
                    color: msg.role === 'user' ? '#fff' : '#e8eaf0',
                    wordBreak: 'break-word' }}>
                    {msg.role === 'ai' && msg.content.includes('---DETAILS---') ? (() => {
                      const [summary, details] = msg.content.split('---DETAILS---');
                      const uid = `iq-${i}`;
                      return (
                        <div>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{summary.trim()}</div>
                          <details style={{ marginTop: 8 }}>
                            <summary style={{ cursor: 'pointer', color: '#4f8ef7', fontSize: 11, userSelect: 'none' }}>▼ Show full analysis</summary>
                            <div style={{ marginTop: 8, whiteSpace: 'pre-wrap', color: '#8891a8', fontSize: 11, borderTop: '1px solid #2a2f3d', paddingTop: 8 }}>{details.trim()}</div>
                          </details>
                        </div>
                      );
                    })() : <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>}
                  </div>
                  {(msg.sources?.length > 0 || msg.tier) && (
                    <div style={{ fontSize: 10, color: '#8891a8', marginTop: 4 }}>
                      {[
                        msg.sources?.length > 0 ? `${msg.sources.length} source${msg.sources.length !== 1 ? 's' : ''}` : null,
                        msg.elapsed != null ? `${msg.elapsed}s` : null,
                        msg.mode,
                        msg.tier ? (msg.tier === 'fast' ? '⚡ Fast' : '🧠 Deep analysis') : null,
                      ].filter(Boolean).join(' · ')}
                    </div>
                  )}
                  {msg.role === 'ai' && msg.tier === 'fast' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                      {msg.confidence === 'low' && !msg.escalated && (
                        <span style={{ fontSize: 10, color: '#e0a458' }}>⚠ low confidence</span>
                      )}
                      <button onClick={() => escalateIq(i)} disabled={msg.escalated || iqLoading}
                        style={msg.confidence === 'low' && !msg.escalated
                          ? { background: '#4f8ef7', border: 'none', color: '#fff', borderRadius: 5, padding: '3px 10px', fontSize: 10, fontWeight: 600, cursor: 'pointer' }
                          : { background: 'none', border: '1px solid #2a2f3d', color: msg.escalated ? '#565d70' : '#4f8ef7', borderRadius: 5, padding: '3px 10px', fontSize: 10, cursor: msg.escalated ? 'default' : 'pointer' }}>
                        {msg.escalated ? '✓ Deeper analysis requested' : '🧠 Get a deeper analysis'}
                      </button>
                    </div>
                  )}
                  {msg.sources?.some(s => s.type === 'skipped') && (
                    <div style={{ fontSize: 10, color: '#e0a458', marginTop: 2 }}>
                      ⚠ Skipped (scanned, no text): {msg.sources.filter(s => s.type === 'skipped').map(s => s.filename).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {iqLoading && (
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#4f8ef7,#7c5cbf)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff' }}>AI</div>
                <div style={{ padding: '10px 14px', background: '#161920', border: '1px solid #2a2f3d', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {[0,1,2].map(i => <div key={i} style={{ width: 5, height: 5, background: '#8891a8', borderRadius: '50%', animation: `bop 1.2s ${i*0.2}s infinite` }} />)}
                  {iqStatus && <span style={{ fontSize: 11, color: '#8891a8', marginLeft: 6 }}>{iqStatus}</span>}
                </div>
              </div>
            )}
            <div ref={iqEndRef} />
          </div>

          {/* Include client PDFs (rag/hybrid only) */}
          {iqMode !== 'general' && fileList.some(_isPdf) && (
            <div style={{ borderTop: '1px solid #2a2f3d', background: '#11141a', padding: '8px 12px', maxHeight: 120, overflowY: 'auto' }}>
              <div style={{ fontSize: 10, color: '#8891a8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Include client PDFs</div>
              {fileList.filter(_isPdf).map(f => (
                <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#c7cdd9', padding: '2px 0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={iqFileIds.has(f.id)} onChange={() => toggleIqFile(f.id)} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.filename}</span>
                </label>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{ padding: '10px 12px', borderTop: '1px solid #2a2f3d', background: '#161920', display: 'flex', gap: 8 }}>
            <textarea value={iqInput} onChange={e => setIqInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendToInsureIQ(); } }}
              placeholder={selectedClient ? `Ask about ${selectedClient.name}...` : 'Ask any insurance question...'}
              rows={2}
              style={{ flex: 1, background: '#1e222d', border: '1px solid #2a2f3d', borderRadius: 8, padding: '8px 10px', color: '#e8eaf0', fontSize: 12, fontFamily: 'inherit', resize: 'none', outline: 'none', lineHeight: 1.5 }} />
            <button onClick={() => sendToInsureIQ()} disabled={iqLoading || !iqInput.trim()}
              style={{ width: 36, height: 36, background: '#4f8ef7', border: 'none', borderRadius: 8, color: '#fff', fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: iqLoading || !iqInput.trim() ? 0.4 : 1, alignSelf: 'flex-end' }}>
              ➤
            </button>
          </div>
        </div>
      )}

      {/* USER MANAGEMENT MODAL */}
      {showUserMgmt && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: panelBg, borderRadius: 10, padding: 28, width: 780, maxWidth: "95vw", maxHeight: "85vh", display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: textPrimary }}>👥 User Management</div>
              <button onClick={() => { setShowUserMgmt(false); setEditingUser(null); }}
                style={{ padding: '5px 12px', border: `1px solid ${borderColor}`, borderRadius: 6, background: panelBg, color: textSecondary, cursor: 'pointer' }}>Close</button>
            </div>

            {/* User list */}
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: 20 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${borderColor}` }}>
                    {['Username', 'Full Name', 'Role', 'Status', 'Bonuses', 'Renewals', 'Calendar', 'Actions'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {userList.map(u => (
                    <tr key={u.id} style={{ borderBottom: `1px solid ${borderColor}`, background: editingUser?.id === u.id ? (darkMode ? '#0d1f2d' : '#E6F1FB') : 'transparent' }}>
                      <td style={{ padding: '10px', color: textPrimary, fontFamily: 'monospace', fontSize: 12 }}>{u.username}</td>
                      <td style={{ padding: '10px', color: textPrimary }}>
                        {editingUser?.id === u.id ? (
                          <input value={editingUser.full_name} onChange={e => setEditingUser(prev => ({ ...prev, full_name: e.target.value }))}
                            style={{ width: '100%', padding: '4px 8px', border: `1px solid ${borderColor}`, borderRadius: 4, background: bg, color: textPrimary, fontSize: 12 }} />
                        ) : u.full_name}
                      </td>
                      <td style={{ padding: '10px' }}>
                        {editingUser?.id === u.id ? (
                          <RoleSelect value={editingUser.role} onChange={role => setEditingUser(prev => ({ ...prev, role }))}
                            style={{ padding: '4px 8px', border: `1px solid ${borderColor}`, borderRadius: 4, background: bg, color: textPrimary, fontSize: 12 }} />
                        ) : (
                          <RoleBadge role={u.role} />
                        )}
                      </td>
                      <td style={{ padding: '10px' }}>
                        {editingUser?.id === u.id ? (
                          <select value={editingUser.is_active ? 'active' : 'inactive'} onChange={e => setEditingUser(prev => ({ ...prev, is_active: e.target.value === 'active' }))}
                            style={{ padding: '4px 8px', border: `1px solid ${borderColor}`, borderRadius: 4, background: bg, color: textPrimary, fontSize: 12 }}>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        ) : (
                          <span style={{ color: u.is_active ? '#27AE60' : '#e74c3c', fontSize: 12 }}>
                            {u.is_active ? '✓ Active' : '✗ Inactive'}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px' }}>
                        {editingUser?.id === u.id ? (
                          <input type="checkbox"
                            checked={editingUser.receives_bonuses ?? true}
                            onChange={e => setEditingUser(prev => ({ ...prev, receives_bonuses: e.target.checked }))} />
                        ) : (
                          <span style={{ color: u.receives_bonuses ? '#27AE60' : textSecondary, fontSize: 12 }}>
                            {u.receives_bonuses ? '✓' : '—'}
                          </span>
                        )}
                      </td>
                      {/* v5.27.0: "Show in renewals" — renewals pickers only; never changes an assignment */}
                      <td style={{ padding: '10px' }} title="Show in renewals pickers">
                        {editingUser?.id === u.id ? (
                          <input type="checkbox"
                            checked={editingUser.show_in_renewals ?? true}
                            onChange={e => setEditingUser(prev => ({ ...prev, show_in_renewals: e.target.checked }))} />
                        ) : (
                          <span style={{ color: u.show_in_renewals !== false ? '#27AE60' : textSecondary, fontSize: 12 }}>
                            {u.show_in_renewals !== false ? '✓' : '—'}
                          </span>
                        )}
                      </td>
                      {/* v5.32.0: Personal calendar + Tasks on HDCal (Office is always shown).
                          v5.32.1: + which HDCal account it comes from (shown only when it is not the username). */}
                      <td style={{ padding: '10px' }} title="Personal calendar + Tasks (HDCal) and the calendar account they come from">
                        {editingUser?.id === u.id ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <input type="checkbox" style={{ alignSelf: 'flex-start' }}
                              checked={editingUser.personal_calendar ?? true}
                              onChange={e => setEditingUser(prev => ({ ...prev, personal_calendar: e.target.checked }))} />
                            <select data-testid="user-calendar-account"
                              value={editingUser.calendar_account || ''}
                              onChange={e => setEditingUser(prev => ({ ...prev, calendar_account: e.target.value || null }))}
                              style={{ padding: '3px 4px', border: `1px solid ${borderColor}`, borderRadius: 4, background: bg, color: textPrimary, fontSize: 11, maxWidth: 150 }}>
                              <option value="">{u.username} (own)</option>
                              {calAccounts.filter(a => a.account !== u.username.toLowerCase()).map(a => (
                                <option key={a.account} value={a.account}>
                                  {a.account}{a.used_by && a.used_by.username !== u.username ? ` — used by ${a.used_by.full_name}` : ''}
                                </option>
                              ))}
                              {editingUser.calendar_account && !calAccounts.some(a => a.account === editingUser.calendar_account) && (
                                <option value={editingUser.calendar_account}>{editingUser.calendar_account}</option>
                              )}
                            </select>
                          </div>
                        ) : (
                          <span style={{ color: u.personal_calendar !== false ? '#27AE60' : textSecondary, fontSize: 12 }}>
                            {u.personal_calendar !== false ? '✓' : '—'}
                            {u.calendar_account && u.calendar_account !== u.username && (
                              <span style={{ color: textSecondary, marginLeft: 6 }}>{u.calendar_account}</span>
                            )}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px' }}>
                        {editingUser?.id === u.id ? (
                          <div style={{ display: 'flex', gap: 6, flexDirection: 'column' }}>
                            <input value={editingUser.newPassword || ''} onChange={e => setEditingUser(prev => ({ ...prev, newPassword: e.target.value }))}
                              placeholder="New password (optional)"
                              type="password"
                              style={{ padding: '4px 8px', border: `1px solid ${borderColor}`, borderRadius: 4, background: bg, color: textPrimary, fontSize: 12, width: 160 }} />
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button onClick={() => saveUser(u.id, { full_name: editingUser.full_name, role: editingUser.role, is_active: editingUser.is_active, receives_bonuses: editingUser.receives_bonuses ?? true, show_in_renewals: editingUser.show_in_renewals ?? true, personal_calendar: editingUser.personal_calendar ?? true, calendar_account: editingUser.calendar_account || null, password: editingUser.newPassword })}
                                style={{ padding: '4px 10px', background: '#2ecc71', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
                                {userSaving ? '...' : 'Save'}
                              </button>
                              <button onClick={() => setEditingUser(null)}
                                style={{ padding: '4px 10px', border: `1px solid ${borderColor}`, borderRadius: 4, background: panelBg, color: textSecondary, cursor: 'pointer', fontSize: 12 }}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => setEditingUser({ ...u, newPassword: '' })}
                              style={{ padding: '4px 10px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
                              Edit
                            </button>
                            {u.username !== user?.username && (
                              <button onClick={() => deleteUser(u.id, u.username)}
                                style={{ padding: '4px 10px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
                                Delete
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Create new user */}
            <div style={{ borderTop: `2px solid ${borderColor}`, paddingTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: textPrimary, marginBottom: 12 }}>Add New User</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input value={newUserForm.username} onChange={e => setNewUserForm(p => ({ ...p, username: e.target.value }))}
                  placeholder="Username (e.g. x1015)"
                  style={{ padding: '8px 10px', border: `1px solid ${borderColor}`, borderRadius: 6, background: bg, color: textPrimary, fontSize: 13, width: 140 }} />
                <input value={newUserForm.full_name} onChange={e => setNewUserForm(p => ({ ...p, full_name: e.target.value }))}
                  placeholder="Full name"
                  style={{ padding: '8px 10px', border: `1px solid ${borderColor}`, borderRadius: 6, background: bg, color: textPrimary, fontSize: 13, flex: 1, minWidth: 140 }} />
                <input value={newUserForm.password} onChange={e => setNewUserForm(p => ({ ...p, password: e.target.value }))}
                  placeholder="Password" type="password"
                  style={{ padding: '8px 10px', border: `1px solid ${borderColor}`, borderRadius: 6, background: bg, color: textPrimary, fontSize: 13, width: 130 }} />
                <RoleSelect value={newUserForm.role} onChange={role => setNewUserForm(p => ({ ...p, role }))}
                  style={{ padding: '8px 10px', border: `1px solid ${borderColor}`, borderRadius: 6, background: bg, color: textPrimary, fontSize: 13 }} />
                <button onClick={createUser} disabled={userSaving}
                  style={{ padding: '8px 16px', background: '#2ecc71', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  {userSaving ? 'Creating...' : '+ Add User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showNotesModal && (
        <div onClick={() => setShowNotesModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 10, padding: 20, width: 560, maxWidth: '92vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: textPrimary }}>📝 New Note</div>
            <div style={{ fontSize: 11, color: textSecondary }}>
              Saved as a .md file in {selectedFolder?.name ? `"${selectedFolder.name}"` : 'this folder'} — searchable by InsureIQ and printable.
            </div>
            <input value={notesName} onChange={e => setNotesName(e.target.value)} autoFocus
              placeholder="Note name (e.g. Coverage call notes)"
              style={{ padding: '8px 10px', fontSize: 13, border: `1px solid ${borderColor}`, borderRadius: 6, background: inputBg || panelBg, color: textPrimary }} />
            <div style={{ fontSize: 11, color: textSecondary, marginTop: -6 }}>
              File: {(notesName.trim().replace(/[\/\\:*?"<>|]/g,'_').replace(/\.\./g,'_').replace(/\s+/g,' ').trim().slice(0,120) || 'note')}__{new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' })}.md
            </div>
            <textarea value={notesBody} onChange={e => setNotesBody(e.target.value)}
              placeholder="Write your note here..."
              style={{ padding: '8px 10px', fontSize: 13, border: `1px solid ${borderColor}`, borderRadius: 6, background: inputBg || panelBg, color: textPrimary, minHeight: 240, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowNotesModal(false)} disabled={uploading}
                style={{ padding: '7px 14px', fontSize: 12, background: panelBg, color: textSecondary, border: `1px solid ${borderColor}`, borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
              <button onClick={saveNote} disabled={uploading}
                style={{ padding: '7px 14px', fontSize: 12, background: '#27AE60', color: '#fff', border: 'none', borderRadius: 6, cursor: uploading ? 'default' : 'pointer', fontWeight: 600 }}>
                {uploading ? 'Saving…' : 'Save Note'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PREVIEW MODAL */}
      {showPreviewModal && previewUrl && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', background: '#111', color: '#fff' }}>
            <span style={{ fontSize: 14, fontWeight: 500 }}>{selectedFile?.filename}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              {selectedFile?.extension?.toLowerCase() === 'pdf' && (
                <button onClick={() => openEditor(selectedFile)}
                  style={{ padding: '5px 14px', background: '#2ecc71', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>✎ Edit</button>
              )}
              <button onClick={() => downloadFile(selectedFile)}
                style={{ padding: '5px 14px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>↓ Download</button>
              <button onClick={() => { setShowPreviewModal(false); setPreviewUrl(null); }}
                style={{ padding: '5px 14px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>✕ Close</button>
            </div>
          </div>
          <iframe key={previewReloadKey} src={previewUrl} style={{ flex: 1, border: 'none', width: '100%' }} title="preview" />
        </div>
      )}

      {/* FOOTER */}
      <div style={{ gridColumn: '1 / 6', background: '#1a1a2e', color: '#888', fontSize: 10, padding: '5px 16px 5px 200px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #333' }}>
        <span>Solutions Insurance Agency, Inc. — Confidential Document Management System</span>
        <span>All information contained herein is strictly confidential and proprietary. Unauthorized access, use, disclosure, or distribution is prohibited. &copy; {new Date().getFullYear()} Solutions Insurance Agency, Inc.</span>
      </div>
    
      {mmModalOpen && mmFile && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#1e2130', border: '1px solid #3a4060', borderRadius: 8, padding: 24, width: 420, maxWidth: '95vw', color: '#e0e4f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 15 }}>💬 Send via Mattermost</h3>
              <span onClick={() => setMmModalOpen(false)} style={{ cursor: 'pointer', color: '#888', fontSize: 18 }}>✕</span>
            </div>
            <div style={{ fontSize: 12, color: '#9aa', marginBottom: 16, background: '#151722', padding: '8px 10px', borderRadius: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>📄 {mmFile.filename}</div>
            <div style={{ fontSize: 12, color: '#aab', marginBottom: 6 }}>Send to</div>
            <select value={mmRecipient} onChange={e => setMmRecipient(e.target.value)}
              style={{ width: '100%', padding: 8, borderRadius: 4, marginBottom: 14, background: '#151722', color: '#e0e4f0', border: '1px solid #3a4060', fontSize: 13 }}>
              <option value="">— Select user —</option>
              {mmUsers.map(u => <option key={u.id} value={u.id}>{u.full_name ? `${u.full_name} (@${u.username})` : `@${u.username}`}</option>)}
            </select>
            <div style={{ fontSize: 12, color: '#aab', marginBottom: 8 }}>Send as</div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              {["file","link"].map(t => (
                <button key={t} onClick={() => setMmSendType(t)} style={{ flex: 1, padding: 8, borderRadius: 4, cursor: 'pointer', fontSize: 13, border: '1px solid', borderColor: mmSendType===t ? '#4a9eda' : '#3a4060', background: mmSendType===t ? '#1d3a50' : '#151722', color: mmSendType===t ? '#4a9eda' : '#8090a0' }}>
                  {t === 'file' ? '📎 File Attachment' : '🔗 InsureDocs Link'}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 12, color: '#aab', marginBottom: 6 }}>Message (optional)</div>
            <textarea value={mmMessage} onChange={e => setMmMessage(e.target.value)} placeholder="Add a note..." rows={3}
              style={{ width: '100%', padding: 8, borderRadius: 4, marginBottom: 14, background: '#151722', color: '#e0e4f0', border: '1px solid #3a4060', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
            {mmError && <div style={{ color: '#e07070', fontSize: 12, marginBottom: 10 }}>{mmError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setMmModalOpen(false)} style={{ padding: '8px 16px', borderRadius: 4, background: '#2a2e40', color: '#aab', border: '1px solid #3a4060', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={handleMmSend} disabled={mmSending} style={{ padding: '8px 16px', borderRadius: 4, background: mmSending ? '#2a4a60' : '#1d628a', color: mmSending ? '#668' : '#fff', border: 'none', cursor: mmSending ? 'default' : 'pointer', fontSize: 13 }}>{mmSending ? 'Sending...' : 'Send'}</button>
            </div>
          </div>
        </div>
      )}
      {signFileTarget && selectedClient && (
        <SignForSignatureModal
          file={signFileTarget}
          clientId={selectedClient.id}
          darkMode={darkMode}
          onClose={() => setSignFileTarget(null)}
          onSent={() => {}}
        />
      )}
      {showHealthCoverage && selectedClient && (
        <HealthCoveragePanel clientId={selectedClient.id} darkMode={darkMode} onClose={() => setShowHealthCoverage(false)} />
      )}
      {showSignedPanel && (
        <SignedDocumentsPanel
          darkMode={darkMode}
          clientId={null}
          onClose={() => setShowSignedPanel(false)}
        />
      )}
  {showUploadModal && selectedClient && selectedFolder && (
    <UploadModal
      clientId={selectedClient.id}
      folderId={selectedFolder.id}
      darkMode={darkMode}
      onClose={() => setShowUploadModal(false)}
      onComplete={async () => {
        const res = await files.list(selectedClient.id, selectedFolder?.id);
        setFileList(res.data.files);
      }}
    />
  )}
  {showBackupModal && (
    <BackupStatusModal darkMode={darkMode} onClose={() => setShowBackupModal(false)} />
  )}
  {showBonuses && (
    <Bonuses
      user={user}
      darkMode={darkMode}
      initialContext={bonusReturnContext}
      onClose={() => setShowBonuses(false)}
      openClientFolder={async (clientId, returnContext = null) => {
        const findCached = () => {
          for (const cab of cabinets) {
            const c = cab.clients.find(x => x.id === clientId);
            if (c) return c;
          }
          return null;
        };
        // Unsaved work: ask first; Stay keeps Bonuses open.
        if (!confirmLeaveClient()) return;
        const epoch = clientEpochRef.current;
        // selectClient() clears bonusReturnContext, so set it AFTER navigating
        // to preserve the snapshot the back button needs.
        const goTo = (c) => { setShowBonuses(false); selectClient(c, null, { confirmed: true }); setBonusReturnContext(returnContext); };
        const cached = findCached();
        if (cached) { goTo(cached); return; }
        // cabinets cache may not include all 11k+ clients — fall back to API fetch
        try {
          const r = await clients.get(clientId);
          if (clientEpochRef.current !== epoch) return;   // switched elsewhere meanwhile: drop
          if (r.data) goTo(r.data);
        } catch (e) {
          // A real fetch failure (network/500) must be distinguishable from a
          // silent "not found" no-op. Leave Bonuses open either way.
          console.warn(`openClientFolder: failed to load client ${clientId}`, e);
          if (isNotFound(e)) alert(NOT_AVAILABLE_MSG);   // 5.21.0: no longer a silent no-op
        }
      }}
    />
  )}
  {addBonusForFolder && (
    <AddBonusModal
      client={addBonusForFolder.client}
      sourceFolderPath={addBonusForFolder.sourceFolderPath}
      darkMode={darkMode}
      onClose={() => setAddBonusForFolder(null)}
      onSaved={() => setAddBonusForFolder(null)}
    />
  )}
  {showChangePassword && (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={() => setShowChangePassword(false)}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: panelBg, color: textPrimary, borderRadius: 10, padding: 24, width: 380, boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 18, color: textPrimary }}>Change your password</h2>
        <p style={{ margin: '0 0 16px', fontSize: 12, color: textSecondary }}>Enter your current password and choose a new one (6 characters minimum).</p>
        <ChangePasswordForm onClose={() => setShowChangePassword(false)} darkMode={darkMode}
          panelBg={panelBg} borderColor={borderColor} textPrimary={textPrimary} textSecondary={textSecondary} />
      </div>
    </div>
  )}
  {addAcordForClient && (
    <AddAcordModal
      client={addAcordForClient}
      onClose={() => setAddAcordForClient(null)}
      darkMode={darkMode}
    />
  )}
  {historyClient && (
    <ClientBonusHistoryModal
      client={historyClient}
      darkMode={darkMode}
      onClose={() => setHistoryClient(null)}
    />
  )}
  {showFaq && (
    <FaqApp
      user={user}
      darkMode={darkMode}
      onClose={() => { setShowFaq(false); setFaqCountTick(n => n + 1); }}
      onConflictsChanged={() => setFaqCountTick(n => n + 1)}
    />
  )}
  {showRenewals && (
    <Renewals
      user={user}
      darkMode={darkMode}
      onClose={() => setShowRenewals(false)}
      onClientTreesChanged={onClientTreesChanged}
      openClientFolder={async (clientId) => {
        // 5.5.2: same full switch as every other path. Unsaved work: ask first; Stay keeps
        // Renewals open.
        if (!confirmLeaveClient()) return;
        const epoch = clientEpochRef.current;
        setShowRenewals(false);
        // Try the already-loaded cabinets for speed (every client lives there,
        // including all ~12k — no truncation). Same shape the tree click uses.
        let cached = null;
        for (const cab of (cabinets || [])) {
          const found = (cab.clients || []).find(x => x.id === clientId);
          if (found) { cached = found; break; }
        }
        if (cached) {
          selectClient(cached, null, { confirmed: true });
          return;
        }
        // Fall back to API fetch
        try {
          const r = await clients.get(clientId);
          if (clientEpochRef.current !== epoch) return;   // switched elsewhere meanwhile: drop
          if (r.data) selectClient(r.data, null, { confirmed: true });
        } catch (e) {
          console.error('Failed to load client', clientId, e);
          alert(isNotFound(e) ? NOT_AVAILABLE_MSG : 'Could not open that client folder.');   // 5.21.0
        }
      }}
    />
  )}
  {addRenewalForClient && (
    <AddRenewalModal
      client={addRenewalForClient}
      darkMode={darkMode}
      onClose={() => setAddRenewalForClient(null)}
      onSaved={() => setAddRenewalForClient(null)}
    />
  )}
  {folderMenu && (
    <div onClick={e => e.stopPropagation()} data-testid="folder-menu"
      style={{ position: 'fixed', top: Math.min(folderMenu.y, window.innerHeight - 60), left: Math.min(folderMenu.x, window.innerWidth - 200), zIndex: 9000, background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.25)', minWidth: 180, padding: 4 }}>
      <div onClick={() => { const c = folderMenu.client; setFolderMenu(null); setFollowupFor(c); }} data-testid="folder-menu-followup"
        style={{ padding: '7px 14px', fontSize: 13, cursor: 'pointer', color: '#7c3aed', fontWeight: 600 }}>📅 Follow-up…</div>
    </div>
  )}
  {followupFor && (
    <FollowupDialog client={followupFor} onClose={() => setFollowupFor(null)}
      onCreated={() => setFollowupReload(k => k + 1)}
      theme={{ bg, panelBg, borderColor, textPrimary, textSecondary, darkMode }} />
  )}
  {menu && (() => {
    const inSel = resolvedSelected.some(f => f.id === menu.file.id);
    const item = (label, fn, disabled) => (
      <div onClick={e => { e.stopPropagation(); if (disabled) return; setMenu(null); fn(); }}
        style={{ padding: '7px 14px', fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer', color: disabled ? textSecondary : textPrimary, opacity: disabled ? 0.5 : 1 }}>{label}</div>
    );
    return (
      <div onClick={e => e.stopPropagation()}
        style={{ position: 'fixed', top: Math.min(menu.y, window.innerHeight - 175), left: Math.min(menu.x, window.innerWidth - 200), zIndex: 9000, background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.25)', minWidth: 180, padding: 4 }}>
        {item(inSel ? `↓ Download (${selCount})` : '↓ Download', inSel ? bulkDownload : () => downloadFile(menu.file))}
        {item(inSel ? `⇄ Move (${checkedFileObjs.length})` : '⇄ Move',
              () => { setMoveTargets(inSel ? checkedFileObjs : [menu.file]); setMoveModalOpen(true); },
              inSel && checkedFileObjs.length === 0)}
        {user?.role === 'admin' && item(inSel ? `🗑 Delete (${selCount})` : '🗑 Delete', inSel ? bulkDelete : () => deleteFile(menu.file))}
        {isPdfFile(menu.file) && item('✍️ Send for signature…', () => openSignFlow(menu.file))}
      </div>
    );
  })()}
  {moveModalOpen && (() => {
    const q = moveSearch.trim();
    const blocked = moveFolder
      ? collectSubtreeIds(findFolderNode(folderTree, moveFolder.id) || { id: moveFolder.id, children: [] })
      : new Set();
    const dests = flattenFolders(folderTree).filter(f =>
      !blocked.has(f.id) && (!q || textMatches(f.name, q))   // 5.27.3 apostrophes ignored
    );
    const heading = moveFolder
      ? `Move folder “${moveFolder.name}” — pick a destination folder`
      : `Move ${moveTargets.length} file${moveTargets.length === 1 ? '' : 's'} — pick a destination folder`;
    const onPick = (id) => {
      if (moveBusy) return;
      if (moveFolder) runMoveFolder(id);
      else runMove(id);
    };
    return (
    <div onClick={() => !moveBusy && (setMoveModalOpen(false), setMoveFolder(null), setMoveSearch(''))} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 8999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: panelBg, borderRadius: 10, width: 420, maxHeight: '70vh', display: 'flex', flexDirection: 'column', border: `1px solid ${borderColor}` }}>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${borderColor}`, fontWeight: 600, color: textPrimary }}>
          {heading}
        </div>
        <div style={{ padding: '8px 12px', borderBottom: `1px solid ${borderColor}` }}>
          <input autoFocus value={moveSearch} onChange={e => setMoveSearch(e.target.value)}
            placeholder="Search folders…"
            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: 13, border: `1px solid ${borderColor}`, borderRadius: 6, background: bg, color: textPrimary }} />
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {dests.map(f => (
            <div key={f.id} onClick={() => onPick(f.id)}
              style={{ padding: `8px 16px 8px ${16 + f._depth * 16}px`, cursor: moveBusy ? 'default' : 'pointer', color: textPrimary, fontSize: 13, borderBottom: `1px solid ${borderColor}` }}>📁 {f.name}</div>
          ))}
          {dests.length === 0 && (
            <div style={{ padding: 16, fontSize: 13, color: textSecondary }}>
              {flattenFolders(folderTree).length === 0 ? 'This client has no folders to move into.' : 'No folders match that search.'}
            </div>
          )}
        </div>
        <div style={{ padding: 10, textAlign: 'right' }}>
          <button onClick={() => { setMoveModalOpen(false); setMoveFolder(null); setMoveSearch(''); }} disabled={moveBusy} style={ACTION_BTN}>{moveBusy ? 'Moving…' : 'Cancel'}</button>
        </div>
      </div>
    </div>
    );
  })()}
  <WriterDrawer darkMode={darkMode} />
</div>
  );
}
